/* LAV TDMS — Reports & exports, Audit log, RBAC admin, Vendors, Search.
   Ported 1:1 from the prototype's app/Admin.jsx (window.APP). Inline styles
   kept verbatim. */
import { type CSSProperties, type MouseEvent, type ReactNode, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
/* SheetJS — обычная зависимость пакета, как в src/lib/excel.ts. Раньше здесь
   стоял `declare const XLSX`, то есть обещание, что библиотеку положат в
   глобальную область: ни тега script, ни присваивания в window в проекте нет,
   и импорт матрицы падал с ReferenceError на первом же клике. */
import * as XLSX from 'xlsx'
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  CodeChip,
  CompletionBar,
  Crit,
  clickable,
  DateCell,
  DateInput,
  Dialog,
  DiscTag,
  Field,
  Input,
  KPIStat,
  LifeBadge,
  lifeLabel,
  PageHead,
  Purpose,
  Select,
  SlaPill,
  SmartTable,
  Spinner,
  Toolbar,
  ToolSpacer,
  DataTable,
  EmptyState,
  toast,
  type Column,
  type SmartColumn,
  railWidth,
} from '@/ui'
import { Icon } from '@/ds/icons'
import { colHeader } from '@/i18n/columns'
import type { AuditEntry, Contract, DictKey, DictRow, Doc, LifeStatus, MatrixActId, MatrixRule, PermArea, Purpose as PurposeCode, PurposeSchemeRule, Role, User, UserGroup, Vendor, CodingDoc, CodingSegment, SegmentSource } from '@/data/types'
import { ApiError, fetchList, fetchSub, noteExport } from '@/data/api'
import { exportAudit, exportDicts, exportRows, parseDicts, parseDocs, type DictImportRow } from '@/lib/excel'
import { DocsTable, Pager } from '@/screens/pt/Registers'
import { qk, useFacets, useList, useMeta, useSub, inboxKeys } from '@/data/queries'
import { useCommand } from '@/data/mutations'
import { useCoding, useContracts, useDictLabels, useDictRows, useDicts, useDocAttrs, useMe } from '@/data/meta'
import { t, type Lang } from '@/i18n/lang'
import { parseRu, today } from '@/lib/format'


/* Кнопки, за которыми нет серверной команды, отключены с пояснением: молча
   ничего не делающая кнопка хуже отсутствующей. Появятся вместе с Ф2. */

/* Экран, чьи данные не пришли, обязан это сказать. Иначе пустая таблица
   читается как «данных нет», а не «сервер недоступен».

   Отказ по правам — не сбой: 403 писался тем же красным «не удалось загрузить
   данные с сервера», и человек шёл заводить заявку в поддержку там, где у него
   просто нет права. Ошибка передаётся, когда её есть чем отличить. */
function ScreenError({ lang, error }: { lang: Lang; error?: unknown }) {
  const denied = error instanceof ApiError && error.status === 403
  return (
    <div style={{ padding: 24, font: 'var(--font-ui)', color: denied ? 'var(--text-muted)' : 'var(--danger-ink)' }}>
      {denied
        ? t(lang, 'Доступ запрещён: у вашей роли нет права на этот раздел. Право выдаёт администратор проекта.',
                  'Access denied: your role has no right to this section. The project administrator grants it.')
        : t(lang, 'Не удалось загрузить данные с сервера', 'Failed to load data from the server')}
    </div>
  )
}

// Icon with an inherited color (DS Icon takes color via currentColor).
function CIcon({ name, size, color }: { name: string; size?: number; color?: string }) {
  return <span style={{ display: 'inline-flex', color }}><Icon name={name} size={size} /></span>
}

// Controlled tab bar (DS Tabs.jsx) — kept verbatim for tabsNode usage.
function PtTabs({
  tabs = [],
  value,
  onChange,
  style = {},
}: {
  tabs?: { id: string; label: ReactNode; count?: number }[]
  value: string
  onChange: (id: string) => void
  style?: CSSProperties
}) {
  return (
    /* Десять вкладок в 1094 px не помещаются. Без переноса они не переносятся,
       а сжимаются: подписи ломаются по словам внутри кнопок, и полоса всё равно
       становится двухрядной — только с рваными заголовками. Перенос по строкам
       с целыми подписями даёт то же место и читается.

       Прокрутка вместо переноса держала бы один ряд, но прячет вкладки за
       краем: на экране администрирования это единственный вход в справочники,
       и «его не видно» здесь дороже лишних 36 px. */
    <div role="tablist" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', borderBottom: 'var(--border-hairline) solid var(--border-default)', ...style }}>
      {tabs.map((tb) => {
        const id = tb.id
        const label = tb.label
        const count = tb.count
        const active = id === value
        return (
          <button
            key={id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange && onChange(id)}
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-5) var(--space-5)',
              marginBottom: -1,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              font: 'var(--font-ui)',
              fontSize: 'var(--text-md)',
              fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-regular)',
              color: active ? 'var(--text-strong)' : 'var(--text-muted)',
              borderBottom: `var(--border-accent) solid ${active ? 'var(--accent)' : 'transparent'}`,
              // Подчёркивание переходит вместе с цветом: порознь текст
              // перетекает, а полоса прыгает.
              transition: 'color var(--duration-fast) var(--ease-standard), border-bottom-color var(--duration-fast) var(--ease-standard)',
            }}
          >
            {label}
            {count != null ? (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-2xs)',
                  fontWeight: 'var(--weight-medium)',
                  color: active ? 'var(--accent-ink)' : 'var(--text-subtle)',
                  background: active ? 'var(--accent-soft)' : 'var(--surface-sunken)',
                  border: `var(--border-hairline) solid ${active ? 'var(--accent-line)' : 'var(--border-subtle)'}`,
                  borderRadius: 'var(--radius-sm)',
                  padding: '1px var(--space-3)',
                }}
              >
                {count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

// ===== Reports (live, computed) =========================================
export function Reports({ lang = 'ru' }: { lang?: Lang }) {
  const me = useMe()
  const [tab, setTab] = useState('backlog')
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={<span>{me.data?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Отчёты', 'Reports')}</span>}
        title={t(lang, 'Отчёты и выгрузки', 'Reports & exports')}
        actions={
          <>
            {/* Переключателя периода здесь больше нет: он ничего не менял, а
                период задаётся параметрами самого отчёта — там он и работает. */}
            {/* Общей кнопки выгрузки здесь больше нет: она не знала, какая из
                трёх вкладок открыта, и годами стояла отключённой. Выгрузка
                живёт у самого отчёта. */}
          </>
        }
        tabsNode={
          <PtTabs
            value={tab}
            onChange={setTab}
            tabs={[
              { id: 'backlog', label: t(lang, 'Бэклог рассмотрения', 'Review backlog') },
              { id: 'status', label: t(lang, 'Статус подачи / согласования', 'Submission / approval') },
              { id: 'escal', label: t(lang, 'Эскалация просрочек', 'Overdue escalation') },
              /* Качество подачи: коды рассмотрения по подрядчикам — вход в
                 письмо о неудовлетворительном качестве и в удержание. */
              { id: 'quality', label: t(lang, 'Качество подачи', 'Submission quality') },
              /* Входной контроль: круг, пройденный впустую. Основания возврата
                 хранятся кодами затем, чтобы этот отчёт считался. */
              { id: 'intake', label: t(lang, 'Входной контроль', 'Intake control') },
              /* Отставание от контрактного срока: сервер считал его с самого
                 начала, а войти в отчёт было неоткуда. */
              { id: 'lag', label: t(lang, 'Отставание от контракта', 'Contract lag') },
            ]}
          />
        }
      />
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {tab === 'backlog' ? <BacklogReport lang={lang} />
          : tab === 'status' ? <StatusReport lang={lang} />
          : tab === 'quality' ? <QualityReport lang={lang} />
          : tab === 'intake' ? <IntakeReport lang={lang} />
          : tab === 'lag' ? <ContractLagReport lang={lang} />
          : <EscalationReport lang={lang} />}
      </div>
    </div>
  )
}

function reportCard(title: ReactNode, sub: ReactNode, children: ReactNode, actions?: ReactNode) {
  return (
    <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{title}</div>
          {sub ? <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 2 }}>{sub}</div> : null}
        </div>
        {/* Выгрузка стоит у самого отчёта, а не в шапке экрана: выгружается то,
            что человек видит, и не приходится гадать, какая из трёх вкладок
            уедет в файл. */}
        {actions}
      </div>
      {children}
    </div>
  )
}

/**
 * Кнопка выгрузки отчёта.
 *
 * <p>Отчёт — читающая модель со своим потолком (сервер отдаёт не больше 500
 * строк). Выгружается ровно то, что пришло, и если пришло не всё — об этом
 * говорится вслух: молча отдать первые 500 строк из полутора тысяч значит
 * подсунуть человеку неполный документ под видом полного.
 */
function ReportExport<R>({ rows, total, columns, filename, sheet, lang, path, pick }: {
  rows: R[]
  total?: number
  columns: [string, (row: R) => unknown][]
  filename: string
  sheet: string
  lang: Lang
  /** Адрес отчёта без потолка: выгрузка просит всё, а не показанную страницу. */
  path?: string
  /** Где в ответе лежат строки и сколько их всего. По умолчанию — `rows`/`total`.
      Отчёт эскалации отдаёт три списка сразу (`docs`, `comments`, `steps`), поля
      `rows` у него нет вовсе: выгрузка молча брала показанные 200 строк из
      пяти тысяч и называла файл полным, а «Выгружено не всё» не срабатывало,
      потому что и `total` приходил пустым. Отчёт несут на планёрку. */
  pick?: (raw: Record<string, unknown>) => { rows: R[]; total?: number }
}) {
  const [busy, setBusy] = useState(false)
  return (
    <Button variant="secondary" size="sm" disabled={rows.length === 0 || busy}
            iconLeft={<Icon name="download" size={14} />}
            title={rows.length === 0 ? t(lang, 'Выгружать нечего', 'Nothing to export') : undefined}
            onClick={() => {
              /* Экран показывает первые две сотни, а в файл уходит весь отчёт:
                 «просрочено 1340» на экране и 200 строк в файле — это документ,
                 который противоречит сам себе. */
              if (!path) {
                exportRows(rows, columns, filename, sheet, lang)
                noteExport('отчёт ' + sheet, undefined, rows.length)
                /* У отчёта без своего адреса выгрузка — это показанные строки.
                   Молчаливый файл с первой сотней из полутора тысяч читается
                   как полный отчёт, поэтому неполнота называется вслух. */
                if (typeof total === 'number' && total > rows.length) {
                  toast({ tone: 'info', title: t(lang, 'Выгружено не всё', 'Partial export'),
                    text: t(lang, `В файл ушло ${rows.length} строк из ${total}: выгружается показанное.`,
                                  `${rows.length} of ${total} rows exported: only what is shown.`) })
                }
                return
              }
              setBusy(true)
              fetchSub<Record<string, unknown>>(path)
                .then((raw) => {
                  const all = pick ? pick(raw) : (raw as { rows: R[]; total?: number })
                  const full = all.rows ?? rows
                  exportRows(full, columns, filename, sheet, lang)
                  noteExport('отчёт ' + sheet, path, full.length)
                  const missed = typeof all.total === 'number' && all.total > full.length
                  if (missed) {
                    toast({ tone: 'info', title: t(lang, 'Выгружено не всё', 'Partial export'),
                      text: t(lang, `В файл ушло ${full.length} строк из ${all.total}: отчёт отдаёт не больше 5000.`,
                                    `${full.length} of ${all.total} rows exported: the report returns at most 5000.`) })
                  }
                })
                .catch((e: unknown) => toast({ tone: 'danger', title: t(lang, 'Выгрузка не удалась', 'Export failed'),
                                               text: e instanceof Error ? e.message : String(e) }))
                .finally(() => setBusy(false))
            }}>
      Excel
    </Button>
  )
}

/* Пока отчёт не пришёл, об этом надо сказать: пустая таблица читается как
   «работы нет», а не «данные ещё не загружены». Подмены из seed больше нет. */
function ScreenLoading({ lang }: { lang: Lang }) {
  return (
    <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', color: 'var(--text-subtle)' }}>
      <Spinner size={13} />
      {t(lang, 'Загрузка…', 'Loading…')}
    </div>
  )
}

/* Формы отчётов — ответы services/core (ReportsController). Все агрегаты
   считает сервер по всему проекту: по отданной странице их не сосчитать. */
interface BacklogRow {
  no: string
  title: string
  disc: string
  vendor: string
  purpose: PurposeCode | '—'
  life: LifeStatus
  crit: boolean
  forecast: string
  /** Дней сверх прогнозной даты; у документов, чей срок не наступил, — 0. */
  wait: number
}
interface Backlog {
  rows: BacklogRow[]
  total: number
  overdue: number
  avgWait: number
}
interface MdrStatusRow {
  id: string
  title: string
  disc: string
  owner: string
  docs: number
  afd: number
  ifa: number
  ifr: number
  planned: number
  overdue: number
}
interface EscalationRow {
  ref: string
  title: string
  disc: string
  vendor: string
  due: string
  /** Дней просрочки. */
  days: number
  /** Кого поднимать — сервер берёт адресата из матрицы по дисциплине. */
  target: string | null
  /** Исходный срок шага и отставание от него: продление двигает только `due`. */
  dueBase?: string | null
  baseDays?: number
}
interface Escalation {
  docs: EscalationRow[]
  comments: EscalationRow[]
  /** Просроченные шаги рассмотрения — вместе с отставанием от исходного срока. */
  steps: EscalationRow[]
  docsTotal: number
  commentsTotal: number
  stepsTotal: number
}

/* Отставание от КОНТРАКТНОГО срока (plan_base, V77): справка, на которой
   держатся удержания по контракту. Сервер считал её с самого начала, а войти в
   неё было неоткуда — вкладок было ровно пять. */
interface ContractLagRow {
  no: string
  title: string
  disc: string
  vendor: string
  stage: string
  planBase: string
  plan: string | null
  fact: string | null
  /** Дней сверх базового срока: сдали позже базы или ещё не сдали. */
  lag: number
}
interface ContractLag {
  rows: ContractLagRow[]
  total: number
  open: number
  avgLag: number
  maxLag: number
}

/* Параметры отчёта: дисциплина, подрядчик, контракт и окно дат.

   Отчёт «по всему проекту» — это отчёт, который никто не сдаёт: сдают сводку
   по своему контракту за месяц. Границы сотрудника при этом действуют и без
   параметров — их накладывает сервер. */
interface ReportParams {
  disc: string
  vendor: string
  contract: string
  from: string
  to: string
}

const NO_PARAMS: ReportParams = { disc: '', vendor: '', contract: '', from: '', to: '' }

/** Дата уходит в запрос в ISO: показывается она по-русски, а сравнивается как дата. */
const isoOf = (ru: string): string => {
  const d = parseRu(ru)
  if (!d) return ''
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function reportQuery(p: ReportParams, dates: boolean): string {
  const parts: string[] = []
  if (p.disc) parts.push('disc=' + encodeURIComponent(p.disc))
  if (p.vendor) parts.push('vendor=' + encodeURIComponent(p.vendor))
  if (p.contract) parts.push('contract=' + encodeURIComponent(p.contract))
  if (dates && isoOf(p.from)) parts.push('from=' + isoOf(p.from))
  if (dates && isoOf(p.to)) parts.push('to=' + isoOf(p.to))
  return parts.length ? '&' + parts.join('&') : ''
}

function ReportParamsBar({ lang, params, setParams, dates }: {
  lang: Lang
  params: ReportParams
  setParams: (p: ReportParams) => void
  /** Окно дат есть не у всех отчётов: у сводки по реестрам даты нет. */
  dates?: boolean
}) {
  const discRows = useDictRows('disc')
  const contracts = useContracts()
  const vendors = useList<Vendor>('vendors', { page: 0, pageSize: 200, sort: [{ id: 'code', desc: false }] })
  const set = (patch: Partial<ReportParams>) => setParams({ ...params, ...patch })
  return (
    <Toolbar>
      <Select size="sm" value={params.disc} onChange={(e) => set({ disc: e.target.value })} block={false} style={{ width: 150 }}>
        <option value="">{t(lang, 'Все дисциплины', 'All disciplines')}</option>
        {discRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}
      </Select>
      <Select size="sm" value={params.vendor} onChange={(e) => set({ vendor: e.target.value })} block={false} style={{ width: 190 }}>
        <option value="">{t(lang, 'Все подрядчики', 'All contractors')}</option>
        {(vendors.data?.rows ?? []).map((v) => <option key={v.code} value={v.name}>{v.name}</option>)}
      </Select>
      <Select size="sm" value={params.contract} onChange={(e) => set({ contract: e.target.value })} block={false} style={{ width: 190 }}>
        <option value="">{t(lang, 'Все контракты', 'All contracts')}</option>
        {contracts.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.title}</option>)}
      </Select>
      {dates ? (
        <>
          <div style={{ width: 130 }}><DateInput value={params.from} onChange={(v) => set({ from: v })} placeholder={t(lang, 'с', 'from')} /></div>
          <div style={{ width: 130 }}><DateInput value={params.to} onChange={(v) => set({ to: v })} placeholder={t(lang, 'по', 'to')} /></div>
        </>
      ) : null}
      <ToolSpacer />
      <Button variant="ghost" size="sm" onClick={() => setParams(NO_PARAMS)}>{t(lang, 'Сбросить', 'Reset')}</Button>
    </Toolbar>)
}

function BacklogReport({ lang }: { lang: Lang }) {
  // Строки уже отсортированы по ожиданию, а три показателя посчитаны по всему
  // бэклогу: считать их по странице значило бы показывать разные числа при
  // разном размере страницы.
  const [params, setParams] = useState<ReportParams>(NO_PARAMS)
  const backlog = useSub<Backlog>('/reports/backlog?limit=200' + reportQuery(params, true))
  const bar = <ReportParamsBar lang={lang} params={params} setParams={setParams} dates />
  if (backlog.isError) return <ScreenError lang={lang} />
  if (backlog.isPending) return <>{bar}<ScreenLoading lang={lang} /></>
  const rows = backlog.data?.rows ?? []
  const cols: Column<BacklogRow>[] = [
    { key: 'no', header: colHeader(lang, '№ документа'), mono: true, nowrap: true, width: 150, render: (r) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Crit on={r.crit} /><span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.no}</span></span> },
    { key: 'disc', header: colHeader(lang, 'Дисц.'), width: 60, render: (r) => <DiscTag code={r.disc} /> },
    { key: 'vendor', header: colHeader(lang, 'Подрядчик'), render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.vendor}</span> },
    { key: 'purpose', header: colHeader(lang, 'Назн.'), width: 60, render: (r) => <Purpose value={r.purpose} size="sm" /> },
    { key: 'life', header: colHeader(lang, 'Статус'), width: 160, render: (r) => <LifeBadge life={r.life} /> },
    { key: 'forecast', header: colHeader(lang, 'Прогноз'), mono: true, align: 'right', width: 90, render: (r) => <span style={{ color: r.wait > 0 ? 'var(--danger-ink)' : 'var(--text-muted)' }}>{r.forecast}</span> },
    { key: 'wait', header: colHeader(lang, 'Ожидание'), align: 'right', width: 110, render: (r) => <SlaPill due={r.forecast} /> },
  ]
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {bar}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        <KPIStat icon="layers" label={t(lang, 'В бэклоге', 'In backlog')} value={backlog.isSuccess ? backlog.data.total : '—'} accent />
        <KPIStat icon="flame" label={t(lang, 'Из них просрочено', 'Overdue')} value={backlog.data?.overdue ?? 0} tone="down" />
        <KPIStat icon="timer" label={t(lang, 'Ср. ожидание', 'Avg wait')} value={backlog.data?.avgWait ?? 0} unit={t(lang, 'дн', 'd')} tone="warn" />
      </div>
      {reportCard(
        t(lang, 'Документы в ожидании рассмотрения и переиздания', 'Documents awaiting review / reissue'),
        t(lang, 'Отсортировано по длительности ожидания', 'Sorted by wait time'),
        <DataTable columns={cols} rows={rows} rowKey={(r) => r.no} />,
        <ReportExport<BacklogRow>
          rows={rows} total={backlog.data?.total} lang={lang}
          path={'/reports/backlog?limit=5000' + reportQuery(params, true)}
          filename="backlog.xlsx" sheet={t(lang, 'Бэклог', 'Backlog')}
          columns={[
            ['№ документа', (r) => r.no],
            ['Дисциплина', (r) => r.disc],
            ['Подрядчик', (r) => r.vendor],
            ['Назначение', (r) => r.purpose],
            /* Состояние — названием, как в бейдже той же строки на экране:
               внутренний код в выгрузке приходилось заменять руками. */
            ['Статус', (r) => lifeLabel(r.life)],
            ['Прогноз', (r) => r.forecast],
            ['Ожидание, дн', (r) => r.wait],
            ['Критичность', (r) => r.crit],
          ]} />,
      )}
    </div>
  )
}

function StatusReport({ lang }: { lang: Lang }) {
  // Комплектность по реестрам собирает сервер: он один видит все документы MDR.
  const [params, setParams] = useState<ReportParams>(NO_PARAMS)
  const status = useSub<MdrStatusRow[]>('/reports/mdr-status?' + reportQuery(params, false).slice(1))
  const bar = <ReportParamsBar lang={lang} params={params} setParams={setParams} />
  if (status.isError) return <ScreenError lang={lang} />
  if (status.isPending) return <>{bar}<ScreenLoading lang={lang} /></>
  const mdrs = status.data ?? []
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {bar}
      {reportCard(
        t(lang, 'Статус подачи и согласования по реестрам MDR', 'Submission & approval status by MDR'),
        t(lang, 'Комплектность: получено → на рассмотрении → согласовано (AFD)', 'Received → in review → approved'),
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thS()}>{t(lang, 'Реестр', 'Register')}</th>
              <th style={{ ...thS(), textAlign: 'right', width: 70 }}>{t(lang, 'Всего', 'Total')}</th>
              <th style={{ ...thS(), textAlign: 'center', width: 64 }}>AFD</th>
              <th style={{ ...thS(), textAlign: 'center', width: 64 }}>IFA</th>
              <th style={{ ...thS(), textAlign: 'center', width: 64 }}>IFR</th>
              <th style={{ ...thS(), width: 240 }}>{t(lang, 'Готовность', 'Completion')}</th>
              <th style={{ ...thS(), textAlign: 'center', width: 80 }}>{t(lang, 'Просроч.', 'Overdue')}</th>
            </tr>
          </thead>
          <tbody>
            {mdrs.map((m) => {
              // Пустой реестр — рядовой случай (документы ещё не заведены):
              // без проверки в готовности встало бы NaN%.
              const pct = m.docs ? Math.round((m.afd / m.docs) * 100) : 0
              return (
                <tr key={m.id} className="lav-tr" style={{ height: 48 }}>
                  <td style={tdS()}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      <DiscTag code={m.disc} />
                      <span>
                        <span style={{ display: 'block', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)', fontWeight: 500 }}>{m.title}</span>
                        <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)' }}>{m.id}</span>
                      </span>
                    </span>
                  </td>
                  <td style={{ ...tdS(), textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-strong)' }}>{m.docs}</td>
                  <td style={{ ...tdS(), textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--green-600)' }}>{m.afd}</td>
                  <td style={{ ...tdS(), textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--blue-600)' }}>{m.ifa}</td>
                  <td style={{ ...tdS(), textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent-ink)' }}>{m.ifr}</td>
                  <td style={tdS()}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ flex: 1 }}><CompletionBar afd={m.afd} ifa={m.ifa} ifr={m.ifr} planned={m.planned} /></span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)', width: 34 }}>{pct}%</span>
                    </span>
                  </td>
                  <td style={{ ...tdS(), textAlign: 'center' }}>{m.overdue ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--danger-ink)' }}>● {m.overdue}</span> : <span style={{ color: 'var(--text-subtle)' }}>—</span>}</td>
                </tr>
              )
            })}
          </tbody>
        </table>,
        <ReportExport<MdrStatusRow>
          rows={mdrs} lang={lang}
          filename="mdr-status.xlsx" sheet={t(lang, 'Статус MDR', 'MDR status')}
          columns={[
            ['Реестр', (m) => m.id],
            ['Наименование', (m) => m.title],
            ['Дисциплина', (m) => m.disc],
            ['Всего', (m) => m.docs],
            ['AFD', (m) => m.afd],
            ['IFA', (m) => m.ifa],
            ['IFR', (m) => m.ifr],
            ['Запланировано', (m) => m.planned],
            // Тот же процент, что на экране: считать его в файле иначе значит
            // получить два ответа на один вопрос.
            ['Готовность, %', (m) => (m.docs ? Math.round((m.afd / m.docs) * 100) : 0)],
            ['Просрочено', (m) => m.overdue],
          ]} />,
      )}
    </div>
  )
}

interface QualityRow {
  vendor: string
  decided: number
  a: number
  b: number
  c: number
  rounds: number
  answerDays: number
}
interface Quality {
  rows: QualityRow[]
  total: number
  rejected: number
}

/* Качество подачи по подрядчикам.

   Ежемесячная оценка — стандартный вход в письмо о неудовлетворительном
   качестве подачи и в удержание по контракту: сколько документов вернули с
   кодом C, за сколько кругов документ доходит до согласования, за сколько дней
   подрядчик отвечает на замечания. Отчётов было три, и ни один не разрезал
   данные по коду рассмотрения. */
function QualityReport({ lang }: { lang: Lang }) {
  const [params, setParams] = useState<ReportParams>(NO_PARAMS)
  const path = '/reports/quality' + (reportQuery(params, true).replace(/^&/, '?') || '')
  const quality = useSub<Quality>(path)
  const bar = <ReportParamsBar lang={lang} params={params} setParams={setParams} dates />
  if (quality.isError) return <ScreenError lang={lang} />
  if (quality.isPending) return <>{bar}<ScreenLoading lang={lang} /></>
  const rows = quality.data?.rows ?? []
  const cols: Column<QualityRow>[] = [
    { key: 'vendor', header: colHeader(lang, 'Подрядчик'), render: (r) => <span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.vendor}</span> },
    { key: 'decided', header: colHeader(lang, 'Решений'), align: 'right', width: 90, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.decided}</span> },
    /* Столбцы называются смыслом кода, а не буквой: сервер и считает их по
       смыслу — у следующего заказчика коды называются цифрами (V71). */
    { key: 'a', header: colHeader(lang, 'Принято'), align: 'right', width: 90, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--green-600)' }}>{r.a}</span> },
    { key: 'b', header: colHeader(lang, 'С замеч.'), align: 'right', width: 90, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--amber-600, var(--text-muted))' }}>{r.b}</span> },
    { key: 'c', header: colHeader(lang, 'Не принято'), align: 'right', width: 100, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--danger-ink)' }}>{r.c}</span> },
    { key: 'rounds', header: colHeader(lang, 'Кругов, ср.'), align: 'right', width: 110, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.rounds}</span> },
    { key: 'answerDays', header: colHeader(lang, 'Ответ, дн'), align: 'right', width: 110, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.answerDays}</span> },
  ]
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {bar}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        <KPIStat icon="check-check" label={t(lang, 'Решений по документам', 'Decisions')} value={quality.data?.total ?? 0} accent />
        <KPIStat icon="file-warning" label={t(lang, 'Из них код C', 'Of them code C')} value={quality.data?.rejected ?? 0} tone="down" />
      </div>
      {reportCard(
        t(lang, 'Качество подачи по подрядчикам', 'Submission quality by contractor'),
        t(lang, 'Коды рассмотрения, среднее число кругов и время ответа на замечания',
                 'Review codes, average rounds and comment response time'),
        <DataTable columns={cols} rows={rows} rowKey={(r) => r.vendor} />,
        /* Потолка у отчёта нет — он отдаёт все строки, — и «total» здесь не
           их число, а число решений по документам. Передавать его значило
           обещать «выгружено не всё» на полностью выгруженном файле. */
        <ReportExport<QualityRow>
          rows={rows} lang={lang}
          filename="quality.xlsx" sheet={t(lang, 'Качество подачи', 'Quality')}
          columns={[
            ['Подрядчик', (r) => r.vendor],
            ['Решений', (r) => r.decided],
            ['A', (r) => r.a],
            ['B', (r) => r.b],
            ['C', (r) => r.c],
            ['Кругов, ср.', (r) => r.rounds],
            ['Ответ, дн', (r) => r.answerDays],
          ]} />,
      )}
    </div>
  )
}

interface IntakeRow {
  vendor: string
  packages: number
  whole: number
  rows: number
}
interface IntakeGround {
  code: string
  reason: string
  count: number
}
interface Intake {
  rows: IntakeRow[]
  grounds: IntakeGround[]
  total: number
  returned: number
}

/* Входной контроль по подрядчикам.

   Возврат на входном контроле — это круг, который подрядчик прошёл впустую, и
   срок, который при этом ушёл. До сих пор основания возврата были видны только
   в самом пакете: «сколько вернули и по каким основаниям» приходилось считать
   глазами по журналу, и в разговор о качестве подачи это не попадало. */
function IntakeReport({ lang }: { lang: Lang }) {
  const [params, setParams] = useState<ReportParams>(NO_PARAMS)
  const path = '/reports/intake' + (reportQuery(params, true).replace(/^&/, '?') || '')
  const intake = useSub<Intake>(path)
  const bar = <ReportParamsBar lang={lang} params={params} setParams={setParams} dates />
  if (intake.isError) return <ScreenError lang={lang} />
  if (intake.isPending) return <>{bar}<ScreenLoading lang={lang} /></>
  const rows = intake.data?.rows ?? []
  const grounds = intake.data?.grounds ?? []
  const num = (v: number, tone?: string) =>
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: tone }}>{v}</span>
  const cols: Column<IntakeRow>[] = [
    { key: 'vendor', header: colHeader(lang, 'Подрядчик'), render: (r) => <span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.vendor}</span> },
    { key: 'packages', header: colHeader(lang, 'Проверено пакетов'), align: 'right', width: 150, render: (r) => num(r.packages) },
    { key: 'whole', header: colHeader(lang, 'Возвращено целиком'), align: 'right', width: 160, render: (r) => num(r.whole, 'var(--danger-ink)') },
    { key: 'rows', header: colHeader(lang, 'Возвращено строк'), align: 'right', width: 150, render: (r) => num(r.rows, 'var(--danger-ink)') },
    /* Доля считается здесь же и от проверенных пакетов: доля от возвратов
       отвечала бы на другой вопрос. */
    { key: 'share', header: colHeader(lang, 'Доля возвратов, %'), align: 'right', width: 150,
      render: (r) => num(r.packages ? Math.round((r.whole / r.packages) * 100) : 0) },
  ]
  const groundCols: Column<IntakeGround>[] = [
    { key: 'reason', header: colHeader(lang, 'Основание'), render: (r) => <span style={{ color: 'var(--text-body)' }}>{r.reason}</span> },
    { key: 'count', header: colHeader(lang, 'Возвратов'), align: 'right', width: 120, render: (r) => num(r.count) },
  ]
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {bar}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        <KPIStat icon="clipboard-check" label={t(lang, 'Проверено пакетов', 'Packages checked')} value={intake.data?.total ?? 0} accent />
        <KPIStat icon="undo-2" label={t(lang, 'Возвращено', 'Returned')} value={intake.data?.returned ?? 0} tone="down" />
      </div>
      {reportCard(
        t(lang, 'Возвраты по подрядчикам', 'Returns by contractor'),
        t(lang, 'Пакеты, прошедшие входной контроль, и что из них вернулось не начав рассмотрения',
                 'Packages that passed intake control and what went back before review'),
        <DataTable columns={cols} rows={rows} rowKey={(r) => r.vendor} />,
        /* «total» — число проверенных пакетов, а не строк отчёта: строка тут
           на подрядчика. Полный файл не должен объявлять себя неполным. */
        <ReportExport<IntakeRow>
          rows={rows} lang={lang}
          filename="intake.xlsx" sheet={t(lang, 'Входной контроль', 'Intake')}
          columns={[
            ['Подрядчик', (r) => r.vendor],
            ['Проверено пакетов', (r) => r.packages],
            ['Возвращено целиком', (r) => r.whole],
            ['Возвращено строк', (r) => r.rows],
            ['Доля возвратов, %', (r) => (r.packages ? Math.round((r.whole / r.packages) * 100) : 0)],
          ]} />,
      )}
      {reportCard(
        t(lang, 'Основания возврата', 'Return grounds'),
        t(lang, 'Одно и то же основание считается и по пакетам, и по строкам: разговор с подрядчиком один',
                 'Package and row returns share one ground: it is a single conversation'),
        <DataTable columns={groundCols} rows={grounds} rowKey={(r) => r.code} />,
        <ReportExport<IntakeGround>
          rows={grounds} lang={lang}
          filename="intake-grounds.xlsx" sheet={t(lang, 'Основания', 'Grounds')}
          columns={[
            ['Основание', (r) => r.reason],
            ['Возвратов', (r) => r.count],
          ]} />,
      )}
    </div>
  )
}

function EscalationReport({ lang }: { lang: Lang }) {
  // Просрочка считается сервером от сегодняшней даты и по тому же определению,
  // что на обзоре и в реестрах: два определения давали бы два разных ответа.
  const [params, setParams] = useState<ReportParams>(NO_PARAMS)
  const path = '/reports/escalation?limit=200' + reportQuery(params, true)
  const escalation = useSub<Escalation>(path)
  const bar = <ReportParamsBar lang={lang} params={params} setParams={setParams} dates />
  const escalate = useCommand<{ ref: string }, { escalated: boolean; target?: string }>({
    path: () => '/reports/escalate',
    body: (a) => a,
    // Отчёт перечитывается: сервер мог закрыть просрочку, пока экран был открыт.
    invalidate: () => [qk.sub(path), qk.meta('notifs')],
  })
  if (escalation.isError) return <ScreenError lang={lang} />
  if (escalation.isPending) return <>{bar}<ScreenLoading lang={lang} /></>
  const overdueDocs = escalation.data?.docs ?? []
  const overdueCrs = escalation.data?.comments ?? []
  const overdueSteps = escalation.data?.steps ?? []
  /* Строка просроченного ответа: код замечания, документ, срок и кого
     поднимать. Те же поля, что у документа, — сервер отдаёт их одним видом. */
  const crsCols: Column<EscalationRow>[] = [
    { key: 'ref', header: colHeader(lang, 'Замечание'), mono: true, width: 190,
      render: (r) => <span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.ref}</span> },
    { key: 'title', header: colHeader(lang, 'Документ'), render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.title}</span> },
    { key: 'disc', header: colHeader(lang, 'Дисц.'), width: 60, render: (r) => <DiscTag code={r.disc} /> },
    { key: 'vendor', header: colHeader(lang, 'Подрядчик'), width: 200, render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.vendor}</span> },
    { key: 'due', header: colHeader(lang, 'Срок ответа'), mono: true, align: 'right', width: 110, render: (r) => <SlaPill due={r.due} /> },
    { key: 'days', header: colHeader(lang, 'Просрочено'), align: 'right', width: 100,
      render: (r) => <span style={{ color: 'var(--danger-ink)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.days}</span> },
    { key: 'target', header: colHeader(lang, 'Поднимать на'), width: 180, render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.target ?? '—'}</span> },
  ]
  /* Шаг рассмотрения: рядом с действующим сроком стоит исходный. Разница и
     есть то, что прятало продление. */
  const stepCols: Column<EscalationRow>[] = [
    { key: 'ref', header: colHeader(lang, 'Пакет'), mono: true, width: 150,
      render: (r) => <span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.ref}</span> },
    { key: 'title', header: colHeader(lang, 'Шаг'), render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.title}</span> },
    { key: 'disc', header: colHeader(lang, 'Дисц.'), width: 60, render: (r) => <DiscTag code={r.disc} /> },
    { key: 'due', header: colHeader(lang, 'Срок'), mono: true, align: 'right', width: 110, render: (r) => <SlaPill due={r.due} /> },
    { key: 'dueBase', header: colHeader(lang, 'Исходный срок'), mono: true, align: 'right', width: 130,
      render: (r) => <span style={{ color: r.dueBase && r.dueBase !== r.due ? 'var(--danger-ink)' : 'var(--text-subtle)' }}>{r.dueBase ?? '—'}</span> },
    { key: 'baseDays', header: colHeader(lang, 'Отставание, дн'), align: 'right', width: 130,
      render: (r) => <span style={{ color: 'var(--danger-ink)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.baseDays ?? 0}</span> },
    { key: 'target', header: colHeader(lang, 'Исполнитель'), width: 180, render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.target ?? '—'}</span> },
  ]
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {bar}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        <KPIStat icon="file-warning" label={t(lang, 'Просроченные документы', 'Overdue documents')} value={overdueDocs.length} tone="down" accent />
        <KPIStat icon="message-square-warning" label={t(lang, 'Просроченные CRS', 'Overdue CRS')} value={overdueCrs.length} tone="down" />
        {/* Плитка «Эскалация на» убрана: адресат у каждой строки свой — сервер
            берёт его из матрицы по дисциплине, — а одного на проект не бывает.
            В прототипе там стояла фамилия литералом, одинаковая для всех. */}
      </div>
      {reportCard(
        t(lang, 'Просроченные документы — на эскалацию', 'Overdue documents — to escalate'),
        t(lang, 'Эскалация ГИП при превышении SLA', 'Escalated to chief engineer past SLA'),
        <div>
          {overdueDocs.length ? (
            overdueDocs.map((d) => (
              <div key={d.ref} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <CIcon name="flag" size={15} color="var(--danger-ink)" />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{d.ref}</span>
                    <DiscTag code={d.disc} />
                    {/* Назначения (IFA/AFD) в строке эскалации нет: сервер его не
                        отдаёт, а показывать чужое значение хуже, чем не показывать. */}
                  </div>
                  <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 2 }}>{d.title} · {d.vendor}{d.target ? ' · ' + t(lang, 'эскалация на ', 'escalate to ') + d.target : ''}</div>
                </div>
                <SlaPill due={d.due} />
                {/* Раньше кнопка показывала тост, ничего не записывая. Теперь
                    шлёт извещение тому, кого отчёт и так называет адресатом, —
                    тем же каналом, что и суточная сводка по срокам. */}
                <Button variant="secondary" size="sm" iconLeft={<Icon name="siren" size={13} />}
                        disabled={escalate.isPending || !d.target}
                        title={d.target
                          ? t(lang, 'Известить ' + d.target, 'Notify ' + d.target)
                          : t(lang, 'В матрице по этой дисциплине нет утверждающего', 'No approver in the matrix for this discipline')}
                        onClick={() => escalate.mutate({ ref: d.ref }, {
                          onSuccess: (r) => toast(r.escalated
                            ? { tone: 'success', title: t(lang, 'Эскалировано', 'Escalated'), text: String(r.target ?? '') }
                            /* Повтор в тот же день письма не шлёт — и молчать об
                               этом нельзя, иначе человек нажмёт ещё пять раз. */
                            : { tone: 'info', title: t(lang, 'Сегодня уже поднимали', 'Already escalated today'), text: String(r.target ?? '') }),
                        })}>
                  {t(lang, 'Эскалировать', 'Escalate')}
                </Button>
              </div>
            ))
          ) : (
            <div style={{ padding: 20, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{t(lang, 'Просроченных документов нет.', 'No overdue documents.')}</div>
          )}
        </div>,
        <ReportExport<EscalationRow>
          rows={overdueDocs} total={escalation.data?.docsTotal} lang={lang}
          path={'/reports/escalation?limit=5000' + reportQuery(params, true)}
          /* Отчёт отдаёт три списка сразу, поля `rows` у него нет: выгрузка
             брала показанные 200 строк из пяти тысяч, называла файл полным и
             писала в журнал «200». Отчёт несут на планёрку. */
          pick={(raw) => ({ rows: (raw.docs ?? []) as EscalationRow[], total: raw.docsTotal as number | undefined })}
          filename="escalation.xlsx" sheet={t(lang, 'Эскалация', 'Escalation')}
          columns={[
            ['Документ', (d) => d.ref],
            ['Наименование', (d) => d.title],
            ['Дисциплина', (d) => d.disc],
            ['Подрядчик', (d) => d.vendor],
            ['Срок', (d) => d.due],
            ['Просрочено, дн', (d) => d.days],
            // Адресат у каждой строки свой — сервер берёт его из матрицы по
            // дисциплине; одного на проект не бывает.
            ['Эскалация на', (d) => d.target ?? ''],
          ]} />,
      )}
      {/* Просроченные ответы на замечания: сервер считал их с самого начала, а
          отчёт показывал одну плитку с числом. На планёрке нужен список — с
          документом, дисциплиной, сроком и адресатом, чтобы разослать и
          приложить к протоколу. */}
      {reportCard(
        t(lang, 'Просроченные ответы на замечания', 'Overdue comment answers'),
        t(lang, 'Срок ответа подрядчика прошёл, ответа нет', 'The contractor response is past due'),
        <div>
          {overdueCrs.length ? (
            <DataTable columns={crsCols} rows={overdueCrs} rowKey={(r) => r.ref} />
          ) : (
            <div style={{ padding: 20, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{t(lang, 'Просроченных ответов нет.', 'No overdue answers.')}</div>
          )}
        </div>,
        <ReportExport<EscalationRow>
          rows={overdueCrs} total={escalation.data?.commentsTotal} lang={lang}
          path={'/reports/escalation?limit=5000' + reportQuery(params, true)}
          pick={(raw) => ({ rows: (raw.comments ?? []) as EscalationRow[], total: raw.commentsTotal as number | undefined })}
          filename="crs-overdue.xlsx" sheet={t(lang, 'Просроченные CRS', 'Overdue CRS')}
          columns={[
            ['Замечание', (d) => d.ref],
            ['Документ', (d) => d.title],
            ['Дисциплина', (d) => d.disc],
            ['Подрядчик', (d) => d.vendor],
            ['Срок ответа', (d) => d.due],
            ['Просрочено, дн', (d) => d.days],
            ['Поднимать на', (d) => d.target ?? ''],
          ]} />,
      )}
      {/* Просроченные шаги рассмотрения. Здесь же видно продление: срок шага
          двигали, и по новому сроку шаг шёл «вовремя» — просрочка исчезала из
          отчётов без следа. Отставание считается от ИСХОДНОГО срока. */}
      {reportCard(
        t(lang, 'Просроченные шаги рассмотрения', 'Overdue review steps'),
        t(lang, 'Отставание считается от исходного срока: продление его не двигает',
                 'The lag is counted from the original deadline: extending does not move it'),
        <div>
          {overdueSteps.length ? (
            <DataTable columns={stepCols} rows={overdueSteps} rowKey={(r) => r.ref + '|' + r.title} />
          ) : (
            <div style={{ padding: 20, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{t(lang, 'Просроченных шагов нет.', 'No overdue steps.')}</div>
          )}
        </div>,
        <ReportExport<EscalationRow>
          rows={overdueSteps} total={escalation.data?.stepsTotal} lang={lang}
          path={'/reports/escalation?limit=5000' + reportQuery(params, true)}
          pick={(raw) => ({ rows: (raw.steps ?? []) as EscalationRow[], total: raw.stepsTotal as number | undefined })}
          filename="steps-overdue.xlsx" sheet={t(lang, 'Просроченные шаги', 'Overdue steps')}
          columns={[
            ['Пакет', (d) => d.ref],
            ['Шаг', (d) => d.title],
            ['Дисциплина', (d) => d.disc],
            ['Отправитель', (d) => d.vendor],
            ['Действующий срок', (d) => d.due],
            ['Исходный срок', (d) => d.dueBase ?? ''],
            ['Отставание от исходного, дн', (d) => d.baseDays ?? 0],
            ['Исполнитель', (d) => d.target ?? ''],
          ]} />,
      )}
    </div>
  )
}

/* Отставание от контрактного срока.

   Сервер считал справку с самого начала (plan_base, V77), а войти в неё было
   неоткуда: вкладок было ровно пять. Именно на ней держатся удержания по
   контракту, и собиралась она до сих пор руками из выгрузки реестра. */
function ContractLagReport({ lang }: { lang: Lang }) {
  const [params, setParams] = useState<ReportParams>(NO_PARAMS)
  const path = '/reports/contract-lag?limit=200' + reportQuery(params, true)
  const lag = useSub<ContractLag>(path)
  const bar = <ReportParamsBar lang={lang} params={params} setParams={setParams} dates />
  if (lag.isError) return <ScreenError lang={lang} />
  if (lag.isPending) return <>{bar}<ScreenLoading lang={lang} /></>
  const rows = lag.data?.rows ?? []
  const cols: Column<ContractLagRow>[] = [
    { key: 'no', header: colHeader(lang, '№ документа'), mono: true, nowrap: true, width: 150,
      render: (r) => <span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.no}</span> },
    { key: 'disc', header: colHeader(lang, 'Дисц.'), width: 60, render: (r) => <DiscTag code={r.disc} /> },
    { key: 'vendor', header: colHeader(lang, 'Подрядчик'), render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.vendor}</span> },
    { key: 'stage', header: colHeader(lang, 'Стадия'), width: 70, render: (r) => <Purpose value={r.stage as PurposeCode} size="sm" /> },
    { key: 'planBase', header: colHeader(lang, 'Контрактный срок'), mono: true, align: 'right', width: 140,
      render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.planBase}</span> },
    { key: 'plan', header: colHeader(lang, 'Текущий план'), mono: true, align: 'right', width: 130,
      render: (r) => <span style={{ color: 'var(--text-subtle)' }}>{r.plan ?? '—'}</span> },
    { key: 'fact', header: colHeader(lang, 'Факт'), mono: true, align: 'right', width: 100,
      render: (r) => <span style={{ color: 'var(--text-subtle)' }}>{r.fact ?? '—'}</span> },
    { key: 'lag', header: colHeader(lang, 'Отставание, дн'), align: 'right', width: 130,
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--danger-ink)' }}>{r.lag}</span> },
  ]
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {bar}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        <KPIStat icon="calendar-days" label={t(lang, 'Строк с отставанием', 'Lagging rows')} value={lag.data?.total ?? 0} tone="down" accent />
        <KPIStat icon="clock" label={t(lang, 'Из них не сдано', 'Still open')} value={lag.data?.open ?? 0} tone="down" />
        <KPIStat icon="timer" label={t(lang, 'Ср. отставание', 'Avg lag')} value={lag.data?.avgLag ?? 0} unit={t(lang, 'дн', 'd')} tone="warn" />
        <KPIStat icon="flame" label={t(lang, 'Наибольшее', 'Max')} value={lag.data?.maxLag ?? 0} unit={t(lang, 'дн', 'd')} tone="down" />
      </div>
      {reportCard(
        t(lang, 'Отставание от контрактного срока', 'Lag against the contract schedule'),
        t(lang, 'Сравнение с базовым сроком — планом на момент первого согласования реестра: его согласования не двигают',
                 'Compared with the base date — the plan at the first register approval; later approvals do not move it'),
        <DataTable columns={cols} rows={rows} rowKey={(r) => r.no + '|' + r.stage} />,
        <ReportExport<ContractLagRow>
          rows={rows} total={lag.data?.total} lang={lang}
          path={'/reports/contract-lag?limit=5000' + reportQuery(params, true)}
          filename="contract-lag.xlsx" sheet={t(lang, 'Отставание', 'Contract lag')}
          columns={[
            ['№ документа', (r) => r.no],
            ['Наименование', (r) => r.title],
            ['Дисциплина', (r) => r.disc],
            ['Подрядчик', (r) => r.vendor],
            ['Стадия', (r) => r.stage],
            ['Контрактный срок', (r) => r.planBase],
            ['Текущий план', (r) => r.plan ?? ''],
            ['Факт', (r) => r.fact ?? ''],
            ['Отставание, дн', (r) => r.lag],
          ]} />,
      )}
    </div>
  )
}

// ===== Audit log ========================================================
const FACET_COLUMNS = ['disc', 'life', 'code'] as const

const AUDIT_PAGE_SIZE = 50
/* Порция и потолок выгрузки журнала. Потолок нужен: аудит растёт быстрее всех
   таблиц, и «выгрузить всё» однажды означало бы миллион строк в браузере.

   Пять тысяч — это меньше месяца работы проекта, а выгружают журнал как раз за
   месяц и за квартал, к разбору. Потолок поднят до пятидесяти тысяч (лист xlsx
   держит миллион). Порция остаётся 200: это потолок страницы на сервере
   (PageResponse.MAX_PAGE_SIZE), и просить больше бессмысленно — он всё равно
   срежет. Обрезка по-прежнему называется вслух. */
const AUDIT_EXPORT_CHUNK = 200
const AUDIT_EXPORT_LIMIT = 50000
const SEARCH_PAGE_SIZE = 50
const DOCS_PAGE_SIZE = 100

const ACT: Record<string, { ru: string; c: string; i: string }> = {
  register: { ru: 'Регистрация', c: '--blue-500', i: 'inbox' },
  assign: { ru: 'Назначение', c: '--amber-500', i: 'user' },
  comment: { ru: 'Замечание', c: '--yellow-600', i: 'message-square' },
  approve: { ru: 'Утверждение', c: '--green-500', i: 'check-check' },
  upload: { ru: 'Загрузка', c: '--blue-500', i: 'upload' },
  /* Скачивание — не загрузка: раньше оба события показывались одним словом,
     и «Загрузка» напротив скачанного файла читалась как подмена. */
  download: { ru: 'Скачивание', c: '--slate-400', i: 'download' },
  issue: { ru: 'Выпуск TRM', c: '--amber-500', i: 'send' },
  login: { ru: 'Вход', c: '--slate-400', i: 'user' },
  /* Выгрузка: файл собирает браузер, а «кто унёс реестр» обязано быть видно. */
  export: { ru: 'Выгрузка', c: '--violet-600', i: 'download' },
  /* Отказ в доступе: разбор происшествия начинается с попыток, а в журнале
     было только то, что получилось. */
  denied: { ru: 'Отказано в доступе', c: '--red-500', i: 'ban' },
  /* Эскалация: вмешательство в процесс, а не просмотр. В журнале её не было
     вовсе — «кто и когда поднимал документ» отвечать было нечем. */
  escalate: { ru: 'Эскалация', c: '--red-500', i: 'siren' },
  /* Остальные значения, которые пишут команды: без них фильтр «Все действия»
     показывал не все, а строка журнала оставалась без подписи. */
  create: { ru: 'Создание', c: '--green-500', i: 'plus' },
  update: { ru: 'Изменение', c: '--amber-500', i: 'pencil' },
  remove: { ru: 'Удаление', c: '--red-500', i: 'trash-2' },
  delete: { ru: 'Удаление', c: '--red-500', i: 'trash-2' },
  markup: { ru: 'Разметка', c: '--violet-600', i: 'pencil' },
  dicts: { ru: 'Справочники', c: '--slate-400', i: 'settings' },
  mail: { ru: 'Почта', c: '--slate-400', i: 'send' },
  settings: { ru: 'Настройки', c: '--slate-400', i: 'settings' },
}
/* Окно дат журнала в том виде, в каком его понимает сервер: `range:с..по`
   по колонке отметки времени. Пустые границы означают «без ограничения», а не
   пустую выборку. */
function auditWindow(from: string, to: string): Record<string, string[]> {
  const a = isoOf(from)
  const b = isoOf(to)
  return a || b ? { ts: [`range:${a}..${b}`] } : {}
}

export function AuditLog({ lang = 'ru' }: { lang?: Lang }) {
  const me = useMe()
  const [q, setQ] = useState('')
  const [act, setAct] = useState('')
  const [page, setPage] = useState(0)
  /* Период. Журнал спрашивают за месяц, за неделю, за день разбора: без окна
     дат приходилось листать всё подряд, а выгрузка упиралась в потолок строк
     ровно потому, что сузить её было нечем. */
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const window = auditWindow(from, to)

  // Аудит — самая быстрорастущая таблица системы, поэтому читается только
  // постранично, а поиск, период и фильтр по действию выполняются на сервере.
  const auditQuery = useList<AuditEntry>('audit', {
    page,
    pageSize: AUDIT_PAGE_SIZE,
    sort: [{ id: 'ts', desc: true }],
    filters: { ...(act ? { action: [act] } : {}), ...window },
    q: q || undefined,
  })
  const rows = auditQuery.data?.rows ?? []
  const reset = (apply: () => void) => { apply(); setPage(0) }

  /* Выгрузка берёт весь отфильтрованный журнал порциями, а не показанную
     страницу. Журнал — самая быстрорастущая таблица системы, поэтому выгрузка
     ограничена тем же отбором, что стоит на экране: «весь аудит целиком» в
     xlsx не влезет и никому не нужен. */
  const exportLog = async () => {
    const all: AuditEntry[] = []
    let p = 0
    let pageCount = 1
    do {
      const chunk = await fetchList<AuditEntry>('audit', {
        page: p, pageSize: AUDIT_EXPORT_CHUNK, sort: [{ id: 'ts', desc: true }],
        filters: { ...(act ? { action: [act] } : {}), ...window }, q: q || undefined,
      })
      all.push(...chunk.rows)
      pageCount = chunk.pageCount
      p += 1
    } while (p < pageCount && all.length < AUDIT_EXPORT_LIMIT)
    exportAudit(all.map((r) => ({ ...r, action: ACT[r.action]?.ru ?? r.action })), 'audit.xlsx', lang)
    /* Выгрузка журнала отмечается в самом журнале: инструмент, который должен
       доказывать, не может быть единственным местом без следа. */
    noteExport('журнал аудита', [act, from, to, q].filter(Boolean).join(' '), all.length)
    if (all.length >= AUDIT_EXPORT_LIMIT) {
      // Молча обрезанная выгрузка выглядела бы полной, не будучи ею.
      toast({ tone: 'info', title: t(lang, 'Выгружено начало журнала', 'Exported the first rows'),
        text: t(lang, `Первые ${AUDIT_EXPORT_LIMIT} записей отбора. Сузьте фильтр или поиск.`,
                     `First ${AUDIT_EXPORT_LIMIT} rows of the selection. Narrow the filter or search.`) })
    }
  }
  const runExport = () => {
    exportLog().catch((e: unknown) => {
      toast({ tone: 'danger', title: t(lang, 'Выгрузка не удалась', 'Export failed'),
        text: e instanceof Error ? e.message : String(e) })
    })
  }

  const cols: SmartColumn<AuditEntry>[] = [
    { key: 'ts', header: colHeader(lang, 'Дата и время'), mono: true, nowrap: true, width: 138, render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.ts}</span> },
    { key: 'user', header: colHeader(lang, 'Пользователь'), width: 170, groupable: true, filterable: true, render: (r) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Avatar name={r.user} size={20} /><span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{r.user}</span></span> },
    // Незнакомое действие не должно ронять экран: сервер вправе завести новое
    // раньше, чем о нём узнает клиент.
    { key: 'action', header: colHeader(lang, 'Действие'), width: 150, groupable: true, filterable: true, valueLabel: (v) => ACT[String(v)]?.ru ?? String(v), render: (r) => { const a = ACT[r.action] ?? { ru: r.action, c: '--text-subtle', i: 'circle' }; return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: `var(${a.c})` }}><Icon name={a.i} size={14} /><span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{a.ru}</span></span> } },
    { key: 'obj', header: colHeader(lang, 'Объект'), mono: true, nowrap: true, width: 130, render: (r) => <span style={{ color: r.obj === '—' ? 'var(--text-subtle)' : 'var(--text-link)' }}>{r.obj}</span> },
    { key: 'target', header: colHeader(lang, 'Описание'), render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.target}</span> },
    { key: 'ip', header: colHeader(lang, 'Источник'), mono: true, align: 'right', width: 96, groupable: true, filterable: true, render: (r) => <span style={{ color: r.ip === 'external' ? 'var(--violet-600)' : 'var(--text-subtle)' }}>{r.ip}</span> },
    /* «Было → стало» собирается сервером с V51 и до сих пор не показывалось:
       строка «Изменены поля: plan, forecast» не говорит, каким срок был и каким
       стал, а на разборе спрашивают именно это. */
    { key: 'changes', header: colHeader(lang, 'Было → стало'), width: 260, render: (r) => r.changes
        ? <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: 260 }} title={r.changes}>{r.changes}</span>
        : <span style={{ color: 'var(--text-subtle)' }}>—</span> },
  ]
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={<span>{me.data?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Журнал аудита', 'Audit log')}</span>}
        title={t(lang, 'Журнал аудита', 'Audit log')}
        count={auditQuery.isSuccess ? auditQuery.data.total : undefined}
        actions={<Button variant="secondary" size="sm" iconLeft={<Icon name="download" size={14} />} onClick={runExport}>{t(lang, 'Выгрузить журнал', 'Export')}</Button>}
      />
      <Toolbar>
        <div style={{ width: 260 }}><Input size="sm" value={q} onChange={(e) => reset(() => setQ(e.target.value))} leading={<Icon name="search" size={14} />} placeholder={t(lang, 'Поиск по журналу…', 'Search log…')} /></div>
        <Select size="sm" value={act} onChange={(e) => reset(() => setAct(e.target.value))} block={false} style={{ width: 180 }}>
          <option value="">{t(lang, 'Все действия', 'All actions')}</option>
          {Object.entries(ACT).map(([k, v]) => <option key={k} value={k}>{v.ru}</option>)}
        </Select>
        {/* Период разбора: журнал спрашивают за месяц или за конкретный день,
            и до сих пор его листали целиком. */}
        <div style={{ width: 130 }}><DateInput value={from} onChange={(v) => reset(() => setFrom(v))} placeholder={t(lang, 'с', 'from')} /></div>
        <div style={{ width: 130 }}><DateInput value={to} onChange={(v) => reset(() => setTo(v))} placeholder={t(lang, 'по', 'to')} /></div>
        <ToolSpacer />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}><Icon name="lock" size={13} />{t(lang, 'Неизменяемый аудит-трейл', 'Immutable trail')}</span>
      </Toolbar>
      {auditQuery.isError
        ? <ScreenError lang={lang} error={auditQuery.error} />
        : <div style={{ flex: 1, minHeight: 0 }}><SmartTable name="audit" columns={cols} rows={rows} rowKey={(r) => r.id} lang={lang} /></div>}
      <Pager page={auditQuery.data?.page ?? 0} pageCount={auditQuery.data?.pageCount ?? 1}
             total={auditQuery.data?.total ?? 0} lang={lang} onPage={setPage} />
    </div>
  )
}

// ===== Admin / RBAC =====================================================
/* Значения отбора учётных записей, считаемые сервером по всему проекту.
   Ключ стороны — external: это имя колонки, а не заголовка столбца. */
/* Дисциплин здесь нет намеренно: это массив, и отбор сравнил бы его целиком —
   «{PI,EL}» как одно значение. Отбор по одной дисциплине потребовал бы своего
   условия на сервере. */
const USER_FACETS = ['role', 'external', 'status'] as const

/* Столбец «Сторона» показывает бейдж, а отбирается по колонке external:
   имя столбца и имя поля расходятся, и отбор надо переложить на второе. */
const USER_FILTER_FIELD: Record<string, string> = { side: 'external' }

const userStatusLabel = (lang: Lang, status: string): string =>
  status === 'active' ? t(lang, 'Активен', 'Active')
    : status === 'blocked' ? t(lang, 'Доступ отозван', 'Blocked')
      : t(lang, 'Приглашён', 'Invited')

export function Admin({ lang = 'ru', focus, onOpenDoc }: { lang?: Lang
                                                          /** Вкладка из адреса: /admin/subs открывает «Замещения». */
                                                          focus?: string
                                                          onOpenDoc?: (no: string) => void }) {
  const me = useMe()
  /* Отбор, счёт и страница — на сервере. Прежде список брал первые двести
     записей и рисовался без постраничности: на проекте с подрядчиками их
     легко триста-восемьсот, и запись № 201 нельзя было ни найти, ни
     заблокировать — притом что «Пользователи» это единственный экран отзыва
     доступа. Счётчик вкладки при этом показывал настоящий total, то есть
     расхождение было видно, а обойти его нечем. */
  const [userPage, setUserPage] = useState(0)
  const [userQ, setUserQ] = useState('')
  /* Отбор считает СЕРВЕР. Галочки, отбирающие загруженную страницу, показали
     бы «двух подрядчиков» там, где их сорок: у проекта учётных записей
     легко несколько сотен, а на странице пятьдесят. */
  const [userFilters, setUserFilters] = useState<Record<string, string[]>>({})
  const usersQuery = useList<User>('users',
    { page: userPage, pageSize: 50, sort: [{ id: 'id', desc: false }], q: userQ.trim() || undefined,
      filters: userFilters })
  /* Значения отбора и числа рядом с ними — по всему набору, а не по странице:
     тем же механизмом фасетов, что у реестров. */
  const userFacets = useFacets('users', USER_FACETS)
  const userCounts = (key: string): Record<string, number> | undefined => {
    const buckets = userFacets.data?.[key]
    return buckets ? Object.fromEntries(buckets.map((b) => [String(b.value), b.count])) : undefined
  }
  /* Смена отбора возвращает на первую страницу: иначе отбор применяется, а
     человек остаётся на пятой странице прежнего набора и видит пустоту. */
  const onUserFilter = (key: string, values: string[] | null) => {
    const field = USER_FILTER_FIELD[key] ?? key
    setUserFilters((prev) => {
      const next = { ...prev }
      if (values && values.length) next[field] = values
      else delete next[field]
      return next
    })
    setUserPage(0)
  }
  /* Обратное соответствие: таблица помечает отмеченное по ИМЕНИ СТОЛБЦА, а
     хранится отбор именами полей сервера. Без разворота галочка «Подрядчик»
     срабатывала бы и тут же гасла. */
  const userColFilters = useMemo(() => {
    const back = Object.fromEntries(Object.entries(USER_FILTER_FIELD).map(([col, field]) => [field, col]))
    return Object.fromEntries(Object.entries(userFilters).map(([field, values]) => [back[field] ?? field, values]))
  }, [userFilters])
  const users = usersQuery.data?.rows ?? []
  const rolesQuery = useMeta<Role[]>('roles')
  const roles = rolesQuery.data ?? []
  /* Учётные записи ведёт администратор — тем же правилом, каким сервер отбивает
     команду. Прятать по своему условию значит показать кнопку, отвечающую 403.

     Справочники и настройки проекта — отдельный раздел прав (V64): их ведёт
     координатор ДО, у которого учётных записей нет и быть не должно. */
  const canAdmin = me.data?.perms?.admin === 'rw'
  const seesPeople = (me.data?.perms?.admin ?? '—') !== '—'
  const canSettings = me.data?.perms?.dicts === 'rw'
  const [userDialog, setUserDialog] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  /* Вкладка по умолчанию — та, что роль вообще даёт: у координатора ДО
     учётных записей нет, и «Пользователи» ответили бы ему 403. */
  /* Вкладка из адреса: «/admin/subs» открывает «Замещения». Приказ за себя
     заводит любой участник, а пункт меню «Администрирование» показан только
     тем, у кого есть право на справочники, — без адресной ссылки рецензент до
     своей же вкладки не добрался бы. */
  const [tab, setTab] = useState(() => focus || ((me.data?.perms?.admin ?? '—') !== '—' ? 'users' : 'dicts'))
  useEffect(() => { if (focus) setTab(focus) }, [focus])
  const [roleDialog, setRoleDialog] = useState(false)
  /* Уровень права. Сбрасывается и «/me»: администратор правит и свою роль, и
     навигация считается по правам вошедшего — иначе пункт остался бы на экране,
     отвечая 403. */
  const setLevel = useCommand<{ role: string; area: string; level: string }>({
    method: 'PUT',
    path: (a) => `/roles/${encodeURIComponent(a.role)}/permissions/${a.area}`,
    body: (a) => ({ level: a.level }),
    invalidate: () => [qk.meta('roles'), qk.sub('/me'), ...inboxKeys, qk.all('audit')],
  })
  /* Разделы прав — те же, что на сервере. Администрирование разделено (V64):
     справочники ведут одни, роли раздают другие, журнал читают третьи. */
  const MOD: [string, string][] = [['mdr', 'MDR'], ['vdr', 'VDR'], ['trm', 'TRM'], ['review', t(lang, 'Рассм.', 'Review')], ['crs', 'CRS'], ['reports', t(lang, 'Отчёты', 'Reports')], ['dicts', t(lang, 'Справ.', 'Dicts')], ['audit', t(lang, 'Журнал', 'Audit')], ['admin', t(lang, 'Роли', 'People')]]
  const permChip = (p: string) => {
    if (p === 'rw') return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, color: 'var(--green-600)', background: 'var(--green-50)', border: '1px solid var(--green-100)', borderRadius: 2, padding: '1px 5px' }}>RW</span>
    if (p === 'r') return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, color: 'var(--blue-600)', background: 'var(--blue-50)', border: '1px solid var(--blue-100)', borderRadius: 2, padding: '1px 6px' }}>R</span>
    return <span style={{ color: 'var(--text-subtle)' }}>—</span>
  }
  const userCols: SmartColumn<User>[] = [
    { key: 'name', header: colHeader(lang, 'Пользователь'), required: true, render: (r) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Avatar name={r.name} size={24} /><span><span style={{ display: 'block', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)', fontWeight: 500 }}>{r.name}</span><span style={{ display: 'block', font: 'var(--font-caption)', color: 'var(--text-subtle)', fontFamily: 'var(--font-mono)' }}>{r.email}</span></span></span> },
    { key: 'role', header: colHeader(lang, 'Роль'), width: 200, groupable: true, filterable: true, filterCounts: userCounts('role'), render: (r) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{r.external ? <CIcon name="external-link" size={13} color="var(--violet-600)" /> : null}<span style={{ color: 'var(--text-body)' }}>{r.role}</span></span> },
    /* Сторона участника. Заказчик — внутренний пользователь (external=false),
       подрядчик — внешний: то же правило, каким сервер отбирает исполнителей
       маршрута и ограничивает видимость. Иконка у роли остаётся — бейдж
       называет сторону словом, а не намёком. */
    /* У подрядчика рядом со стороной — его организация: ею и ограничена
       видимость, и без неё бейдж «Подрядчик» не говорит, чей он. */
    /* Отбор и группировка идут по САМОЙ стороне, а не по нарисованному бейджу:
       сервер хранит её булевым external, и в отбор уходят те же 'true'/'false',
       которыми он сравнивает. */
    { key: 'side', header: colHeader(lang, 'Сторона'), width: 200, groupable: true, filterable: true,
      value: (r) => (r.external ? 'true' : 'false'), filterCounts: userCounts('external'),
      valueLabel: (v) => (String(v) === 'true' ? t(lang, 'Подрядчик', 'Contractor') : t(lang, 'Заказчик', 'Customer')),
      render: (r) => r.external
      ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <Badge tone="accent" size="sm">{t(lang, 'Подрядчик', 'Contractor')}</Badge>
          <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: r.vendor ? 'var(--text-muted)' : 'var(--danger-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {r.vendor ?? t(lang, 'без организации', 'no organisation')}
          </span>
        </span>
      )
      : <Badge tone="info" size="sm">{t(lang, 'Заказчик', 'Customer')}</Badge> },
    { key: 'disc', header: colHeader(lang, 'Дисциплины'), width: 180, render: (r) => <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>{r.disc.map((d: string) => <span key={d} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 2, padding: '0 4px' }}>{d}</span>)}</span> },
    { key: 'status', header: colHeader(lang, 'Статус'), width: 130, groupable: true, filterable: true,
      filterCounts: userCounts('status'), valueLabel: (v) => userStatusLabel(lang, String(v)),
      render: (r) => r.status === 'active'
        ? <Badge tone="success" size="sm" dot>{t(lang, 'Активен', 'Active')}</Badge>
        : r.status === 'blocked'
          ? <Badge tone="danger" size="sm" dot>{t(lang, 'Доступ отозван', 'Blocked')}</Badge>
          : <Badge tone="warning" size="sm" dot>{t(lang, 'Приглашён', 'Invited')}</Badge> },
    { key: 'last', header: colHeader(lang, 'Последний вход'), mono: true, align: 'right', width: 130, render: (r) => <span style={{ color: 'var(--text-subtle)' }}>{r.last}</span> },
  ]
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={<span>{me.data?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Администрирование', 'Admin')}</span>}
        title={t(lang, 'Администрирование системы', 'System administration')}
        /* Кнопки «Добавить запись» здесь больше нет: добавление живёт на самой
           вкладке справочников, рядом с выбранным справочником — только там
           известно, куда добавлять. */
        actions={canAdmin && tab === 'users' ? (
          <Button variant="primary" size="sm" iconLeft={<Icon name="user-plus" size={14} />}
                  onClick={() => { setEditingUser(null); setUserDialog(true) }}>
            {t(lang, 'Добавить пользователя', 'Add user')}
          </Button>
        ) : canAdmin && tab === 'roles' ? (
          <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />}
                  onClick={() => setRoleDialog(true)}>
            {t(lang, 'Добавить роль', 'Add role')}
          </Button>
        ) : null}
        tabsNode={
          <PtTabs
            value={tab}
            onChange={setTab}
            tabs={[
              ...(seesPeople ? [
                { id: 'users', label: t(lang, 'Пользователи', 'Users'), count: usersQuery.isSuccess ? usersQuery.data.total : undefined },
                { id: 'roles', label: t(lang, 'Роли и доступ', 'Roles'), count: roles.length },
              ] : []),
              /* Вкладка не под правом администрирования: приказ за себя заводит
                 любой участник проекта — право подписи передаётся по
                 собственному решению. Что видно и что разрешено, решает сервер;
                 рецензент увидит здесь только свои приказы. */
              { id: 'subs', label: t(lang, 'Замещения', 'Substitutions') },
              { id: 'matrix', label: t(lang, 'Матрица распределения', 'Distribution matrix') },
              /* Контракты — не справочник: у них есть подрядчик и сроки, и на
                 контракт заводится реестр MDR. Поэтому своя вкладка, а не
                 строка в «Справочниках». */
              ...(canSettings ? [{ id: 'contracts', label: t(lang, 'Контракты', 'Contracts') }] : []),
              { id: 'dicts', label: t(lang, 'Справочники', 'Dictionaries') },
              /* Колонки реестра заказчика, которых нет в схеме: без них файл
                 на сорок колонок терял двадцать при загрузке. */
              { id: 'attrs', label: t(lang, 'Атрибуты документа', 'Document attributes') },
              { id: 'coding', label: t(lang, 'Кодирование', 'Coding rules') },
              /* Свои извещения, а не чужие: вкладка видна всем, кто дошёл до
                 экрана, и правит только собственные настройки — сервер берёт
                 адресата из токена. */
              { id: 'notify', label: t(lang, 'Мои извещения', 'My notifications') },
              /* Подключение к почте и формат рассылки — решение проекта, а не
                 личное: вкладка видна там же, где прочие настройки уровня
                 проекта, и сервер требует права администрирования. */
              ...(canSettings ? [{ id: 'mail', label: t(lang, 'Почта', 'Mail') }] : []),
              /* Сроки ответа и рассмотрения были константами сборки: поменять
                 их мог только тот, у кого есть доступ к серверу. */
              ...(canSettings ? [{ id: 'project', label: t(lang, 'Проект', 'Project') }] : []),
            ]}
          />
        }
      />
      {userDialog ? (
        <UserDialog open onClose={() => setUserDialog(false)} lang={lang} user={editingUser} roles={roles} />
      ) : null}
      {roleDialog ? <RoleDialog open onClose={() => setRoleDialog(false)} lang={lang} /> : null}
      {tab === 'subs' ? <SubstitutionsBody lang={lang} canAdmin={canAdmin} /> : null}
      {tab === 'matrix' ? <MatrixTab lang={lang} onOpenDoc={onOpenDoc} /> : null}
      {tab === 'contracts' && canSettings ? <ContractsBody lang={lang} /> : null}
      {tab === 'dicts' ? <DictsBody lang={lang} /> : null}
      {tab === 'attrs' ? <DocAttrsBody lang={lang} /> : null}
      {tab === 'coding' ? <CodingBody lang={lang} /> : null}
      {tab === 'notify' ? <NotifyPrefsBody lang={lang} /> : null}
      {tab === 'mail' && canSettings ? <MailSettingsBody lang={lang} /> : null}
      {tab === 'project' && canSettings ? <ProjectSettingsBody lang={lang} /> : null}
      {tab === 'users' ? (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Toolbar style={{ padding: '10px 16px' }}>
            <div style={{ width: 320 }}>
              <Input size="sm" value={userQ} onChange={(e) => { setUserQ(e.target.value); setUserPage(0) }}
                     leading={<Icon name="search" size={14} />}
                     placeholder={t(lang, 'Имя, роль или почта…', 'Name, role or e-mail…')} />
            </div>
            <ToolSpacer />
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              {usersQuery.isSuccess
                ? t(lang, `${users.length} из ${usersQuery.data.total}`, `${users.length} of ${usersQuery.data.total}`)
                : ''}
            </span>
          </Toolbar>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {/* Отбор уходит на сервер, а группировка собирает страницу — как в
                реестрах: группу по всему набору считать нечем, зато вместе с
                отбором она честная (отобрал подрядчиков — их и группирует). */}
            <SmartTable name="users" columns={userCols} rows={users} rowKey={(r) => r.id} lang={lang}
                        loading={usersQuery.isLoading}
                        colFilters={userColFilters} onFilter={onUserFilter}
                        emptyText={t(lang, 'Нет учётных записей', 'No accounts')}
                        onRowClick={canAdmin ? (r) => { setEditingUser(r); setUserDialog(true) } : undefined} />
          </div>
          <Pager page={usersQuery.data?.page ?? 0} pageCount={usersQuery.data?.pageCount ?? 1}
                 total={usersQuery.data?.total ?? 0} lang={lang} onPage={setUserPage} />
        </div>
      ) : tab === 'roles' ? (
        <div style={{ padding: 24 }}>
          <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thS()}>{t(lang, 'Роль', 'Role')}</th>
                  <th style={{ ...thS(), textAlign: 'center', width: 64 }}>{t(lang, 'Польз.', 'Users')}</th>
                  {MOD.map(([k, l]) => <th key={k} style={{ ...thS(), textAlign: 'center', width: 78 }}>{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <tr key={r.id} className="lav-tr" style={{ height: 44 }}>
                    <td style={tdS()}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>{r.id === 'vendor' ? <CIcon name="external-link" size={13} color="var(--violet-600)" /> : <CIcon name="shield" size={13} color="var(--text-subtle)" />}<span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)', fontWeight: 500 }}>{r.name}</span></span></td>
                    <td style={{ ...tdS(), textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>{r.users}</td>
                    {MOD.map(([k]) => (
                      <td key={k} style={{ ...tdS(), textAlign: 'center' }}>
                        {canAdmin ? (
                          /* Уровень правится тут же: матрица на экране и есть та
                             матрица, по которой сервер решает доступ. Отказы
                             (последнее администрирование, подрядчик с админкой)
                             приходят с сервера — прятать их кнопкой нечестно. */
                          <Select size="sm" block={false} style={{ width: 74 }}
                                  disabled={setLevel.isPending}
                                  value={(r.perms[k as PermArea] ?? 'none') === '—' ? 'none' : (r.perms[k as PermArea] ?? 'none')}
                                  onChange={(e) => setLevel.mutate({ role: r.id, area: k, level: e.target.value })}>
                            <option value="none">—</option>
                            <option value="r">R</option>
                            <option value="rw">RW</option>
                          </Select>
                        ) : permChip(r.perms[k as PermArea])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Группы — рядом с ролями, но не роли: роль отвечает за права, группа
              за адресацию шага рассмотрения. */}
          <GroupsBlock lang={lang} canAdmin={canAdmin} />
          <div style={{ marginTop: 12, display: 'flex', gap: 16, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            <span><b style={{ color: 'var(--green-600)' }}>RW</b> — чтение и запись</span>
            <span><b style={{ color: 'var(--blue-600)' }}>R</b> — только чтение</span>
            <span>— нет доступа</span>
            {/* Прежняя подпись обещала фильтрацию доступа по дисциплинам, которой
                нет ни в правах, ни в выборках: администратор расставлял галочки
                и считал, что ограничил доступ. Решение заказчика 18.08.26 —
                участники проекта видят все его документы, и подпись говорит
                ровно это. */}
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="info" size={13} />{t(lang, 'Право даёт раздел; границы учётной записи сужают его до дисциплин и контрактов',
                     'A right grants a section; account boundaries narrow it to disciplines and contracts')}</span>
          </div>
        </div>
      ) : null}
    </div>
  )
}
function thS(): CSSProperties {
  return { textAlign: 'left', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-2xs)', fontWeight: 600, letterSpacing: 'var(--tracking-caps)', textTransform: 'uppercase', color: 'var(--text-subtle)', background: 'var(--surface-sunken)', padding: '9px 12px', borderBottom: '1px solid var(--border-strong)' }
}
function tdS(): CSSProperties {
  return { padding: '0 12px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' }
}

// ===== Учётные записи ===================================================
/**
 * Учётная запись участника проекта.
 *
 * Форма заводит участника, а не вход: сам вход живёт в Keycloak и создаётся
 * там. Поэтому обязательно поле «имя входа» — по нему запись связывается с
 * владельцем токена, и по нему же считаются права.
 *
 * Роль выбирается из справочника ролей и уходит на сервер **кодом**: реестр
 * показывает название, а команда принимает код, и разрешение одного в другое
 * делается здесь — связать прочитанное с записываемым иначе нечем.
 */
function UserDialog({ open, onClose, lang, user, roles, preset }: {
  open: boolean
  onClose: () => void
  lang: Lang
  user: User | null
  roles: Role[]
  /** Префилл с экрана организаций: сразу внешняя учётка этой организации. */
  preset?: { vendorName: string } | null
}) {
  const me = useMe()
  const discRows = useDictRows('disc')
  const contracts = useContracts()
  const vendorsQuery = useList<Vendor>('vendors', { page: 0, pageSize: 200, sort: [{ id: 'code', desc: false }] })
  /* Внешней учётке предлагаются только подрядчики: организация здесь — граница
     видимости, и поставщик оборудования ею быть не может. То же проверяет сервер. */
  const contractors = (vendorsQuery.data?.rows ?? []).filter((v) => v.isContractor)
  const codeByName = new Map(roles.map((r) => [r.name, r.id]))
  const [form, setForm] = useState({
    code: user?.id ?? '',
    name: user?.name ?? (preset ? '' : ''),
    subject: user?.subject ?? '',
    email: user?.email ?? '',
    /* Роль и сторона — одно и то же, сказанное дважды: у внешней учётки роль
       всегда vendor (форсируется и здесь, и на сервере). Дефолт — пустой выбор:
       прежний roles[0] после сортировки давал «Администратора» по умолчанию. */
    roleCode: user ? (codeByName.get(user.role) ?? '') : (preset ? 'vendor' : ''),
    disc: user?.disc ?? [],
    status: user?.status ?? 'invited',
    external: user?.external ?? preset != null,
    /* Организация — ИМЯ из справочника: тем же именем держится вся изоляция
       подрядчика (claim vendor ↔ document.vendor_code). */
    vendorName: user?.vendor ?? preset?.vendorName ?? '',
    /* Границы доступа (V49): что человеку ВИДНО. Пустые списки — весь проект. */
    scopeDisc: user?.scopeDisc ?? [],
    scopeContracts: user?.scopeContracts ?? [],
  })
  const [createLogin, setCreateLogin] = useState(user == null)
  const [creds, setCreds] = useState<{ login: string; password: string; reset?: boolean } | null>(null)
  const [manualHelp, setManualHelp] = useState(false)
  const save = useCommand<Record<string, unknown>,
    { code: string; login?: string; tempPassword?: string
      /* Что осталось на человеке при отзыве доступа: открытые шаги, реестры во
         владении, группы маршрута. Считает сервер — он же знает маршруты. */
      left?: { kind: string; ref: string; note: string }[] }>({
    method: user ? 'PATCH' : 'POST',
    path: () => (user ? `/users/${encodeURIComponent(user.id)}` : '/users'),
    body: (a) => a,
    invalidate: () => [qk.all('users'), qk.meta('roles'), qk.all('audit')],
  })
  /* Сброс пароля: команда без тела — сервер сам берёт имя входа из записи и
     возвращает новый временный пароль ровно один раз. */
  const reset = useCommand<Record<string, never>, { login: string; tempPassword: string }>({
    method: 'POST',
    path: () => `/users/${encodeURIComponent(user?.id ?? '')}/reset-password`,
    body: () => ({}),
    invalidate: () => [qk.all('audit')],
  })
  const resetPassword = () => reset.mutate({}, {
    onSuccess: (r) => setCreds({ login: r.login, password: r.tempPassword, reset: true }),
    onError: (e) => {
      // Учётка без входа в Keycloak — тупик без объяснения; даём инструкцию.
      if (e instanceof ApiError && (e.code === 'keycloak.disabled' || e.code === 'keycloak.login-missing')) {
        setManualHelp(true)
      }
    },
  })
  /* Учётка принадлежит организации: её завели из строки организации или она
     уже заведена подрядчику. Ни сторону, ни организацию менять нельзя — то же
     правило отбивает сервер (user.external-fixed, user.vendor-fixed). */
  const vendorLocked = preset != null || Boolean(user?.external)
  const setExternal = (external: boolean) => setForm((f) => ({
    ...f,
    external,
    roleCode: external ? 'vendor' : f.roleCode === 'vendor' ? '' : f.roleCode,
  }))
  const field = (key: 'code' | 'name' | 'subject' | 'email', label: string, disabled?: boolean, hint?: string) => (
    <div>
      <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{label}</label>
      <Input value={form[key]} disabled={disabled} placeholder={hint} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
    </div>
  )
  const toggleDisc = (code: string) => setForm((f) => ({
    ...f,
    disc: f.disc.includes(code) ? f.disc.filter((d) => d !== code) : [...f.disc, code],
  }))
  const toggleScope = (key: 'scopeDisc' | 'scopeContracts', code: string) => setForm((f) => ({
    ...f,
    [key]: f[key].includes(code) ? f[key].filter((d) => d !== code) : [...f[key], code],
  }))
  const ready = form.code.trim() !== '' && form.name.trim() !== '' && form.roleCode !== ''
    && (user != null || form.subject.trim() !== '')
    && (!form.external || form.vendorName !== '')
  const submit = () => {
    /* Непереданное поле сервер не трогает, поэтому пустое имя входа при правке
       означает «оставить прежнее», а не «стереть». */
    const body: Record<string, unknown> = {
      code: form.code, name: form.name, roleCode: form.roleCode, email: form.email,
      disc: form.disc, external: form.external,
      vendorCode: form.external ? form.vendorName : null,
      /* Границы уходят всегда: снятая последняя галочка означает «весь
         проект», и пропущенное поле сервер прочитал бы как «оставить». */
      scopeDisc: form.external ? [] : form.scopeDisc,
      scopeContracts: form.external ? [] : form.scopeContracts,
    }
    if (form.subject.trim()) body.subject = form.subject.trim()
    if (user) body.status = form.status
    if (!user && createLogin) body.createLogin = true
    save.mutate(body, {
      onSuccess: (r) => {
        if (r.tempPassword && r.login) {
          /* Пароль показывается один раз и нигде не хранится — диалог остаётся
             открытым, пока администратор его не заберёт. */
          setCreds({ login: r.login, password: r.tempPassword })
          return
        }
        /* Отзыв доступа срабатывает сразу, но молчать об остатках нельзя:
           уволился рецензент, доступ закрыли, а на нём висят открытые шаги —
           пакет стоит, и передать работу никто не догадается. */
        const left = r.left ?? []
        if (form.status === 'blocked' && left.length) {
          toast({ tone: 'info', title: t(lang, 'Доступ отозван, работа осталась', 'Access revoked, work remains'),
            text: t(lang, `${form.name}: назначений ${left.length} — ${left.slice(0, 3).map((x) => x.ref).join(', ')}. Передайте их другому исполнителю.`,
                          `${form.name}: ${left.length} assignment(s) — ${left.slice(0, 3).map((x) => x.ref).join(', ')}. Hand them over.`) })
        } else {
          toast({ tone: 'success', title: user ? 'Учётная запись изменена' : 'Учётная запись заведена', text: form.name })
        }
        onClose()
      },
      onError: (e) => {
        // Интеграция выключена — честная инструкция вместо тупика.
        if (e instanceof ApiError && e.code === 'keycloak.disabled') setManualHelp(true)
      },
    })
  }
  const copy = (value: string) => { void navigator.clipboard.writeText(value) }
  if (creds) {
    return (
      <Dialog open={open} onClose={onClose} sizeKey="user"
              title={creds.reset ? t(lang, 'Пароль сброшен', 'Password reset') : t(lang, 'Вход создан', 'Login created')}>
        <div style={{ width: 480, maxWidth: '100%', display: 'grid', gap: 12 }}>
          <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
            {creds.reset
              ? t(lang, 'Передайте пароль лично. Он показывается один раз и нигде не хранится: при следующем входе система потребует его сменить.',
                 'Hand the password over in person. It is shown once and stored nowhere: the next sign-in forces a change.')
              : t(lang, 'Временный пароль показывается один раз и нигде не хранится. При первом входе Keycloak потребует его сменить.',
                 'The temporary password is shown once and stored nowhere. Keycloak forces a change on first sign-in.')}
          </div>
          {[[t(lang, 'Вход', 'Login'), creds.login], [t(lang, 'Временный пароль', 'Temporary password'), creds.password]].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 160, font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{label}</span>
              <span style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-strong)' }}>{value}</span>
              <Button variant="secondary" size="sm" onClick={() => copy(value!)}>{t(lang, 'Копировать', 'Copy')}</Button>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <Button variant="primary" onClick={onClose}>{t(lang, 'Готово', 'Done')}</Button>
          </div>
        </div>
      </Dialog>
    )
  }
  return (
    <Dialog open={open} onClose={onClose} sizeKey="user" title={user ? t(lang, 'Изменить учётную запись', 'Edit account') : t(lang, 'Новая учётная запись', 'New account')}>
      <div style={{ width: 560, maxWidth: '100%', display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12 }}>
          {/* Код — ключ записи: на него ссылаются шаги маршрута рассмотрения. */}
          {field('code', t(lang, 'Код', 'Code'), user != null)}
          {field('name', t(lang, 'Имя', 'Name'))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {field('subject', t(lang, 'Имя входа', 'Sign-in name'), false,
                 user ? t(lang, 'оставить прежнее', 'keep current') : 'i.ivanov')}
          {field('email', t(lang, 'Почта', 'Email'))}
        </div>
        <div>
          <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{t(lang, 'Роль', 'Role')}</label>
          {form.external ? (
            <Select value="vendor" disabled>
              <option value="vendor">{roles.find((r) => r.id === 'vendor')?.name ?? t(lang, 'Подрядчик (внешний)', 'Contractor (external)')}</option>
            </Select>
          ) : (
            <Select value={form.roleCode} onChange={(e) => setForm((f) => ({ ...f, roleCode: e.target.value }))}>
              <option value="">{t(lang, '— роль —', '— role —')}</option>
              {roles.filter((r) => r.id !== 'vendor').map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </Select>
          )}
        </div>
        <div>
          <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{t(lang, 'Дисциплины — специализация', 'Disciplines — specialisation')}</label>
          {/* Это специализация для подсказок и фильтров, а не право: документы
              проекта участник видит все. Названо прямо, потому что галочка без
              подписи читается как ограничение доступа. */}
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginBottom: 6 }}>
            {t(lang, 'Подсказывает, кого предлагать исполнителем и как фильтровать списки. Доступ не ограничивает.',
                  'Suggests who to assign and how to filter lists. It does not restrict access.')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {discRows.map((d) => (
              <label key={d.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                <input type="checkbox" checked={form.disc.includes(d.code)} onChange={() => toggleDisc(d.code)} />
                {d.code}
              </label>
            ))}
          </div>
        </div>
        {/* Границы доступа — не специализация: за них человека не пускает
            выборка. Работали они с V49, а задать их было нечем: экран ролей
            уверял, что границ не существует. Пусто — без ограничения. */}
        {form.external ? null : (
          <div>
            <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>
              {t(lang, 'Границы доступа', 'Access boundaries')}
            </label>
            <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginBottom: 6 }}>
              {t(lang, 'Отмеченное — то, что человеку ВИДНО. Ничего не отмечено — виден весь проект.',
                    'What is ticked is what the person can SEE. Nothing ticked means the whole project.')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
              {discRows.map((d) => (
                <label key={d.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                  <input type="checkbox" checked={form.scopeDisc.includes(d.code)} onChange={() => toggleScope('scopeDisc', d.code)} />
                  {d.code}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {contracts.map((c) => (
                <label key={c.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}
                       title={c.title}>
                  <input type="checkbox" checked={form.scopeContracts.includes(c.code)} onChange={() => toggleScope('scopeContracts', c.code)} />
                  {c.code}
                </label>
              ))}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          {/* Сторона и организация заперты в двух случаях: диалог открыт из
              строки организации (её и заводят) и учётка подрядчика уже заведена.
              Изоляция держится на паре (внешний, организация), а токен человека
              несёт организацию из Keycloak: снять отметку значило бы выдать ему
              внутренние права, сменить организацию — чужие документы. Сервер
              отбивает то же самое, здесь — чтобы не подставлять под отказ. */}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)',
                          color: vendorLocked ? 'var(--text-subtle)' : 'var(--text-body)' }}
                 title={vendorLocked ? t(lang, 'Учётка принадлежит организации — сторону не меняем', 'The account belongs to an organisation — the side is fixed') : undefined}>
            <input type="checkbox" checked={form.external} disabled={vendorLocked}
                   onChange={(e) => setExternal(e.target.checked)} />
            {t(lang, 'Внешний участник (подрядчик)', 'External participant (contractor)')}
          </label>
          {/* Внешний участник видит документы своей организации — без неё запись
              не ограничена ничем, поэтому поле появляется вместе с признаком. */}
          {form.external ? (
            <Select value={form.vendorName} disabled={vendorLocked}
                    title={vendorLocked ? t(lang, 'Организация закреплена за учётной записью', 'The organisation is fixed for this account') : undefined}
                    onChange={(e) => setForm((f) => ({ ...f, vendorName: e.target.value }))}>
              <option value="">{user?.vendor ? t(lang, '— не менять —', '— keep current —') : t(lang, '— организация —', '— organisation —')}</option>
              {contractors.map((v) => <option key={v.code} value={v.name}>{v.name}</option>)}
            </Select>
          ) : null}
          {user ? (
            <Select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as User['status'] }))}>
              <option value="invited">{t(lang, 'Приглашён', 'Invited')}</option>
              <option value="active">{t(lang, 'Активен', 'Active')}</option>
              {/* Отзыв доступа: человек уволился или ушёл на другую площадку.
                  Учётная запись остаётся — по ней читается, кто что делал. */}
              <option value="blocked">{t(lang, 'Доступ отозван', 'Blocked')}</option>
            </Select>
          ) : null}
        </div>
        {/* Вход заводится и сотруднику, и подрядчику: разводит их не наличие
            входа, а атрибут vendor, который есть только у внешнего. */}
        {!user ? (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
            <input type="checkbox" checked={createLogin} onChange={(e) => setCreateLogin(e.target.checked)} />
            {t(lang, 'Создать вход в Keycloak (временный пароль покажется один раз)',
               'Create the Keycloak login (a one-time temporary password will be shown)')}
          </label>
        ) : (
          /* Пароль сбрасывается только у заведённой записи: имя входа берётся
             из неё, а самого пароля здесь нет и не было. */
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="secondary" size="sm" disabled={reset.isPending} onClick={resetPassword}>
              {reset.isPending ? t(lang, 'Сброс…', 'Resetting…') : t(lang, 'Сбросить пароль', 'Reset password')}
            </Button>
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              {t(lang, 'Выдаётся новый временный пароль: при следующем входе система потребует его сменить.',
                 'Issues a new temporary password: the next sign-in forces a change.')}
            </span>
          </div>
        )}
        {manualHelp ? (
          <div style={{ padding: '10px 12px', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(lang, 'Создание входов выключено — заведите вход в Keycloak руками:', 'Login provisioning is off — create the login in Keycloak manually:')}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, whiteSpace: 'pre-wrap' }}>
              {/* У сотрудника атрибута vendor нет: подсказать его значило бы
                  продиктовать администратору изоляцию подрядчика. */}
              {`username: ${form.subject.trim() || '<имя входа>'}\n`
               + (form.external ? `attributes.vendor: ${form.vendorName || '<точное имя организации>'}\n` : '')
               + `attributes.project: ${me.data?.project ?? '<проект>'}`}
            </div>
          </div>
        ) : (
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            {form.external
              ? t(lang,
                 'Изоляция подрядчика держится на точном имени организации: оно попадает в атрибут vendor входа.',
                 'Contractor isolation rests on the exact organisation name: it goes into the vendor attribute of the login.')
              : t(lang,
                 'Заводится участник проекта. Без входа человек не получит токен — заведите его здесь или в Keycloak.',
                 'This creates a project participant. Without a sign-in there is no token — create one here or in Keycloak.')}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={!ready || save.isPending} onClick={submit}
                  title={ready ? undefined : t(lang, 'Заполните код, имя, вход, роль и организацию', 'Fill in the code, name, sign-in, role and organisation')}>
            {save.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить', 'Save')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ===== Vendors ==========================================================
/**
 * Организация проекта. Одна и та же компания бывает и подрядчиком, и
 * поставщиком оборудования — роли отмечаются флажками, а не выбором одного
 * значения: выбор запретил бы совмещение.
 */
function VendorDialog({ open, onClose, lang, vendor }: { open: boolean; onClose: () => void; lang: Lang; vendor: Vendor | null }) {
  const [form, setForm] = useState({
    code: vendor?.code ?? '', name: vendor?.name ?? '', role: vendor?.role ?? '',
    contact: vendor?.contact ?? '', isContractor: vendor?.isContractor ?? true, isSupplier: vendor?.isSupplier ?? false,
    /* Отзыв доступа: организация остаётся в проекте со всей историей, но её
       люди больше не входят. Контракт кончился — доступ кончился вместе с ним,
       и делать это в Keycloak мимо системы значит терять след в журнале. */
    status: vendor?.status ?? 'active',
  })
  const save = useCommand<typeof form>({
    method: vendor ? 'PATCH' : 'POST',
    path: () => (vendor ? `/vendors/${encodeURIComponent(vendor.code)}` : '/vendors'),
    body: (a) => a,
    invalidate: () => [qk.all('vendors'), qk.all('audit')],
  })
  const field = (key: 'code' | 'name' | 'role' | 'contact', label: string, disabled?: boolean) => (
    <div>
      <label style={{ display: 'block', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 6 }}>{label}</label>
      <Input value={form[key]} disabled={disabled} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
    </div>
  )
  const ready = form.code.trim() !== '' && form.name.trim() !== '' && (form.isContractor || form.isSupplier)
  const check = (key: 'isContractor' | 'isSupplier', label: string) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
      <input type="checkbox" checked={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))} />
      {label}
    </label>
  )
  return (
    <Dialog open={open} onClose={onClose} title={vendor ? t(lang, 'Изменить организацию', 'Edit organisation') : t(lang, 'Новая организация', 'New organisation')}>
      <div style={{ width: 520, maxWidth: '100%', display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 }}>
          {/* Код — ключ записи: у существующей организации он не правится. */}
          {field('code', t(lang, 'Код', 'Code'), vendor != null)}
          {field('name', t(lang, 'Наименование', 'Name'))}
        </div>
        {field('role', t(lang, 'Роль (описание)', 'Role (free text)'))}
        {field('contact', t(lang, 'Контакт', 'Contact'))}
        <div style={{ display: 'flex', gap: 20, padding: '4px 0' }}>
          {check('isContractor', t(lang, 'Подрядчик (документация проекта, MDR)', 'Contractor (project documents, MDR)'))}
          {check('isSupplier', t(lang, 'Поставщик оборудования (заказ, VDR)', 'Equipment supplier (PO, VDR)'))}
        </div>
        {vendor ? (
          <Select label={t(lang, 'Доступ', 'Access')} value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as Vendor['status'] }))}
                  hint={t(lang, 'Заблокированная организация остаётся в проекте: её документы и переписка видны, входить её люди не могут',
                                'A blocked organisation stays in the project: its documents remain, its people cannot sign in')}>
            <option value="active">{t(lang, 'Действует', 'Active')}</option>
            <option value="blocked">{t(lang, 'Доступ отозван', 'Blocked')}</option>
          </Select>
        ) : null}
        {/* Сервер отбивает неполную форму тремя отказами по очереди — код,
            наименование, роль, — и без подсказки это три круга «нажал → узнал
            об одном поле». Условие здесь то же, что в requireVendorFields. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          {ready ? null : (
            <span style={{ marginRight: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              {t(lang, 'Нужны код, наименование и хотя бы одна роль', 'Code, name and at least one role are required')}
            </span>
          )}
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={save.isPending || !ready} onClick={() => save.mutate(form, {
            onSuccess: () => { toast({ tone: 'success', title: vendor ? 'Организация изменена' : 'Организация добавлена', text: form.name }); onClose() },
          })}>{save.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить', 'Save')}</Button>
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Показатель контрагента как ссылка на его срез.
 *
 * <p>Ноль ссылкой не делается: вести в заведомо пустой отбор незачем. Без
 * навигации (её может не быть у вызывающего) остаётся обычное число.
 */
function CountLink({ value, to, title, onNav, danger }: {
  value: number
  to: string
  title: string
  onNav?: (to: string) => void
  danger?: boolean
}) {
  const color = danger ? 'var(--danger-ink)' : 'var(--text-body)'
  if (!onNav || !value) return <span style={{ fontFamily: 'var(--font-mono)', color: value ? color : 'var(--text-subtle)' }}>{value}</span>
  return (
    <button type="button" title={title} aria-label={`${title}: ${value}`} className="tool-btn count-link"
            onClick={(e) => { e.stopPropagation(); onNav(to) }}
            style={{ fontFamily: 'var(--font-mono)', fontSize: 'inherit', color: danger ? 'var(--danger-ink)' : 'var(--text-link)', border: 0, padding: 0, background: 'transparent', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}>
      {value}
    </button>
  )
}

export function Vendors({ lang = 'ru', onNav }: { lang?: Lang; onNav?: (to: string) => void }) {
  const me = useMe()
  // Справочник контрагентов невелик и целиком помещается в страницу; показатели
  // (документы, открытые CRS, коды C/D, выпуск в срок) считает сервер — в seed
  // это были проставленные числа, не сходившиеся с фактом.
  const vendorsQuery = useList<Vendor>('vendors', { page: 0, pageSize: 200, sort: [{ id: 'code', desc: false }] })
  const rows = vendorsQuery.data?.rows ?? []
  /* Справочник организаций ведёт администратор — тем же правилом, каким сервер
     отбивает команду. Прятать по своему условию значит показать кнопку,
     отвечающую отказом. */
  const canEdit = me.data?.perms?.dicts === 'rw'
  const [dialog, setDialog] = useState(false)
  const [editing, setEditing] = useState<Vendor | null>(null)
  /* Учётка подрядчика заводится отсюда же: организация выбрана строкой, диалог
     открывается уже внешним и с нею — замечание «нет возможности создавать
     учётные записи для подрядчиков». */
  const [accountFor, setAccountFor] = useState<Vendor | null>(null)
  const rolesQuery = useMeta<Role[]>('roles')
  const cols: SmartColumn<Vendor>[] = [
    { key: 'name', header: colHeader(lang, 'Поставщик / подрядчик'), required: true, render: (r) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}><Avatar name={r.name} size={26} /><span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-md)', color: 'var(--text-strong)', fontWeight: 500 }}>{r.name}</span></span> },
    { key: 'role', header: colHeader(lang, 'Роль'), width: 220, groupable: true, filterable: true, render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.role}</span> },
    {
      key: 'isContractor', header: t(lang, 'Кто', 'As'), width: 150, render: (r) => (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {r.isContractor ? <Badge tone="info">{t(lang, 'Подрядчик', 'Contractor')}</Badge> : null}
          {r.isSupplier ? <Badge tone="neutral">{t(lang, 'Поставщик', 'Supplier')}</Badge> : null}
          {!r.isContractor && !r.isSupplier ? <span style={{ color: 'var(--text-subtle)' }}>—</span> : null}
        </span>
      ),
    },
    /* Показатели кликабельны. Прежде это были просто числа: перед разбором с
       подрядчиком ТДО открывал реестр и ставил отбор руками, потому что
       провалиться в «Документов 37» было нельзя. Отбор ставится готовым. */
    { key: 'docs', header: colHeader(lang, 'Документов'), mono: true, align: 'right', width: 100, render: (r) => (
      <CountLink value={r.docs} onNav={onNav} to={'mdrdocs?vendor=' + encodeURIComponent(r.name)}
                 title={t(lang, 'Документы подрядчика в реестрах MDR', 'The contractor’s documents in the MDR registers')} />
    ) },
    { key: 'open', header: colHeader(lang, 'Открытых CRS'), align: 'right', width: 120, render: (r) => (
      <CountLink value={r.open} onNav={onNav} to={'crs?vendor=' + encodeURIComponent(r.name)} danger={r.open > 8}
                 title={t(lang, 'Открытые замечания по документам подрядчика', 'Open comments on the contractor’s documents')} />
    ) },
    { key: 'codeC', header: colHeader(lang, 'Код C'), align: 'center', width: 70, render: (r) => r.codeC ? <CodeChip code="C" size="sm" /> : <span style={{ color: 'var(--text-subtle)' }}>0</span> },
    { key: 'otr', header: colHeader(lang, 'Выпуск в срок'), width: 160, render: (r) => <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ flex: 1, height: 6, background: 'var(--surface-sunken)', borderRadius: 3, overflow: 'hidden' }}><span style={{ display: 'block', width: r.otr + '%', height: '100%', background: r.otr > 85 ? 'var(--green-500)' : r.otr > 75 ? 'var(--amber-400)' : 'var(--red-500)' }} /></span><span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)', width: 34 }}>{r.otr}%</span></span> },
    { key: 'contact', header: colHeader(lang, 'Контакт'), mono: true, align: 'right', width: 180, render: (r) => <span style={{ color: 'var(--text-subtle)' }}>{r.contact}</span> },
    {
      key: 'account', header: '', width: 168, render: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
          {/* Переписка с контрагентом: у пакета нет колонки подрядчика, поэтому
              журнал открывается с его именем в строке поиска — отправитель и
              получатель по ней и ищутся. */}
          {onNav ? (
            <button type="button" className="icon-btn" aria-label={t(lang, 'Трансмитталы контрагента', 'Contractor transmittals')}
                    title={t(lang, 'Трансмитталы контрагента', 'Contractor transmittals')}
                    onClick={(e) => { e.stopPropagation(); onNav('trm?vendor=' + encodeURIComponent(r.name)) }}
                    style={{ display: 'grid', placeItems: 'center', width: 26, height: 26, border: 0, background: 'transparent', color: 'var(--text-subtle)', cursor: 'pointer' }}>
              <Icon name="send" size={14} />
            </button>
          ) : null}
          {/* Правка — отдельной кнопкой. Раньше форма организации открывалась
              обычным кликом по строке данных: оператор, целившийся в цифру,
              попадал в редактирование справочника. */}
          {canEdit ? (
            <button type="button" className="icon-btn" aria-label={t(lang, 'Изменить организацию', 'Edit organisation')}
                    title={t(lang, 'Изменить организацию', 'Edit organisation')}
                    onClick={(e) => { e.stopPropagation(); setEditing(r); setDialog(true) }}
                    style={{ display: 'grid', placeItems: 'center', width: 26, height: 26, border: 0, background: 'transparent', color: 'var(--text-subtle)', cursor: 'pointer' }}>
              <Icon name="pencil" size={14} />
            </button>
          ) : null}
          {canEdit && r.isContractor ? (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="user-plus" size={13} />}
                    onClick={(e) => { e.stopPropagation(); setAccountFor(r) }}>{t(lang, 'Учётка', 'Account')}</Button>
          ) : null}
        </span>
      ),
    },
  ]
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      {/* Утолщение подчёркивания, а не перекраска: у danger-варианта свой
          семантический красный в обеих темах, общий hover-цвет его ломал бы. */}
      <style>{`@media (hover: hover) and (pointer: fine) { .count-link:hover { text-decoration-thickness: 2px; } }`}</style>
      <PageHead
        crumbs={<span>{me.data?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Поставщики', 'Vendors')}</span>}
        title={t(lang, 'Поставщики и подрядчики', 'Vendors & contractors')}
        count={vendorsQuery.isSuccess ? vendorsQuery.data.total : undefined}
        actions={<>
          {canEdit ? (
            <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />}
                    onClick={() => { setEditing(null); setDialog(true) }}>{t(lang, 'Добавить', 'Add')}</Button>
          ) : null}
          {/* Выгружается страница, которая на экране (200 организаций):
              подкачивать остальное ради файла значит показать в нём то, чего
              человек не видел. */}
          <ReportExport<Vendor>
            rows={rows} total={vendorsQuery.data?.total} lang={lang}
            filename="vendors.xlsx" sheet={t(lang, 'Организации', 'Vendors')}
            columns={[
              ['Код', (v) => v.code],
              ['Наименование', (v) => v.name],
              ['Роль', (v) => v.role],
              ['Подрядчик', (v) => (v.isContractor ? t(lang, 'да', 'yes') : '')],
              ['Поставщик', (v) => (v.isSupplier ? t(lang, 'да', 'yes') : '')],
              ['Документов', (v) => v.docs],
              ['Открытых замечаний', (v) => v.open],
              ['Код C', (v) => v.codeC],
              ['OTR, %', (v) => v.otr],
              ['Контакт', (v) => v.contact],
              ['Доступ', (v) => (v.status === 'blocked' ? t(lang, 'отозван', 'blocked') : t(lang, 'действует', 'active'))],
            ]} />
        </>}
      />
      {vendorsQuery.isError
        ? <ScreenError lang={lang} />
        : <div style={{ flex: 1, minHeight: 0 }}>
            {/* Клика по строке нет: у строки нет одного очевидного назначения —
                документы, переписка, замечания и правка ведут в разные места,
                и каждое из них названо своей кнопкой. */}
            <SmartTable name="vendors" columns={cols} rows={rows} rowKey={(r) => r.code} lang={lang} />
          </div>}
      {dialog ? <VendorDialog open onClose={() => setDialog(false)} lang={lang} vendor={editing} /> : null}
      {accountFor ? (
        <UserDialog open onClose={() => setAccountFor(null)} lang={lang} user={null}
                    roles={rolesQuery.data ?? []} preset={{ vendorName: accountFor.name }} />
      ) : null}
    </div>
  )
}

// ===== Search ===========================================================
export function Search({ lang = 'ru', onOpenDoc, onOpenViewer }: {
  lang?: Lang
  onOpenDoc?: (no: string) => void
  /** Находка внутри файла ведёт на тот лист, где она нашлась. */
  onOpenViewer?: (no: string, at?: { page?: number; rev?: string; ver?: number }) => void
}) {
  const me = useMe()
  // Значения фасета «дисциплина» — из справочника сервера: выбранный код уходит
  // в фильтр запроса, и расхождение со справочником молча дало бы пустую выдачу.
  const discLabels = useDictLabels('disc')
  const lifeLabels = useDictLabels('life')
  const [q, setQ] = useState('ТП-4')
  const [facet, setFacet] = useState<{ disc: string; life: string; code: string }>({ disc: '', life: '', code: '' })
  const [page, setPage] = useState(0)

  // Поиск и фасеты выполняются на сервере: реестр целиком в браузер не грузится.
  const docsQuery = useList<Doc>('docs', {
    page,
    pageSize: SEARCH_PAGE_SIZE,
    sort: [{ id: 'no', desc: false }],
    filters: {
      ...(facet.disc ? { disc: [facet.disc] } : {}),
      ...(facet.life ? { life: [facet.life] } : {}),
      ...(facet.code ? { code: [facet.code] } : {}),
    },
    q: q || undefined,
  })
  const res = docsQuery.data?.rows ?? []

  /* Счётчики фасета сервер считает по всему реестру и без учёта запроса
     (RegisterRepository.facets). Пока запроса нет, это ровно те числа, которые
     и нужны. Как только он появился, они начинают противоречить выдаче: рядом
     с «Найдено: 1» стояло «EL 40», а нажатие на EL давало «Найдено: 0». Числа
     тогда не показываются — обещать их нечем, — а сам список значений остаётся:
     значение, которого нет во всём реестре, не найдётся ни по какому запросу. */
  const facetsQuery = useFacets('docs', FACET_COLUMNS)
  const countOf = (column: string, value: string) =>
    facetsQuery.data?.[column]?.find((b) => String(b.value) === value)?.count ?? 0
  const showCounts = q.trim() === ''

  const pick = (patch: Partial<typeof facet>) => { setFacet({ ...facet, ...patch }); setPage(0) }

  const FacetGroup = ({ title, items, val, onPick }: { title: ReactNode; items: [string, ReactNode, number][]; val: string; onPick: (v: string) => void }) => (
    <div style={{ marginBottom: 18 }}>
      <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FacetRow label={t(lang, 'Все', 'All')} on={!val} onClick={() => onPick('')} />
        {items.map(([k, l, n]) => <FacetRow key={k} label={l} count={showCounts ? n : undefined} on={val === k} onClick={() => onPick(k)} />)}
      </div>
    </div>
  )
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead crumbs={<span>{me.data?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Поиск', 'Search')}</span>} title={t(lang, 'Расширенный поиск', 'Advanced search')} />
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-card)', display: 'grid', gap: 12 }}>
        <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0) }} size="lg" leading={<Icon name="search" size={16} />} placeholder={t(lang, 'Поиск по № документа, наименованию, TRM, замечанию…', 'Search…')} />
        {/* Контекстный поиск идёт словами и по всему проекту сразу; ниже —
            прежний список документов с фасетами, где ищут обрывок номера.
            Это два разных вопроса, и оба нужны.

            Высота ограничена: пять групп по восемь строк выталкивали таблицу
            реестра за нижний край экрана, и человек видел только половину
            ответа, не зная, что есть вторая. */}
        <div style={{ maxHeight: '38vh', overflow: 'auto' }}>
          <ContextResults q={q} lang={lang} onOpenDoc={onOpenDoc} onOpenViewer={onOpenViewer} />
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ width: railWidth(240), flex: '0 0 auto', padding: 20, borderRight: '1px solid var(--border-default)', background: 'var(--surface-card)', overflow: 'auto' }}>
          {/* Пока идёт запрос, числа рядом со значениями скрыты — и об этом
              сказано, иначе их исчезновение читается как сбой. */}
          {!showCounts ? (
            <div style={{ marginBottom: 14, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              {t(lang, 'Счётчики считаются по всему реестру и запрос не учитывают, поэтому при запросе не показываются.',
                       'Counts cover the whole register and ignore the query, so they are hidden while a query is typed.')}
            </div>
          ) : null}
          <FacetGroup title={t(lang, 'Дисциплина', 'Discipline')} val={facet.disc} onPick={(v) => pick({ disc: v })} items={Object.keys(discLabels).map((k) => [k, k + ' — ' + discLabels[k], countOf('disc', k)] as [string, ReactNode, number]).filter((x) => x[2])} />
          <FacetGroup title={t(lang, 'Статус', 'Status')} val={facet.life} onPick={(v) => pick({ life: v })} items={Object.entries(lifeLabels).map(([k, v]) => [k, v, countOf('life', k)] as [string, ReactNode, number]).filter((x) => x[2])} />
          <FacetGroup title={t(lang, 'Код', 'Code')} val={facet.code} onPick={(v) => pick({ code: v })} items={['A', 'B', 'C', 'D'].map((c) => [c, 'Код ' + c, countOf('code', c)] as [string, ReactNode, number]).filter((x) => x[2])} />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 16px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-subtle)' }}>{t(lang, 'Найдено', 'Found')}: <b style={{ color: 'var(--text-strong)' }}>{docsQuery.isSuccess ? docsQuery.data.total : '—'}</b></div>
          {docsQuery.isError
            ? <ScreenError lang={lang} />
            : <div style={{ flex: 1, overflow: 'auto' }}><DocsTable rows={res} lang={lang} onOpenDoc={onOpenDoc ?? (() => {})} /></div>}
          <Pager page={docsQuery.data?.page ?? 0} pageCount={docsQuery.data?.pageCount ?? 1}
                 total={docsQuery.data?.total ?? 0} lang={lang} onPage={setPage} />
        </div>
      </div>
    </div>
  )
}
interface ContextHit {
  ref: string
  title: string | null
  meta: string | null
  state: string | null
  snippet: string | null
  /** Только у находок внутри файлов: где именно нашлось. */
  rev?: string | null
  ver?: number | null
  /** Текст получен распознаванием: надёжность другая, и это видно. */
  ocr?: boolean
}
/* `total` — сколько НАЙДЕНО, `found` в группе — то же по источнику: показанных
   строк всегда не больше, и путать эти числа значит уверять, что находок
   ровно столько, сколько поместилось. */
interface ContextSearchResult {
  query: string
  total: number
  shown?: number
  groups: { id: string; title: string; found?: number; rows: ContextHit[] }[]
}

const CONTEXT_GROUP_ICON: Record<string, string> = {
  docs: 'file-text', trm: 'send', crs: 'message-square-warning', registers: 'layers',
  files: 'file-search',
}

/**
 * Контекстный поиск: словами, по всему проекту, с фрагментом текста.
 *
 * <p>Отличается от поиска в реестре не мощностью, а вопросом. В реестре человек
 * знает, где ищет, и печатает обрывок номера. Здесь он знает только, что ищет,
 * и не знает, документ это, замечание или трансмиттал.
 *
 * <p>Фрагмент показывается с подсветкой совпадения — иначе список замечаний
 * это список кодов, по которому нельзя понять, то ли нашлось.
 */
function ContextResults({ q, lang, onOpenDoc, onOpenViewer }: {
  q: string
  lang: Lang
  onOpenDoc?: (no: string) => void
  onOpenViewer?: (no: string, at?: { page?: number; rev?: string; ver?: number }) => void
}) {
  const query = q.trim()
  /* Запрос короче двух знаков сервер отбивает — не спрашиваем вовсе, чтобы не
     мигать ошибкой на каждой первой букве. */
  /* Сколько строк просить у источника. Восемь на четыреста находок — не
     выдача, а намёк, поэтому «показать ещё» просит больше у сервера, а не
     дорисовывает из воздуха. */
  const [limit, setLimit] = useState(8)
  const result = useSub<ContextSearchResult>(
    query.length >= 2 ? `/search?q=${encodeURIComponent(query)}&limit=${limit}` : null)
  if (query.length < 2) return null
  if (result.isError) {
    return <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--danger-ink)' }}>
      {t(lang, 'Контекстный поиск недоступен', 'Contextual search is unavailable')}
    </div>
  }
  const groups = (result.data?.groups ?? []).filter((g) => g.rows.length > 0)
  if (result.isPending) {
    return <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{t(lang, 'Ищем…', 'Searching…')}</div>
  }
  if (!groups.length) {
    return <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
      {/* «Ничего не найдено» рядом с «Найдено: 1» ниже читалось как сбой:
          обрывок номера словами не ищется, его находит список реестра под
          этой строкой. Подсказка называет, где смотреть. */}
      {t(lang, 'Словами ничего не найдено — обрывок номера ищет список реестра ниже. Поиск по словам понимает падежи и не зависит от порядка слов; точная фраза — в кавычках, исключение — со знаком минус.',
            'Nothing found by words — a fragment of a number is matched by the register list below. Word search understands word forms and ignores order; quote an exact phrase, prefix a minus to exclude.')}
    </div>
  }
  /* Найдено — это сколько нашлось, а не сколько поместилось: «найдено 8» на
     сотни находок читается как «в проекте их восемь». */
  const total = result.data?.total ?? groups.reduce((n, g) => n + g.rows.length, 0)
  const shown = groups.reduce((n, g) => n + g.rows.length, 0)
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* Подпись обязательна: ниже на том же экране стоит список документов по
          фасетам, и без неё две выдачи читаются как одна сбившаяся. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>
          {t(lang, 'По словам, во всём проекте', 'By words, across the project')}
        </span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>
          {shown < total ? `${shown} / ${total}` : total}
        </span>
        {shown < total ? (
          <Button variant="ghost" size="sm" onClick={() => setLimit(limit >= 40 ? 100 : 40)}
                  title={t(lang, 'Спросить у сервера больше строк по каждому источнику',
                                'Ask the server for more rows per source')}>
            {t(lang, 'Показать ещё', 'Show more')}
          </Button>
        ) : null}
      </div>
      {groups.map((g) => (
        <div key={g.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
            <span style={{ color: 'var(--text-subtle)', display: 'inline-flex' }}><Icon name={CONTEXT_GROUP_ICON[g.id] ?? 'search'} size={13} /></span>
            <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{g.title}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)' }}>
              {g.found != null && g.found > g.rows.length ? `${g.rows.length} / ${g.found}` : g.rows.length}
            </span>
          </div>
          <div style={{ display: 'grid', gap: 4 }}>
            {g.rows.map((row) => {
              /* Находка внутри файла ведёт на лист, где она нашлась: номер
                 листа сервер отдаёт полем state. Открывать карточку значило бы
                 заставить человека искать это место второй раз руками. */
              const inFile = g.id === 'files' && onOpenViewer
              const openable = inFile || (g.id === 'docs' && onOpenDoc)
              return (
                <button key={g.id + row.ref + (row.state ?? '')} disabled={!openable}
                        onClick={() => {
                          if (inFile) onOpenViewer(row.ref, {
                            page: Number(row.state) || undefined,
                            rev: row.rev ?? undefined,
                            ver: row.ver ?? undefined,
                          })
                          else if (g.id === 'docs' && onOpenDoc) onOpenDoc(row.ref)
                        }}
                        className={openable ? 'tdms-rail' : undefined}
                        style={{ display: 'flex', alignItems: 'baseline', gap: 10, width: '100%', textAlign: 'left', padding: '7px 9px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-page)', cursor: openable ? 'pointer' : 'default' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)', flex: '0 0 auto' }}>{row.ref}</span>
                  <span style={{ flex: 1, minWidth: 0, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.snippet || row.title}
                  </span>
                  {row.ocr ? (
                    <span title={t(lang, 'Текст получен распознаванием скана: возможны ошибки', 'Text comes from OCR: errors are possible')}
                          style={{ flex: '0 0 auto', font: 'var(--font-caption)', color: 'var(--text-subtle)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '0 5px' }}>
                      {t(lang, 'распознано', 'OCR')}
                    </span>
                  ) : null}
                  {row.meta ? <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', flex: '0 0 auto' }}>{row.meta}</span> : null}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function FacetRow({ label, count, on, onClick }: { label: ReactNode; count?: number; on?: boolean; onClick?: () => void }) {
  return (
    <button onClick={onClick} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 8px', border: 0, borderRadius: 'var(--radius-sm)', background: on ? 'var(--accent-soft)' : 'transparent', color: on ? 'var(--accent-ink)' : 'var(--text-muted)', cursor: 'pointer', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: on ? 600 : 400, textAlign: 'left' }}>
      <span style={{ flex: 1 }}>{label}</span>
      {count != null ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)' }}>{count}</span> : null}
    </button>
  )
}

// ===== Distribution-matrix configuration ================================
const MACT: Record<string, string> = { check: 'scan-search', review: 'eye', translate: 'type', approve: 'check-check', issue: 'send' }
/** Адрес ветки матрицы: дисциплина плюс стадия/контракт/объект; пусто — «любой». */
interface MatrixBranchRef { disc: string; stage: string; contract: string; area: string }
/** Строка редактируемой матрицы рассмотрения: правило плюс поля формы. */
interface MatrixStepDraft {
  role: string
  /** Код учётной записи (app_user.code) — команда принимает его, а не имя. */
  assignee: string
  act: MatrixActId
  /** Срок первого круга рассмотрения, дней. */
  sla: number
  /** Сроки второго и третьего+ кругов (V96). Пусто — наследует предыдущий. */
  sla2: number | ''
  sla3: number | ''
  parallel: boolean
}


function MatrixConfig({ lang, initial, onBack }: { lang: Lang; initial?: MatrixBranchRef; onBack?: () => void }) {
  const rulesQuery = useMeta<Record<string, MatrixRule[]>>('matrix-rules')
  const rules = rulesQuery.data
  const actions = useMeta<{ id: MatrixActId; ru: string; icon: string }[]>('matrix-actions').data ?? []
  /* Исполнителем шага может быть только сотрудник: подрядчик рассмотрение не
     ведёт. Берутся они из справочника исполнителей, а не из реестра users:
     реестр закрыт правом администрирования и координатору, которому правка
     маршрута разрешена, отдавал 403 — все списки «Исполнитель» были пусты, а
     сохранённые назначения показывались как «— не назначен». */
  const reviewers = useMeta<{ code: string; name: string; kind: 'user' | 'group' }[]>('reviewers').data
  const people = useMemo(
    () => (reviewers ?? []).filter((r) => r.kind === 'user').map((r) => ({ id: r.code, name: r.name })),
    [reviewers])
  /* Сервер отдаёт в правиле отображаемое имя (снимок на момент назначения), а
     команда принимает код учётной записи. Разрешение имени в код — здесь, по
     списку сотрудников: другого способа связать прочитанное с записываемым нет. */
  const codeByName = useMemo(() => new Map(people.map((u) => [u.name, u.id])), [people])
  /* Шаг поручают человеку ИЛИ группе — исполнитель лежит в одном поле, и
     список выбора один, разделённый на два раздела. */
  const groups = useMeta<UserGroup[]>('groups').data ?? []
  const discRows = useDictRows('disc')
  /* Стадии проектирования и контракты — те же справочники проекта, что и
     всюду: ветка маршрута задаётся их кодами, а не свободным текстом. */
  const stageRows = useDictRows('stage')
  const contracts = useContracts()
  /* Объект (площадка): у одного своя приёмная комиссия, у другого своя, и
     «электрику ГПП-5» рассматривают не те же люди, что электрику корпуса. */
  const areaRows = useDictRows('area')
  const [disc, setDisc] = useState(initial?.disc ?? 'default')
  /* Ветка маршрута (V88, V72): стадия проектирования и контракт. Пусто —
     «любая» и «любой», то есть прежний общий маршрут дисциплины. Базовый
     проект не обязан идти тем же кругом, что рабочая документация, а два
     контракта одной дисциплины рассматривают разные люди. */
  const [routeStage, setRouteStage] = useState(initial?.stage ?? '')
  const [routeContract, setRouteContract] = useState(initial?.contract ?? '')
  const [routeArea, setRouteArea] = useState(initial?.area ?? '')
  /* Ключ ветки такой же, как у сервера: дисциплина либо
     «дисциплина|стадия|контракт|объект». */
  const routeKey = routeStage || routeContract || routeArea
    ? `${disc}|${routeStage}|${routeContract}|${routeArea}`
    : disc
  /* Своя ветка или унаследованная. У дисциплин пометка «наследует» была, у
     стадии, контракта и объекта — нет: выбранная ветка выглядела как общий
     маршрут, и правка уходила в копию, о существовании которой по экрану
     узнать было нельзя. Ключ тот же, каким ветки отдаёт сервер. */
  const branchPicked = routeStage !== '' || routeContract !== '' || routeArea !== ''
  const ownBranch = (stage: string, contract: string, area: string) =>
    rules != null && Object.prototype.hasOwnProperty.call(rules, `${disc}|${stage}|${contract}|${area}`)
  const [steps, setSteps] = useState<MatrixStepDraft[]>([])
  const [dirty, setDirty] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)

  /* Команда заменяет маршрут области целиком: порядок и состав шагов меняются
     вместе, правка одного шага в отрыве смысла не имеет. Сброс meta('matrix-rules')
     обязателен — ключ живёт бессрочно (useMeta, staleTime: Infinity). */
  const saveRoute = useCommand<{ scope: string; stage: string; contract: string; area: string
                                 steps: { role: string; assignee: string | null; act: MatrixActId
                                          sla: number; sla2: number | null; sla3: number | null }[] }>({
    method: 'PUT',
    path: (a) => `/admin/routing/${encodeURIComponent(a.scope)}`,
    body: (a) => ({ steps: a.steps, stage: a.stage || null, contract: a.contract || null,
                    area: a.area || null }),
    invalidate: () => [qk.meta('matrix-rules'), qk.all('audit')],
  })

  const ACT_RU2ID: Record<string, MatrixActId> = {}
  actions.forEach((a) => { ACT_RU2ID[a.ru.toLowerCase()] = a.id })
  const normAct = (v: unknown): MatrixActId => { if (!v) return 'review'; const s = String(v).trim().toLowerCase(); const known = actions.find((a) => a.id === s); if (known) return known.id; return ACT_RU2ID[s] ?? 'review' }
  async function importExcel(file: File) {
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' })
      const sheet = wb.SheetNames[0]
      const ws = sheet ? wb.Sheets[sheet] : undefined
      const rows = ws ? XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' }) : []
      /* Тосты — общесистемные (ui/toast + ToastHost в App). Прежние вызовы
         store.toast складывались в массив, который никто не отображает: об
         ошибке импорта пользователь так и не узнавал. */
      if (!rows.length) { toast({ tone: 'info', title: t(lang, 'Пустой файл', 'Empty file'), text: t(lang, 'В Excel не найдено строк маршрута.', 'No routing rows found.') }); return }
      // Имя из файла, которого нет в списке сотрудников, не подменяется первым
      // попавшимся: шаг остаётся без исполнителя, и об этом сказано в тосте.
      let unknown = 0
      const next = rows.map((r) => {
        const get = (keys: string[]) => { for (const k of Object.keys(r)) { if (keys.some((n) => k.toLowerCase().includes(n))) return r[k] } return '' }
        const person = String(get(['исполн', 'assignee', 'person', '执行人'])).trim()
        const assignee = codeByName.get(person) ?? ''
        if (person && !assignee) unknown++
        const act = normAct(get(['действ', 'action', 'act', '操作']))
        /* Колонок SLA до трёх — по кругам рассмотрения (V96). Ищутся все
           колонки со «sla»/«срок» в порядке файла: первая — первый круг,
           вторая и третья — второй и последующие. Старый файл с одной
           колонкой остаётся правильным: недостающие круги наследуют. */
        const slaKeys = Object.keys(r).filter((k) => /sla|срок/i.test(k))
        const slaAt = (n: number): number | '' => {
          const k = slaKeys[n]
          if (k == null || String(r[k]).trim() === '') return ''
          const v = Number(r[k])
          return Number.isFinite(v) ? v : ''
        }
        return {
          act,
          role: String(get(['роль', 'role', '角色']) || 'Роль'),
          assignee,
          sla: slaAt(0) === '' ? 5 : (slaAt(0) as number),
          sla2: slaAt(1),
          sla3: slaAt(2),
          /* Колонок «эскалация» и «параллельно» в файле больше нет: их никто
             не сохранял — save отправляет role/assignee/act/sla, и полей под
             них нет в схеме. Одновременность выводится из действия шага, как
             и на экране. */
          parallel: act === 'review',
        }
      })
      setSteps(next); setDirty(true)
      toast({
        tone: 'success',
        title: t(lang, 'Маршрут загружен из Excel', 'Routing imported'),
        text: next.length + t(lang, ' строк импортировано.', ' rows imported.')
              + (unknown ? t(lang, ` Не опознано исполнителей: ${unknown}.`, ` Unknown assignees: ${unknown}.`) : ''),
      })
    } catch (err) {
      toast({ tone: 'danger', title: t(lang, 'Ошибка импорта', 'Import error'), text: err instanceof Error ? err.message : String(err) })
    }
  }
  function downloadTemplate() {
    /* Заголовки — на языке интерфейса, тем же словарём, что и экранные
       таблицы. Значения (названия действий, Да/Нет) — данные маршрута и
       остаются на языке источника; обратный импорт понимает все три языка. */
    const h = (ru: string) => colHeader(lang, ru)
    /* В шаблоне только то, что маршрут действительно хранит. Колонки
       «Эскалация, дн» и «Параллельно» отсюда убраны: их значения никуда не
       уходили — save отправляет role/assignee/act/sla, и таких полей в схеме
       нет. Администратор заполнял шаблон и был уверен, что настроил эскалацию. */
    const sample = steps.map((s, i) => ({ [h('№')]: i + 1, [h('Действие')]: actions.find((a) => a.id === s.act)?.ru ?? s.act, [h('Роль')]: s.role, [h('Исполнитель')]: people.find((u) => u.id === s.assignee)?.name ?? '', [h('SLA, 1-й круг')]: s.sla, [h('SLA, 2-й круг')]: s.sla2, [h('SLA, 3-й и далее')]: s.sla3 }))
    const ws = XLSX.utils.json_to_sheet(sample)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Маршрут')
    XLSX.writeFile(wb, 'Матрица_распределения_шаблон.xlsx')
    toast({ tone: 'info', title: t(lang, 'Шаблон выгружен', 'Template exported'), text: t(lang, 'Заполните и загрузите обратно.', 'Fill and re-import.') })
  }
  /* Черновик собирается из ответа сервера. Список сотрудников — тоже источник:
     без него имя исполнителя не превратить в код, поэтому он в зависимостях. */
  const applyRules = useCallback((source: Record<string, MatrixRule[]> | undefined) => {
    /* Ветки нет — черновик берётся у общего маршрута дисциплины: так его и
       заводят, правкой копии, а не с чистого листа. */
    const list = source?.[routeKey] ?? source?.[disc] ?? source?.default ?? []
    setSteps(list.map((r) => ({
      role: r.role,
      /* Код исполнителя приходит с сервера. Прежде он восстанавливался обратным
         поиском по отображаемому имени, и сохранение маршрута стирало то, чего
         в списке сотрудников нет: переименованного человека, а с появлением
         групп — группу целиком (команда заменяет маршрут области целиком). */
      assignee: r.assignee ?? codeByName.get(r.person) ?? '',
      act: r.act,
      sla: r.sla,
      sla2: r.sla2 ?? '',
      sla3: r.sla3 ?? '',
      parallel: r.act === 'review',
    })))
    setDirty(false)
  }, [disc, routeKey, codeByName])
  /* Правила приходят с сервера асинхронно: без зависимости от загруженного
     объекта редактор остался бы пустым после первой отрисовки. */
  useEffect(() => { applyRules(rules) }, [rules, applyRules])
  /* «Сбросить» перечитывает маршрут у сервера, а не восстанавливает локальную
     копию: пока экран открыт, маршрут мог изменить другой администратор. */
  const reset = async () => { const fresh = await rulesQuery.refetch(); applyRules(fresh.data) }
  const save = () => saveRoute.mutate(
    { scope: disc, stage: routeStage, contract: routeContract, area: routeArea,
      steps: steps.map((s) => ({ role: s.role.trim() || 'Роль', assignee: s.assignee || null, act: s.act,
                                 sla: s.sla, sla2: s.sla2 === '' ? null : s.sla2, sla3: s.sla3 === '' ? null : s.sla3 })) },
    /* Успех виден сам: метка «есть несохранённые изменения» гаснет, а список
       перечитывается инвалидацией. Тоста здесь нет намеренно. */
    { onSuccess: () => setDirty(false) },
  )
  const upd = (i: number, k: keyof MatrixStepDraft, v: MatrixStepDraft[keyof MatrixStepDraft]) => { const n = steps.map((s, j) => j === i ? { ...s, [k]: v } : s); setSteps(n); setDirty(true) }
  const move = (i: number, d: number) => { const j = i + d; if (j < 0 || j >= steps.length) return; const n = steps.slice(); const a = n[i]; const b = n[j]; if (!a || !b) return; n[i] = b; n[j] = a; setSteps(n); setDirty(true) }
  const del = (i: number) => { setSteps(steps.filter((_, j) => j !== i)); setDirty(true) }
  const add = () => { setSteps([...steps, { role: 'Новая роль', assignee: people[0]?.id ?? '', act: 'review', sla: 5, sla2: '', sla3: '', parallel: false }]); setDirty(true) }
  const reviewerCount = steps.filter((s) => s.act === 'review' && s.parallel).length
  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        {/* Четыре отбора и четыре кнопки — около 1290 px в колонке шириной 980:
            без переноса «Сохранить маршрут» уходила за край окна, а вбок
            прокручивалась вся вкладка, а не её таблица. */}
        <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 14, marginBottom: 14 }}>
          {onBack ? (
            <div style={{ paddingBottom: 2 }}>
              <Button variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={14} />} onClick={onBack}>
                {t(lang, 'К реестру', 'To the registry')}
              </Button>
            </div>
          ) : null}
          <div style={{ width: 260 }}>
            <Select label={t(lang, 'Маршрут для дисциплины', 'Routing for discipline')} value={disc} onChange={(e) => setDisc(e.target.value)}>
              <option value="default">{t(lang, 'По умолчанию (все дисциплины)', 'Default (all disciplines)')}</option>
              {/* Согласование реестров — своя область: состав шагов и SLA у
                  него свои, а без ветки реестры идут маршрутом по умолчанию. */}
              <option value="register">{t(lang, 'Реестры MDR/VDR (согласование)', 'MDR/VDR registers (approval)')}{rules?.register ? '' : t(lang, ' · наследует', ' · inherits')}</option>
              {discRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}{rules?.[r.code] ? '' : t(lang, ' · наследует', ' · inherits')}</option>)}
            </Select>
          </div>
          {/* Ветка маршрута: базовый проект не идёт тем же кругом, что рабочая
              документация, а два контракта одной дисциплины рассматривают разные
              люди. Пусто — общий маршрут дисциплины, как было. */}
          <div style={{ width: 180 }}>
            <Select label={t(lang, 'Стадия', 'Stage')} value={routeStage} onChange={(e) => setRouteStage(e.target.value)}>
              <option value="">{t(lang, 'Любая', 'Any')}</option>
              {stageRows.map((r) => <option key={r.code} value={r.code}>{r.code}{ownBranch(r.code, routeContract, routeArea) ? t(lang, ' · своя ветка', ' · own branch') : ''}</option>)}
            </Select>
          </div>
          <div style={{ width: 180 }}>
            <Select label={t(lang, 'Контракт', 'Contract')} value={routeContract} onChange={(e) => setRouteContract(e.target.value)}>
              <option value="">{t(lang, 'Любой', 'Any')}</option>
              {contracts.map((c) => <option key={c.code} value={c.code}>{c.code}{ownBranch(routeStage, c.code, routeArea) ? t(lang, ' · своя ветка', ' · own branch') : ''}</option>)}
            </Select>
          </div>
          <div style={{ width: 180 }}>
            <Select label={t(lang, 'Объект', 'Area')} value={routeArea} onChange={(e) => setRouteArea(e.target.value)}>
              <option value="">{t(lang, 'Любой', 'Any')}</option>
              {areaRows.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}{ownBranch(routeStage, routeContract, a.code) ? t(lang, ' · своя ветка', ' · own branch') : ''}</option>)}
            </Select>
          </div>
          {/* Что открыто: своя ветка или маршрут дисциплины. Без этой строки
              ветка «Стадия = DD» неотличима на экране от общего маршрута, а
              отвязывается она навсегда — правку умолчания больше не подхватит. */}
          {branchPicked ? (
            <div style={{ paddingBottom: 8 }}
                 title={ownBranch(routeStage, routeContract, routeArea)
                   ? t(lang, 'У ветки свой маршрут: правка общего маршрута дисциплины её не касается',
                             'The branch has its own route: editing the discipline route does not touch it')
                   : t(lang, 'Своего маршрута у ветки нет — показан маршрут дисциплины. «Сохранить маршрут» заведёт ветку',
                             'The branch has no route of its own — the discipline route is shown. Saving will create the branch')}>
              <Badge tone={ownBranch(routeStage, routeContract, routeArea) ? 'info' : 'neutral'}>
                {ownBranch(routeStage, routeContract, routeArea)
                  ? t(lang, 'Своя ветка', 'Own branch')
                  : t(lang, 'Наследует маршрут дисциплины', 'Inherits the discipline route')}
              </Badge>
            </div>
          ) : null}
          {/* Кнопки держатся вместе и уезжают на вторую строку целой группой:
              распорка flex: 1 растягивала бы только первую из них. */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            {dirty ? <Badge tone="warning" dot>{t(lang, 'Есть несохранённые изменения', 'Unsaved changes')}</Badge> : null}
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void importExcel(f); e.target.value = '' }} />
            <Button variant="ghost" size="sm" iconLeft={<Icon name="file-down" size={14} />} onClick={downloadTemplate}>{t(lang, 'Шаблон', 'Template')}</Button>
            <Button variant="secondary" size="sm" iconLeft={<Icon name="upload" size={14} />} onClick={() => fileRef.current && fileRef.current.click()}>{t(lang, 'Загрузить Excel', 'Import Excel')}</Button>
            <Button variant="secondary" size="sm" disabled={rulesQuery.isFetching} onClick={() => void reset()}>{rulesQuery.isFetching ? t(lang, 'Чтение…', 'Reading…') : t(lang, 'Сбросить', 'Reset')}</Button>
            {/* Пустой маршрут сервер отбивает кодом route.empty. Кнопка гаснет
                раньше не вместо этой проверки, а чтобы не слать заведомый отказ. */}
            <Button variant="primary" size="sm" iconLeft={<Icon name="save" size={14} />}
                    disabled={saveRoute.isPending || !steps.length}
                    title={steps.length ? undefined : t(lang, 'В маршруте нужен хотя бы один шаг', 'A route needs at least one step')}
                    onClick={save}>
              {saveRoute.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить маршрут', 'Save routing')}
            </Button>
          </div>
        </div>

        {/* Переключателя «параллельно / последовательно» и поля «эскалация, дн»
            здесь больше нет. Они выглядели настройками, но на сервер не
            уходили: администратор их выставлял и был уверен, что настроил
            маршрут. Порядок задаёт действие шага, и об этом сказано прямо. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', marginBottom: 14, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
          <span style={{ color: 'var(--accent-ink)', display: 'inline-flex' }}><Icon name="info" size={15} /></span>
          <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
            {t(lang,
              `Порядок задаёт действие шага: входной контроль и рассмотрение (${reviewerCount}) идут одновременно, утверждение открывается, когда они закрыты, выпуск — последним.`,
              `Order comes from the step action: intake and review (${reviewerCount}) run at once, approval opens when they are done, issue comes last.`)}
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 7, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            <CIcon name="siren" size={13} color="var(--danger-ink)" />
            {t(lang, 'Просрочку поднимают в отчёте эскалации', 'Overdue items are escalated from the escalation report')}
          </span>
        </div>

        <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          {/* Три колонки SLA — по кругам рассмотрения (V96): первая подача,
              вторая и последующие до кода A. Пустая колонка наследует
              предыдущую — её действующее значение видно плейсхолдером. */}
          <div style={{ display: 'grid', gridTemplateColumns: '30px 1fr 1.1fr 1.1fr 64px 64px 64px 70px', gap: 10, padding: '9px 14px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-strong)' }}>
            {['№', t(lang, 'Действие', 'Action'), t(lang, 'Роль', 'Role'), t(lang, 'Исполнитель', 'Assignee'), t(lang, 'SLA 1 круг', 'SLA rd 1'), t(lang, 'SLA 2 круг', 'SLA rd 2'), t(lang, 'SLA 3+ круг', 'SLA rd 3+'), ''].map((h, i) => <span key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-2xs)', fontWeight: 600, letterSpacing: 'var(--tracking-caps)', textTransform: 'uppercase', color: 'var(--text-subtle)' }}>{h}</span>)}
          </div>
          {steps.map((s, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '30px 1fr 1.1fr 1.1fr 64px 64px 64px 70px', gap: 10, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ display: 'grid', placeItems: 'center', width: 24, height: 24, borderRadius: 999, background: 'var(--accent-soft)', color: 'var(--accent-ink)', border: '1px solid var(--accent-line)' }}><Icon name={MACT[s.act] || 'circle'} size={13} /></span>
              <Select size="sm" value={s.act} onChange={(e) => upd(i, 'act', e.target.value as MatrixActId)} block={false}>{actions.map((a) => <option key={a.id} value={a.id}>{a.ru}</option>)}</Select>
              <Input size="sm" value={s.role} onChange={(e) => upd(i, 'role', e.target.value)} />
              {/* Видимое — имя, отправляемое — код учётной записи: неизвестный
                  код сервер отбивает как route.unknown-assignee. */}
              <Select size="sm" value={s.assignee} onChange={(e) => upd(i, 'assignee', e.target.value)} block={false}>
                <option value="">{t(lang, '— не назначен', '— unassigned')}</option>
                <optgroup label={t(lang, 'Сотрудники', 'People')}>
                  {people.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </optgroup>
                {groups.length ? (
                  <optgroup label={t(lang, 'Группы', 'Groups')}>
                    {groups.map((g) => (
                      <option key={g.code} value={g.code}>{g.name} ({g.members.length})</option>
                    ))}
                  </optgroup>
                ) : null}
              </Select>
              <Input size="sm" type="number" value={s.sla} onChange={(e) => upd(i, 'sla', Number(e.target.value))} />
              {/* Пусто — наследует предыдущий круг; действующий срок показан
                  плейсхолдером, чтобы «пусто» не читалось как «ноль». */}
              <Input size="sm" type="number" value={s.sla2} placeholder={String(s.sla)}
                     onChange={(e) => upd(i, 'sla2', e.target.value === '' ? '' : Number(e.target.value))} />
              <Input size="sm" type="number" value={s.sla3} placeholder={String(s.sla2 === '' ? s.sla : s.sla2)}
                     onChange={(e) => upd(i, 'sla3', e.target.value === '' ? '' : Number(e.target.value))} />
              <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <IconBtn icon="chevron-up" onClick={() => move(i, -1)} />
                <IconBtn icon="chevron-down" onClick={() => move(i, 1)} />
                <IconBtn icon="trash-2" danger onClick={() => del(i)} />
              </div>
            </div>
          ))}
          <button onClick={add} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', padding: '11px', border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--text-link)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}><Icon name="plus" size={14} />{t(lang, 'Добавить шаг маршрута', 'Add routing step')}</button>
        </div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          <Icon name="info" size={13} />{t(lang, 'Параллельные шаги рассмотрения выполняются одновременно; замечания консолидирует координатор ДО. При просрочке SLA задача эскалируется.', 'Parallel steps run at once; the coordinator consolidates. Overdue tasks escalate.')}
        </div>
      </div>
    </div>
  )
}
// ===== Matrix registry: every branch at a glance =========================
/** Строка реестра: ветка целиком, как её отдаёт сервер. */
interface MatrixBranchRow extends MatrixBranchRef { key: string; steps: MatrixRule[] }

/** Эффективные суммы SLA по кругам: пустая колонка наследует предыдущую. */
function slaSums(steps: MatrixRule[]): [number, number, number] {
  const s1 = steps.reduce((a, r) => a + (r.sla ?? 0), 0)
  const s2 = steps.reduce((a, r) => a + (r.sla2 ?? r.sla ?? 0), 0)
  const s3 = steps.reduce((a, r) => a + (r.sla3 ?? r.sla2 ?? r.sla ?? 0), 0)
  return [s1, s2, s3]
}

/** Совпадают ли маршруты по существу: роли, исполнители, действия и сроки. */
function sameRoute(a: MatrixRule[] | undefined, b: MatrixRule[] | undefined): boolean {
  if (!a || !b || a.length !== b.length) return false
  return a.every((r, i) => {
    const o = b[i]
    return o != null && r.role === o.role && (r.assignee ?? '') === (o.assignee ?? '')
      && r.act === o.act && r.sla === o.sla
      && (r.sla2 ?? null) === (o.sla2 ?? null) && (r.sla3 ?? null) === (o.sla3 ?? null)
  })
}

/** Разобранная загрузка из Excel — то, что уйдёт bulk-командой после просмотра. */
interface MatrixImportPreview {
  routes: { scope: string; stage: string | null; contract: string | null; area: string | null
            steps: { role: string; assignee: string | null; act: MatrixActId
                     sla: number; sla2: number | null; sla3: number | null }[]
            label: string; state: 'new' | 'changed' | 'same' }[]
  unknown: number
}

function MatrixRegistry({ lang, onOpen }: { lang: Lang; onOpen: (b: MatrixBranchRef) => void }) {
  const rulesQuery = useMeta<Record<string, MatrixRule[]>>('matrix-rules')
  const rules = rulesQuery.data
  const actions = useMeta<{ id: MatrixActId; ru: string; icon: string }[]>('matrix-actions').data ?? []
  const reviewers = useMeta<{ code: string; name: string; kind: 'user' | 'group' }[]>('reviewers').data
  const discRows = useDictRows('disc')
  const stageRows = useDictRows('stage')
  const contracts = useContracts()
  const areaRows = useDictRows('area')
  const discName = useMemo(() => new Map(discRows.map((r) => [r.code, r.name])), [discRows])
  const nameByCode = useMemo(() => new Map((reviewers ?? []).map((r) => [r.code, r.name])), [reviewers])
  const codeByName = useMemo(() => new Map((reviewers ?? []).map((r) => [r.name, r.code])), [reviewers])
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [pending, setPending] = useState<MatrixImportPreview | null>(null)
  const [removing, setRemoving] = useState<MatrixBranchRow | null>(null)

  const rows = useMemo<MatrixBranchRow[]>(() => {
    const out = Object.entries(rules ?? {}).map(([key, steps]) => {
      const [disc = key, stage = '', contract = '', area = ''] = key.split('|')
      return { key, disc, stage, contract, area, steps }
    })
    /* Порядок чтения: default первым, дальше дисциплины по алфавиту, ветки
       дисциплины — под её общим маршрутом. */
    out.sort((a, b) => {
      const ad = a.disc === 'default' ? '' : a.disc
      const bd = b.disc === 'default' ? '' : b.disc
      return ad.localeCompare(bd) || a.key.localeCompare(b.key)
    })
    return out
  }, [rules])

  /* От чего ветка отличается: ветка сравнивается с общим маршрутом своей
     дисциплины, общий маршрут дисциплины — с default. Ради этого реестр и
     заведён: «уникальна ветка или копия» прежде выяснялось только глазами. */
  const diffOf = (row: MatrixBranchRow): { label: string; distinct: boolean } => {
    if (row.key === 'default') return { label: t(lang, 'базовый маршрут', 'base route'), distinct: true }
    const trunk = row.stage === '' && row.contract === '' && row.area === ''
    const base = trunk ? rules?.default : rules?.[row.disc] ?? rules?.default
    const vs = trunk
      ? t(lang, 'default', 'default')
      : rules?.[row.disc] ? t(lang, 'маршрута дисциплины', 'the discipline route') : t(lang, 'default', 'default')
    return sameRoute(row.steps, base)
      ? { label: t(lang, 'совпадает с ', 'same as ') + vs, distinct: false }
      : { label: t(lang, 'отличается от ', 'differs from ') + vs, distinct: true }
  }

  // ---- Excel: все матрицы одним файлом ---------------------------------
  function exportAll() {
    const h = (ru: string) => colHeader(lang, ru)
    const data = rows.flatMap((row) => row.steps.map((r, i) => ({
      [h('Дисциплина')]: row.disc,
      [h('Стадия')]: row.stage,
      [h('Контракт')]: row.contract,
      [h('Объект')]: row.area,
      [h('№')]: i + 1,
      [h('Действие')]: actions.find((a) => a.id === r.act)?.ru ?? r.act,
      [h('Роль')]: r.role,
      [h('Исполнитель')]: (r.assignee ? nameByCode.get(r.assignee) : undefined) ?? r.person ?? '',
      [h('SLA, 1-й круг')]: r.sla,
      [h('SLA, 2-й круг')]: r.sla2 ?? '',
      [h('SLA, 3-й и далее')]: r.sla3 ?? '',
    })))
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Матрицы')
    XLSX.writeFile(wb, 'Матрицы_распределения.xlsx')
    toast({ tone: 'info', title: t(lang, 'Матрицы выгружены', 'Matrices exported'),
            text: t(lang, `Веток: ${rows.length}. Правьте и загружайте обратно.`, `${rows.length} branches. Edit and re-import.`) })
  }

  const ACT_RU2ID: Record<string, MatrixActId> = {}
  actions.forEach((a) => { ACT_RU2ID[a.ru.toLowerCase()] = a.id })
  const normAct = (v: unknown): MatrixActId => {
    if (!v) return 'review'
    const s = String(v).trim().toLowerCase()
    return actions.find((a) => a.id === s)?.id ?? ACT_RU2ID[s] ?? 'review'
  }

  async function importAll(file: File) {
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' })
      const sheet = wb.SheetNames[0]
      const ws = sheet ? wb.Sheets[sheet] : undefined
      const raw = ws ? XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' }) : []
      if (!raw.length) {
        toast({ tone: 'info', title: t(lang, 'Пустой файл', 'Empty file'), text: t(lang, 'В Excel не найдено строк.', 'No rows found.') })
        return
      }
      const get = (r: Record<string, unknown>, keys: string[]) => {
        for (const k of Object.keys(r)) { if (keys.some((n2) => k.toLowerCase().includes(n2))) return r[k] }
        return ''
      }
      /* Коды веток сверяются со справочниками ДО отправки: scope сервер не
         проверяет, и опечатка в дисциплине молча завела бы ветку-сироту,
         которую не выберет ни один пакет. Ошибка в любой строке — файл не
         принимается целиком, тем же правилом, каким сервер применяет загрузку. */
      const knownDisc = new Set(discRows.map((r) => r.code))
      const knownStage = new Set(stageRows.map((r) => r.code))
      const knownContract = new Set(contracts.map((c) => c.code))
      const knownArea = new Set(areaRows.map((a) => a.code))
      const bad: string[] = []
      let unknown = 0
      const branches = new Map<string, { ref: MatrixBranchRef; steps: MatrixImportPreview['routes'][number]['steps'] }>()
      let prev: MatrixBranchRef | null = null
      raw.forEach((r, i) => {
        const line = i + 2 // строка листа: после шапки
        const discRaw = String(get(r, ['дисципл', 'discipline', '专业'])).trim()
        let ref: MatrixBranchRef
        if (!discRaw && prev) {
          /* Пустая ячейка дисциплины — строка продолжает ветку выше: так
             выглядит выгрузка, у которой ветку не повторяли в каждой строке. */
          ref = prev
        } else {
          const disc = /^(default|по умолчанию)$/i.test(discRaw) ? 'default'
            : /^(register|реестр)/i.test(discRaw) ? 'register' : discRaw
          const stage = String(get(r, ['стади', 'stage', '阶段'])).trim()
          const contract = String(get(r, ['контракт', 'contract', '合同'])).trim()
          const area = String(get(r, ['объект', 'area', '对象'])).trim()
          if (!disc) { bad.push(t(lang, `строка ${line}: нет дисциплины`, `row ${line}: no discipline`)); return }
          if (disc !== 'default' && disc !== 'register' && !knownDisc.has(disc)) bad.push(t(lang, `строка ${line}: дисциплина «${disc}»`, `row ${line}: discipline “${disc}”`))
          if (stage && !knownStage.has(stage)) bad.push(t(lang, `строка ${line}: стадия «${stage}»`, `row ${line}: stage “${stage}”`))
          if (contract && !knownContract.has(contract)) bad.push(t(lang, `строка ${line}: контракт «${contract}»`, `row ${line}: contract “${contract}”`))
          if (area && !knownArea.has(area)) bad.push(t(lang, `строка ${line}: объект «${area}»`, `row ${line}: area “${area}”`))
          ref = { disc, stage, contract, area }
        }
        prev = ref
        const person = String(get(r, ['исполн', 'assignee', 'person', '执行人'])).trim()
        const assignee = codeByName.get(person) ?? (nameByCode.has(person) ? person : '')
        if (person && !assignee) unknown++
        const slaKeys = Object.keys(r).filter((k) => /sla|срок/i.test(k))
        const slaAt = (j: number): number | null => {
          const k = slaKeys[j]
          if (k == null || String(r[k]).trim() === '') return null
          const v = Number(r[k])
          return Number.isFinite(v) ? v : null
        }
        const key = `${ref.disc}|${ref.stage}|${ref.contract}|${ref.area}`
        const entry = branches.get(key) ?? { ref, steps: [] }
        entry.steps.push({
          role: String(get(r, ['роль', 'role', '角色']) || 'Роль'),
          assignee: assignee || null,
          act: normAct(get(r, ['действ', 'action', '操作'])),
          sla: slaAt(0) ?? 5,
          sla2: slaAt(1),
          sla3: slaAt(2),
        })
        branches.set(key, entry)
      })
      if (bad.length) {
        toast({ tone: 'danger', title: t(lang, 'Файл не принят', 'File rejected'),
                text: t(lang, 'Неизвестные коды: ', 'Unknown codes: ') + bad.slice(0, 5).join('; ')
                      + (bad.length > 5 ? ` (+${bad.length - 5})` : '') })
        return
      }
      const routes = [...branches.values()].map(({ ref, steps }) => {
        const key = ref.stage || ref.contract || ref.area
          ? `${ref.disc}|${ref.stage}|${ref.contract}|${ref.area}` : ref.disc
        const current = rules?.[key]
        const asRules: MatrixRule[] = steps.map((s2) => ({
          role: s2.role, person: '', assignee: s2.assignee ?? undefined, act: s2.act,
          sla: s2.sla, sla2: s2.sla2, sla3: s2.sla3,
        }))
        const label = (ref.disc === 'default' ? t(lang, 'По умолчанию', 'Default')
            : ref.disc === 'register' ? t(lang, 'Реестры', 'Registers') : ref.disc)
          + (ref.stage ? ` · ${ref.stage}` : '') + (ref.contract ? ` · ${ref.contract}` : '')
          + (ref.area ? ` · ${ref.area}` : '')
        return {
          scope: ref.disc, stage: ref.stage || null, contract: ref.contract || null, area: ref.area || null,
          steps, label,
          state: current == null ? 'new' as const : sameRoute(asRules, current) ? 'same' as const : 'changed' as const,
        }
      })
      setPending({ routes, unknown })
    } catch (err) {
      toast({ tone: 'danger', title: t(lang, 'Ошибка импорта', 'Import error'), text: err instanceof Error ? err.message : String(err) })
    }
  }

  /* Загрузка применяется одной командой: сервер заменяет перечисленные ветки
     атомарно, не названные в файле не трогает. Ветки без изменений не шлются —
     незачем плодить версии и записи аудита из ничего. */
  const bulkSave = useCommand<{ routes: MatrixImportPreview['routes'] }>({
    method: 'PUT',
    path: () => '/admin/routing',
    body: (a) => ({ routes: a.routes.filter((r) => r.state !== 'same')
      .map(({ scope, stage, contract, area, steps }) => ({ scope, stage, contract, area, steps })) }),
    invalidate: () => [qk.meta('matrix-rules'), qk.all('audit')],
  })
  const apply = () => {
    if (!pending) return
    const changed = pending.routes.filter((r) => r.state !== 'same')
    if (!changed.length) {
      toast({ tone: 'info', title: t(lang, 'Изменений нет', 'Nothing to apply'), text: t(lang, 'Файл совпадает с действующими матрицами.', 'The file matches the current matrices.') })
      setPending(null)
      return
    }
    bulkSave.mutate({ routes: pending.routes }, {
      onSuccess: () => {
        toast({ tone: 'success', title: t(lang, 'Матрицы загружены', 'Matrices imported'),
                text: t(lang, `Заменено веток: ${changed.length}.`, `${changed.length} branches replaced.`) })
        setPending(null)
      },
      onError: (err) => toast({ tone: 'danger', title: t(lang, 'Загрузка отклонена', 'Import rejected'), text: err.message }),
    })
  }

  /* Снятие ветки — отдельная явная команда: загрузка из Excel не удаляет
     ничего, и «ветки нет в файле» не значит «ветку снять». */
  const deleteRoute = useCommand<{ row: MatrixBranchRow }>({
    method: 'DELETE',
    path: ({ row }) => {
      const p = new URLSearchParams()
      if (row.stage) p.set('stage', row.stage)
      if (row.contract) p.set('contract', row.contract)
      if (row.area) p.set('area', row.area)
      const qs = p.toString()
      return `/admin/routing/${encodeURIComponent(row.disc)}${qs ? '?' + qs : ''}`
    },
    invalidate: () => [qk.meta('matrix-rules'), qk.all('audit')],
  })

  const branchLabel = (row: MatrixBranchRow) =>
    row.disc === 'default'
      ? t(lang, 'По умолчанию (все дисциплины)', 'Default (all disciplines)')
      : row.disc === 'register'
        ? t(lang, 'Реестры MDR/VDR (согласование)', 'MDR/VDR registers (approval)')
        : `${row.disc}${discName.get(row.disc) ? ' — ' + discName.get(row.disc) : ''}`

  const grid = 'minmax(220px, 1.4fr) 90px 110px 90px 60px 150px minmax(180px, 1fr) 90px'
  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--text-title)' }}>
            {t(lang, 'Реестр матриц', 'Matrix registry')}
          </span>
          <Badge tone="info">{t(lang, `Веток: ${rows.length}`, `Branches: ${rows.length}`)}</Badge>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                   onChange={(e) => { const f = e.target.files?.[0]; if (f) void importAll(f); e.target.value = '' }} />
            <Button variant="ghost" size="sm" iconLeft={<Icon name="file-down" size={14} />} onClick={exportAll}>
              {t(lang, 'Выгрузить все в Excel', 'Export all to Excel')}
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<Icon name="upload" size={14} />} onClick={() => fileRef.current && fileRef.current.click()}>
              {t(lang, 'Загрузить Excel', 'Import Excel')}
            </Button>
            <Button variant="primary" size="sm" iconLeft={<Icon name="pencil" size={14} />}
                    onClick={() => onOpen({ disc: 'default', stage: '', contract: '', area: '' })}>
              {t(lang, 'Редактор', 'Editor')}
            </Button>
          </div>
        </div>

        <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: grid, gap: 10, padding: '9px 14px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-strong)' }}>
            {[t(lang, 'Ветка', 'Branch'), t(lang, 'Стадия', 'Stage'), t(lang, 'Контракт', 'Contract'), t(lang, 'Объект', 'Area'),
              t(lang, 'Шагов', 'Steps'), t(lang, 'SLA по кругам', 'SLA by round'), t(lang, 'Отличие', 'Difference'), '']
              .map((h, i) => <span key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-2xs)', fontWeight: 600, letterSpacing: 'var(--tracking-caps)', textTransform: 'uppercase', color: 'var(--text-subtle)' }}>{h}</span>)}
          </div>
          {rows.map((row) => {
            const diff = diffOf(row)
            const [s1, s2, s3] = slaSums(row.steps)
            const sub = row.stage !== '' || row.contract !== '' || row.area !== ''
            return (
              <div key={row.key} className="tdms-rail"
                   style={{ display: 'grid', gridTemplateColumns: grid, gap: 10, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                   {...clickable(() => onOpen({ disc: row.disc, stage: row.stage, contract: row.contract, area: row.area }))}>
                <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', paddingLeft: sub ? 18 : 0 }}>
                  {sub ? <span style={{ color: 'var(--text-subtle)' }}>└ </span> : null}{branchLabel(row)}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>{row.stage || '—'}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>{row.contract || '—'}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>{row.area || '—'}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}>{row.steps.length}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)' }}
                      title={t(lang, 'Сумма сроков шагов: 1-й / 2-й / 3-й+ круг', 'Total step SLA: round 1 / 2 / 3+')}>
                  {s1} / {s2} / {s3}
                </span>
                <span style={{ font: 'var(--font-caption)', color: diff.distinct ? 'var(--text-body)' : 'var(--text-subtle)' }}>{diff.label}</span>
                <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <IconBtn icon="pencil" title={t(lang, 'Открыть в редакторе', 'Open in the editor')}
                           onClick={(e) => { e.stopPropagation(); onOpen({ disc: row.disc, stage: row.stage, contract: row.contract, area: row.area }) }} />
                  <IconBtn icon="trash-2" danger disabled={row.key === 'default'}
                           title={row.key === 'default'
                             ? t(lang, 'Маршрут по умолчанию снять нельзя', 'The default route cannot be removed')
                             : t(lang, 'Снять ветку', 'Remove the branch')}
                           onClick={(e) => { e.stopPropagation(); setRemoving(row) }} />
                </div>
              </div>
            )
          })}
          {!rows.length && !rulesQuery.isFetching ? (
            <div style={{ padding: 24, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              {t(lang, 'Маршрутов пока нет.', 'No routes yet.')}
            </div>
          ) : null}
        </div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          <Icon name="info" size={13} />
          {t(lang,
            'Дисциплина без строки в реестре идёт маршрутом по умолчанию. Загрузка из Excel заменяет перечисленные в файле ветки целиком; не названные — не трогает.',
            'A discipline with no row here follows the default route. Excel import replaces the branches named in the file; others are left intact.')}
        </div>

        {/* Предпросмотр загрузки: что заведётся, что заменится, что совпадает.
            Применение — одна атомарная команда сервера. */}
        <Dialog open={pending != null} onClose={() => setPending(null)} title={t(lang, 'Загрузка матриц', 'Matrix import')}>
          {pending ? (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Badge tone="success">{t(lang, `Новых: ${pending.routes.filter((r) => r.state === 'new').length}`, `New: ${pending.routes.filter((r) => r.state === 'new').length}`)}</Badge>
                <Badge tone="warning">{t(lang, `Изменится: ${pending.routes.filter((r) => r.state === 'changed').length}`, `Changed: ${pending.routes.filter((r) => r.state === 'changed').length}`)}</Badge>
                <Badge tone="neutral">{t(lang, `Без изменений: ${pending.routes.filter((r) => r.state === 'same').length}`, `Unchanged: ${pending.routes.filter((r) => r.state === 'same').length}`)}</Badge>
                {pending.unknown ? <Badge tone="danger">{t(lang, `Не опознано исполнителей: ${pending.unknown}`, `Unknown assignees: ${pending.unknown}`)}</Badge> : null}
              </div>
              <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)' }}>
                {pending.routes.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
                    <span style={{ flex: 1 }}>{r.label}</span>
                    <span style={{ color: 'var(--text-subtle)' }}>{t(lang, `шагов: ${r.steps.length}`, `${r.steps.length} steps`)}</span>
                    <Badge tone={r.state === 'new' ? 'success' : r.state === 'changed' ? 'warning' : 'neutral'}>
                      {r.state === 'new' ? t(lang, 'новая', 'new') : r.state === 'changed' ? t(lang, 'изменится', 'changed') : t(lang, 'без изменений', 'unchanged')}
                    </Badge>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <Button variant="secondary" size="sm" onClick={() => setPending(null)}>{t(lang, 'Отмена', 'Cancel')}</Button>
                <Button variant="primary" size="sm" disabled={bulkSave.isPending} onClick={apply}>
                  {bulkSave.isPending ? t(lang, 'Применение…', 'Applying…') : t(lang, 'Применить', 'Apply')}
                </Button>
              </div>
            </div>
          ) : null}
        </Dialog>

        {/* Снятие ветки называет последствие словами: пакеты пойдут маршрутом
            дисциплины либо default — а не молча удаляет строку. */}
        <Dialog open={removing != null} onClose={() => setRemoving(null)} title={t(lang, 'Снять ветку маршрута', 'Remove the route branch')}>
          {removing ? (
            <div style={{ display: 'grid', gap: 14 }}>
              <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
                {t(lang,
                  `Ветка «${branchLabel(removing)}${removing.stage ? ' · ' + removing.stage : ''}${removing.contract ? ' · ' + removing.contract : ''}${removing.area ? ' · ' + removing.area : ''}» будет снята. Её пакеты пойдут ${removing.stage || removing.contract || removing.area ? 'общим маршрутом дисциплины либо маршрутом по умолчанию' : 'маршрутом по умолчанию'}. Идущие рассмотрения не трогаются.`,
                  `Branch “${branchLabel(removing)}” will be removed. Its packages will follow ${removing.stage || removing.contract || removing.area ? 'the discipline route or the default route' : 'the default route'}. Reviews under way are not affected.`)}
              </span>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <Button variant="secondary" size="sm" onClick={() => setRemoving(null)}>{t(lang, 'Отмена', 'Cancel')}</Button>
                <Button variant="danger" size="sm" disabled={deleteRoute.isPending}
                        onClick={() => deleteRoute.mutate({ row: removing }, {
                          onSuccess: () => { toast({ tone: 'success', title: t(lang, 'Ветка снята', 'Branch removed'), text: branchLabel(removing) }); setRemoving(null) },
                          onError: (err) => toast({ tone: 'danger', title: t(lang, 'Снять не удалось', 'Removal failed'), text: err.message }),
                        })}>
                  {deleteRoute.isPending ? t(lang, 'Снятие…', 'Removing…') : t(lang, 'Снять ветку', 'Remove the branch')}
                </Button>
              </div>
            </div>
          ) : null}
        </Dialog>
      </div>
    </div>
  )
}

function IconBtn({ icon, onClick, danger, disabled, title }: { icon: string; onClick?: (e: MouseEvent<HTMLButtonElement>) => void; danger?: boolean; disabled?: boolean; title?: string }) {
  return <button onClick={onClick} disabled={disabled} title={title} className="icon-btn" style={{ display: 'grid', placeItems: 'center', width: 26, height: 26, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: danger ? 'var(--danger-ink)' : 'var(--text-subtle)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}><Icon name={icon} size={14} /></button>
}

// ===== Reference dictionaries ===========================================
/** Поля строки справочника, которые принимают команды добавления и правки. */
interface DictRowArgs {
  dict: string
  code: string
  name: string
  active: boolean
  vendor: string | null
  area: string | null
  /* Смысл кода рассмотрения: в какое состояние переходит документ, его
     получивший. Только у справочника code — остальным передаётся null. */
  meaning: string | null
}
interface DictRemoveResult { dict: string; code: string; deactivated: boolean; uses: number }

/* Все три команды сбрасывают meta('dicts'). Без этого правка администратора не
   видна до перезагрузки вкладки: справочники читаются с staleTime: Infinity. */
const DICT_INVALIDATE = [qk.meta('dicts'), qk.all('audit')]

interface MailConfig {
  host: string | null
  port: number
  username: string | null
  from: string | null
  starttls: boolean
  auth: boolean
  appUrl: string | null
  digestHour: number
  /** Значение пароля сервер не отдаёт — только факт, что он задан. */
  passwordSet: boolean
  enabled: boolean
  formats: { kind: string; mode: string }[]
}

/**
 * Подключение к почте и форматы рассылки. Настройки проекта: у разных строек
 * разные почтовые контуры, и общий релей означал бы, что письма одной уходят
 * через сервер другой.
 *
 * Пароль в форму не приходит и не показывается. Пустое поле при сохранении —
 * «оставить прежний»: иначе правка часа сводки стирала бы секрет.
 */
/* Адреса рассылки без учётной записи.

   Копия каждого исходящего — в общий ящик отдела документооборота,
   представителю заказчика на площадке, в группу технадзора: учётных записей у
   них нет и не будет. Список вели правилами в Outlook, и при смене состава он
   рвался молча — узнавали, когда письмо не дошло.

   Адрес подписан на ВИД извещения: общий ящик отдела не ждёт писем про чужие
   шаги рассмотрения. */
interface MailAddress {
  kind: string
  address: string
  note: string | null
}

function MailAddresses({ lang }: { lang: Lang }) {
  const rows = useSub<{ rows: MailAddress[] }>('/admin/mail/addresses')
  const [form, setForm] = useState({ kind: NOTIFY_KINDS[0]?.id ?? 'trm-received', address: '', note: '' })
  const invalidate = () => [qk.sub('/admin/mail/addresses'), qk.all('audit')]
  const add = useCommand<typeof form>({
    method: 'PUT',
    path: () => '/admin/mail/addresses',
    body: (a) => a,
    invalidate,
  })
  const drop = useCommand<{ kind: string; address: string }>({
    method: 'DELETE',
    path: (a) => `/admin/mail/addresses?kind=${encodeURIComponent(a.kind)}&address=${encodeURIComponent(a.address)}`,
    invalidate,
  })
  const list = rows.data?.rows ?? []
  const nameOf = (kind: string) => {
    const k = NOTIFY_KINDS.find((n) => n.id === kind)
    return k ? t(lang, k.ru, k.en) : kind
  }
  return (
    <div style={{ display: 'grid', gap: 10, marginTop: 18 }}>
      <div style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Адреса рассылки', 'Mailing addresses')}</div>
      <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
        {t(lang, 'Для тех, у кого нет учётной записи: общий ящик отдела, представитель на площадке, группа технадзора. Письмо уходит в том же виде, что и сотруднику проекта — с номером предмета.',
                 'For those without an account: a department mailbox, a site representative, a supervision team. The letter is the same one project staff receive.')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
        <Select label={t(lang, 'Вид извещения', 'Notification kind')} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
          {NOTIFY_KINDS.map((k) => <option key={k.id} value={k.id}>{t(lang, k.ru, k.en)}</option>)}
        </Select>
        <Input label={t(lang, 'Адрес почты', 'Email')} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
               placeholder="edms@company.ru" />
        <Input label={t(lang, 'Чей ящик', 'Whose mailbox')} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
               placeholder={t(lang, 'напр. отдел ДО заказчика', 'e.g. customer document control')} />
        <Button variant="secondary" disabled={!form.address.includes('@') || add.isPending}
                title={form.address.includes('@') ? undefined : t(lang, 'Адрес пишется целиком, с собакой', 'Write the full address')}
                onClick={() => add.mutate(form, {
                  onSuccess: () => { setForm({ ...form, address: '', note: '' })
                                     toast({ tone: 'success', title: t(lang, 'Адрес добавлен', 'Address added') }) },
                  onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не добавлен', 'Not added'), text: e.message }),
                })}>{t(lang, 'Добавить', 'Add')}</Button>
      </div>
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        {list.length === 0 ? (
          <div style={{ padding: 12, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
            {t(lang, 'Адресов нет: извещения уходят только участникам проекта.', 'No addresses: notifications go to project members only.')}
          </div>
        ) : list.map((r) => (
          <div key={r.kind + r.address} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', width: 260 }}>{nameOf(r.kind)}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-strong)', flex: 1 }}>{r.address}</span>
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', flex: 1 }}>{r.note ?? ''}</span>
            <Button variant="ghost" size="sm" iconLeft={<Icon name="trash-2" size={13} />} disabled={drop.isPending}
                    onClick={() => drop.mutate({ kind: r.kind, address: r.address })}>{t(lang, 'Снять', 'Remove')}</Button>
          </div>
        ))}
      </div>
    </div>
  )
}

function MailSettingsBody({ lang }: { lang: Lang }) {
  const cfg = useSub<MailConfig>('/admin/mail')
  const [form, setForm] = useState<Partial<MailConfig> & { password?: string } | null>(null)
  const cur = { ...(cfg.data ?? {} as MailConfig), ...(form ?? {}) }
  const dirty = form != null
  const set = (patch: Partial<MailConfig> & { password?: string }) => setForm({ ...(form ?? {}), ...patch })

  const save = useCommand<Record<string, unknown>>({
    method: 'PUT',
    path: () => '/admin/mail',
    body: (a) => a,
    invalidate: () => [qk.sub('/admin/mail'), qk.all('audit')],
  })
  /* Проверка идёт тем же транспортом, что и рабочие извещения: проверка,
     которая ходит своим путём, ничего не проверяет. */
  const test = useCommand<Record<string, never>, { to: string }>({
    path: () => '/admin/mail/test',
    body: () => ({}),
    invalidate: () => [],
  })

  if (cfg.isPending) return <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', color: 'var(--text-subtle)' }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div>
  if (cfg.isError) return <div style={{ padding: 24, font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{t(lang, 'Настройки почты доступны администратору проекта', 'Mail settings are available to the project administrator')}</div>

  const formats: Record<string, string> = Object.fromEntries((cur.formats ?? []).map((f) => [f.kind, f.mode]))
  const submit = () => save.mutate({
    host: cur.host ?? '', port: cur.port ?? 587, username: cur.username ?? '',
    // Пустая строка не уходит как пароль: сервер поймёт её как «не менять».
    password: form?.password ?? '',
    from: cur.from ?? '', starttls: cur.starttls ?? true, auth: cur.auth ?? true,
    appUrl: cur.appUrl ?? '', digestHour: cur.digestHour ?? 7,
    formats: NOTIFY_KINDS.map((k) => ({ kind: k.id, mode: formats[k.id] ?? 'instant' })),
  }, { onSuccess: () => { setForm(null); toast({ tone: 'success', title: t(lang, 'Настройки почты сохранены', 'Mail settings saved') }) } })

  const field = (label: string, node: ReactNode) => (
    <div style={{ display: 'grid', gap: 5 }}>
      <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{label}</span>
      {node}
    </div>
  )

  return (
    /* Прокрутка — на внешнем блоке, сетка — внутри. Сетка на самом
       флекс-элементе получала определённую высоту от колонки экрана, а
       карточки с overflow: hidden сжимались до трети этой высоты: кнопки
       «Проверить связь / Сохранить» оказывались за нижней границей карточки. */
    <div style={{ padding: 24, overflow: 'auto' }}>
     <div style={{ display: 'grid', gap: 18, maxWidth: 860 }}>
      {/* Очередь первой: администратор открывает эту вкладку, когда письмо не
          дошло, а не когда хочет поменять релей. */}
      <MailQueue lang={lang} />
      <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Сервер исходящей почты', 'Outgoing mail server')}</span>
          <Badge tone={cur.enabled ? 'success' : 'neutral'}>
            {cur.enabled ? t(lang, 'Канал включён', 'Channel on') : t(lang, 'Канал выключен', 'Channel off')}
          </Badge>
        </div>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
          {field(t(lang, 'Сервер (SMTP)', 'SMTP host'),
            <Input value={cur.host ?? ''} mono placeholder="smtp.lav.ru" onChange={(e) => set({ host: e.target.value })} />)}
          {field(t(lang, 'Порт', 'Port'),
            <Input value={String(cur.port ?? 587)} mono onChange={(e) => set({ port: Number(e.target.value) || 587 })} />)}
          {field(t(lang, 'Учётная запись', 'Username'),
            <Input value={cur.username ?? ''} mono onChange={(e) => set({ username: e.target.value })} />)}
          {field(cur.passwordSet ? t(lang, 'Пароль (задан)', 'Password (set)') : t(lang, 'Пароль', 'Password'),
            <Input type="password" value={form?.password ?? ''} mono
                   placeholder={cur.passwordSet ? '••••••••' : ''}
                   onChange={(e) => set({ password: e.target.value })} />)}
          {field(t(lang, 'Адрес отправителя', 'From address'),
            <Input value={cur.from ?? ''} mono placeholder="tdms@lav.ru" onChange={(e) => set({ from: e.target.value })} />)}
          {field(t(lang, 'Час суточной сводки', 'Digest hour'),
            <Input value={String(cur.digestHour ?? 7)} mono onChange={(e) => set({ digestHour: Number(e.target.value) || 0 })} />)}
          {field(t(lang, 'Адрес приложения для ссылок', 'App URL for links'),
            <Input value={cur.appUrl ?? ''} mono placeholder="https://tdms.lav.ru" onChange={(e) => set({ appUrl: e.target.value })} />)}
          <div style={{ display: 'grid', gap: 8, alignContent: 'end' }}>
            <label htmlFor="mail-starttls" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
              <input id="mail-starttls" type="checkbox" checked={cur.starttls ?? true} onChange={(e) => set({ starttls: e.target.checked })} />
              STARTTLS
            </label>
            <label htmlFor="mail-auth" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
              <input id="mail-auth" type="checkbox" checked={cur.auth ?? true} onChange={(e) => set({ auth: e.target.checked })} />
              {t(lang, 'Аутентификация', 'Authentication')}
            </label>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--border-subtle)' }}>
          <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            {t(lang, 'Пустой сервер выключает рассылку: события копятся, уведомления в приложении работают.',
                  'An empty host turns mailing off: events keep queuing and in-app notifications still work.')}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" disabled={test.isPending || dirty}
                    title={dirty ? t(lang, 'Сначала сохраните настройки', 'Save the settings first') : undefined}
                    onClick={() => test.mutate({}, {
                      onSuccess: (r) => toast({ tone: 'success', title: t(lang, 'Письмо отправлено', 'Test message sent'), text: r.to }),
                    })}>
              {test.isPending ? t(lang, 'Отправка…', 'Sending…') : t(lang, 'Проверить связь', 'Send test')}
            </Button>
            <Button variant="secondary" size="sm" disabled={!dirty} onClick={() => setForm(null)}>{t(lang, 'Отменить', 'Cancel')}</Button>
            <Button variant="primary" size="sm" disabled={!dirty || save.isPending} onClick={submit}>
              {save.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить', 'Save')}
            </Button>
          </span>
        </div>
      </div>

      <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Формат рассылки', 'Mailing format')}</div>
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', marginTop: 4 }}>
            {t(lang, 'Решение проекта. Участник может отказаться от того, что проект рассылает, но не включить себе выключенное.',
                  'A project-level decision. A member may opt out of what the project sends, but cannot switch on what the project turned off.')}
          </div>
        </div>
        {/* Сетка, а не флекс: Select по умолчанию блочный (ширина 100 %), и в
            строке флекса он растягивался поверх подписи. Ширину задаёт колонка —
            тогда все выпадашки столбцом выровнены, а подпись переносится. */}
        {NOTIFY_KINDS.map((k) => (
          <div key={k.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 190px', alignItems: 'center', gap: 16, padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ minWidth: 0, font: 'var(--font-ui)', color: 'var(--text-strong)' }}>{t(lang, k.ru, k.en)}</span>
            <Select size="sm" value={formats[k.id] ?? 'instant'}
                    onChange={(e) => set({ formats: NOTIFY_KINDS.map((x) => ({ kind: x.id, mode: x.id === k.id ? e.target.value : (formats[x.id] ?? 'instant') })) })}>
              <option value="instant">{t(lang, 'Сразу письмом', 'Instant email')}</option>
              <option value="digest">{t(lang, 'Суточной сводкой', 'Daily digest')}</option>
              <option value="off">{t(lang, 'Не рассылать', 'Do not send')}</option>
            </Select>
          </div>
        ))}
      </div>
      {/* Адреса без учётной записи: общий ящик отдела, представитель на
          площадке, группа технадзора. Их список вели в Outlook. */}
      <MailAddresses lang={lang} />
     </div>
    </div>
  )
}

/* Очередь доставки писем: что не ушло и почему.

   Письма копились молча — «подрядчику не пришло» разбиралось чтением логов
   сервера. Показывается только незавершённое и отказавшее: сотни успешно
   отправленных на экране не нужны. */
function MailQueue({ lang }: { lang: Lang }) {
  const queue = useSub<{
    pending: number; failed: number; skipped: number
    rows: { id: number; kind: string; subject: string; to: string; state: string
            attempts: number; error: string | null; nextTry: string | null }[]
  }>('/admin/mail/queue')
  const rows = queue.data?.rows ?? []
  return (
    <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
        <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Очередь доставки', 'Delivery queue')}</span>
        <Badge tone="info" size="sm">{t(lang, 'в очереди', 'pending')}: {queue.data?.pending ?? 0}</Badge>
        {queue.data?.failed ? <Badge tone="danger" size="sm">{t(lang, 'не ушло', 'failed')}: {queue.data.failed}</Badge> : null}
        {queue.data?.skipped ? <Badge tone="neutral" size="sm">{t(lang, 'пропущено', 'skipped')}: {queue.data.skipped}</Badge> : null}
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
          {queue.isPending ? t(lang, 'Загрузка…', 'Loading…') : t(lang, 'Всё доставлено', 'Everything delivered')}
        </div>
      ) : (
      /* Очередь бывает на сотню писем: она крутится внутри карточки, иначе
         настройки релея уезжают на второй экран. */
      <div style={{ maxHeight: 320, overflow: 'auto' }}>
      {rows.map((r) => (
        <div key={r.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 170px 120px', gap: 12, alignItems: 'center', padding: '8px 16px', borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
              {r.kind} · <span style={{ fontFamily: 'var(--font-mono)' }}>{r.subject}</span>
            </div>
            {r.error ? <div style={{ font: 'var(--font-caption)', color: 'var(--danger-ink)' }}>{r.error}</div> : null}
          </div>
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.to}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
            <Badge tone={r.state === 'failed' ? 'danger' : r.state === 'skipped' ? 'neutral' : 'warning'} size="sm">
              {r.state === 'failed' ? t(lang, 'отказ', 'failed') : r.state === 'skipped' ? t(lang, 'пропуск', 'skipped') : t(lang, 'ждёт', 'pending')}
            </Badge>
            {r.attempts ? <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>×{r.attempts}</span> : null}
          </div>
        </div>
      ))}
      </div>
      )}
    </div>)
}

/** Типы извещений в порядке, в каком они важны получателю. */
const NOTIFY_KINDS: { id: string; ru: string; en: string; note: [string, string] }[] = [
  { id: 'step-assigned', ru: 'Назначен шаг рассмотрения', en: 'Review step assigned',
    note: ['Приходит сразу, когда шаг открыт на вас или на вашу группу', 'Sent as soon as a step opens for you or your group'] },
  { id: 'trm-received', ru: 'Поступил трансмиттал', en: 'Transmittal received',
    note: ['Подача подрядчика — координатору ДО', 'Contractor submission — to the document controller'] },
  { id: 'crs-issued', ru: 'Выпущены замечания', en: 'Comments issued',
    note: ['Возвратный трансмиттал ушёл подрядчику', 'The return transmittal has gone to the contractor'] },
  { id: 'vendor-answered', ru: 'Подрядчик ответил', en: 'Contractor answered',
    note: ['Автору замечания — о поступившем ответе', 'To the comment author when an answer arrives'] },
  { id: 'step-due', ru: 'Истекает срок шага', en: 'Step is due',
    note: ['Сводкой раз в сутки, а не письмом на каждый шаг', 'A once-a-day digest, not a letter per step'] },
  { id: 'doc-overdue', ru: 'Документ просрочен', en: 'Document overdue',
    note: ['Сводкой раз в сутки владельцу реестра', 'A once-a-day digest to the register owner'] },
  /* Эскалация: шаг просрочен, и письмо идёт не исполнителю, а тому, кто ведёт
     срок пакета. Прежде её гасило собственное же напоминание исполнителю. */
  { id: 'step-overdue', ru: 'Шаг просрочен (эскалация)', en: 'Step overdue (escalation)',
    note: ['Координатору ДО: работу можно передать другому', 'To the document controller: the work can be handed over'] },
  /* Виды, заведённые позже: без строки здесь их нельзя было ни выключить, ни
     увидеть, что они вообще есть, — а письма по ним уходили. */
  { id: 'crs-due', ru: 'Истекает срок ответа на замечание', en: 'Comment response is due',
    note: ['Подрядчику — о замечаниях, по которым срок ответа на исходе', 'To the contractor about comments nearing their response deadline'] },
  { id: 'trm-acknowledged', ru: 'Получение подтверждено', en: 'Receipt acknowledged',
    note: ['Отправителю — о том, что пакет дошёл', 'To the sender when the package has arrived'] },
  { id: 'trm-accepted', ru: 'Пакет принят входным контролем', en: 'Package accepted at intake',
    note: ['Подрядчику — пакет ушёл на рассмотрение', 'To the contractor: the package went to review'] },
  { id: 'trm-rejected', ru: 'Пакет возвращён без рассмотрения', en: 'Package returned at intake',
    note: ['Подрядчику — с формальным основанием возврата', 'To the contractor with the formal reason'] },
  { id: 'trm-cancelled', ru: 'Трансмиттал аннулирован', en: 'Transmittal cancelled',
    note: ['Контрагенту — о письме, которого больше нет', 'To the counterparty about a letter that no longer stands'] },
]

/**
 * Свои извещения. Правятся ТОЛЬКО свои: адресата сервер берёт из токена, и
 * тела запроса с чужим именем не существует — иначе выключить извещения можно
 * было бы соседу.
 *
 * Колокольчик здесь не выключается: настройка про почту, а не про
 * осведомлённость внутри приложения.
 */
function NotifyPrefsBody({ lang }: { lang: Lang }) {
  const prefs = useSub<{ prefs: { kind: string; email: boolean }[] }>('/notify/prefs')
  const [draft, setDraft] = useState<Record<string, boolean> | null>(null)
  const save = useCommand<{ prefs: { kind: string; email: boolean }[] }>({
    method: 'PUT',
    path: () => '/notify/prefs',
    body: (a) => a.prefs,
    invalidate: () => [qk.sub('/notify/prefs')],
  })
  const current = draft ?? Object.fromEntries((prefs.data?.prefs ?? []).map((p) => [p.kind, p.email]))
  const dirty = draft != null

  if (prefs.isPending) return <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', color: 'var(--text-subtle)' }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div>
  if (prefs.isError) return <div style={{ padding: 24, font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{t(lang, 'Не удалось получить настройки', 'Failed to load settings')}</div>

  return (
    <div style={{ padding: 24, overflow: 'auto' }}>
      <div style={{ maxWidth: 760, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Извещения на почту', 'Email notifications')}</div>
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', marginTop: 4 }}>
            {t(lang, 'Уведомления в приложении приходят всегда — здесь настраивается только почта.',
                  'In-app notifications always arrive — this page configures email only.')}
          </div>
        </div>
        {NOTIFY_KINDS.map((k) => (
          /* Три колонки: флажок, название вида, пояснение. Во флексе название
             было голым текстовым узлом и сжималось пояснением до переноса по
             буквам — читалось как наложение. */
          <label key={k.id} htmlFor={'notify-' + k.id} style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 240px) minmax(0, 1fr)', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}>
            <input id={'notify-' + k.id} type="checkbox" checked={current[k.id] ?? true}
                   onChange={(e) => setDraft({ ...current, [k.id]: e.target.checked })} />
            <span style={{ minWidth: 0, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
              {t(lang, k.ru, k.en)}
            </span>
            <span style={{ minWidth: 0, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              {t(lang, k.note[0], k.note[1])}
            </span>
          </label>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px' }}>
          <Button variant="secondary" size="sm" disabled={!dirty} onClick={() => setDraft(null)}>
            {t(lang, 'Отменить', 'Cancel')}
          </Button>
          <Button variant="primary" size="sm" disabled={!dirty || save.isPending}
                  onClick={() => save.mutate(
                    { prefs: NOTIFY_KINDS.map((k) => ({ kind: k.id, email: current[k.id] ?? true })) },
                    { onSuccess: () => { setDraft(null); toast({ tone: 'success', title: t(lang, 'Настройки сохранены', 'Settings saved') }) } },
                  )}>
            {save.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить', 'Save')}
          </Button>
        </div>
      </div>
    </div>
  )
}

/* Контракты проекта.
   Контракт называет подрядчика и сроки, а реестр MDR заводится на контракт и
   наследует и то и другое — поэтому он сущность, а не строка справочника, и
   правится здесь, а не на вкладке «Справочники». Код контракта не меняется:
   на него ссылаются реестры и уже выпущенные номера. */
/* Параметры проекта: название, объект и сроки.

   Срок ответа на замечание и срок рассмотрения пакета были константами сборки:
   поменять «14 дней» на «10» мог только тот, у кого есть доступ к серверу и
   право перезапустить сервис. В договорах сроки разные. */
interface ProjectCard {
  id: string
  name: string
  plant: string
  'crs-due-days': number
  'trm-due-days': number
  /* Считать сроки рабочими днями (1) или календарными (0). */
  'due-work-days': number
  /* Обозначение ревизий: numeric — 00/01, alpha — A/B, free — как в штампе
     поставщика. Прежде принималось только число, и VDR со своими ревизиями
     падал построчно на импорте. */
  'revision-scheme': string
  /* Коды сторон переписки: стоят в номере документа и в номере трансмиттала
     (сегменты маски «Код проекта» и «Код заказчика»). Идентификатор бывает
     длинным («CMK-DP5»), а в номере нужен короткий код площадки. */
  code: string
  customerCode: string
}

/* Рабочий календарь проекта: праздники и перенесённые рабочие субботы.

   Сроки ставились «сегодня + N дней». Пакет, выпущенный в пятницу со сроком
   в два дня, был просрочен утром понедельника: суббота и воскресенье съедали
   весь срок, а отвечать в выходной некому. Хранятся только исключения —
   выходные по умолчанию суббота и воскресенье. */
interface CalendarDay {
  day: string
  working: boolean
  note: string | null
}

function WorkCalendarPanel({ lang }: { lang: Lang }) {
  const days = useSub<{ days: CalendarDay[] }>('/admin/calendar')
  const [day, setDay] = useState('')
  const [working, setWorking] = useState(false)
  const [note, setNote] = useState('')
  const invalidate = () => [qk.sub('/admin/calendar'), qk.all('audit')]
  const add = useCommand<{ day: string; working: boolean; note: string }>({
    method: 'PUT',
    path: () => '/admin/calendar',
    body: (a) => a,
    invalidate,
  })
  const drop = useCommand<{ day: string }>({
    method: 'DELETE',
    path: (a) => `/admin/calendar/${a.day}`,
    invalidate,
  })
  /* Год разом: по одному дню календарь вбивают полчаса и повторяют каждый
     декабрь, а незаполненный год молча считает 3 января рабочим. */
  const calFileRef = useRef<HTMLInputElement>(null)
  const bulk = useCommand<{ days: { day: string; working: boolean; note: string }[] }>({
    method: 'PUT',
    path: () => '/admin/calendar:bulk',
    body: (a) => a,
    invalidate,
  })
  /* Сервер принимает и отдаёт день по-разному: принимает ISO (LocalDate),
     показывает по-русски. Перевод — здесь, а не в двух местах. */
  const iso = (ru: string) => {
    const d = parseRu(ru)
    if (!d) return null
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  const chosen = iso(day)

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Рабочий календарь', 'Working calendar')}</div>
      <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
        {t(lang, 'Суббота и воскресенье нерабочие по умолчанию. Здесь перечисляются исключения: праздник среди недели и перенесённая рабочая суббота.',
                 'Saturday and Sunday are non-working by default. List the exceptions here: a holiday on a weekday and a moved working Saturday.')}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <div style={{ width: 150 }}><DateInput label={t(lang, 'День', 'Day')} value={day} onChange={setDay} lang={lang} /></div>
        <div style={{ width: 170 }}>
          <Select label={t(lang, 'Вид дня', 'Kind')} value={working ? 'work' : 'off'} onChange={(e) => setWorking(e.target.value === 'work')}>
            <option value="off">{t(lang, 'Нерабочий', 'Non-working')}</option>
            <option value="work">{t(lang, 'Рабочий', 'Working')}</option>
          </Select>
        </div>
        <div style={{ flex: 1 }}><Input label={t(lang, 'Примечание', 'Note')} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t(lang, 'напр. День Победы', 'e.g. Victory Day')} /></div>
        <Button variant="secondary" disabled={!chosen || add.isPending}
                title={chosen ? undefined : t(lang, 'Не выбран день', 'No day picked')}
                onClick={() => chosen && add.mutate({ day: chosen, working, note: note.trim() }, {
                  onSuccess: () => { setDay(''); setNote('') },
                  onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не сохранилось', 'Not saved'), text: e.message }),
                })}>{t(lang, 'Добавить', 'Add')}</Button>
      </div>
      {/* Год разом: по одному дню производственный календарь вбивают полчаса и
          повторяют это каждый декабрь. Файл — тот же список, что показывает
          экран: дата, вид дня, примечание. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input ref={calFileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
               onChange={(e) => {
                 const file = e.target.files?.[0]
                 e.target.value = ''
                 if (!file) return
                 parseDocs(file)
                   .then((sheet) => {
                     // Календарь читается тем же разбором листа, что и реестр:
                     // шапка ищется, а не считается первой строкой файла.
                     const days = sheet.rows.map((r) => {
                       const cells = Object.values(r).map((v) => String(v ?? '').trim())
                       const date = cells.find((v) => /^\d{2}[.]\d{2}[.]\d{4}$/.test(v)) ?? ''
                       const iso = date ? `${date.slice(6, 10)}-${date.slice(3, 5)}-${date.slice(0, 2)}` : ''
                       const kind = cells.join(' ').toLowerCase()
                       return { day: iso, working: kind.includes('рабоч') && !kind.includes('нерабоч'), note: cells[cells.length - 1] ?? '' }
                     }).filter((d) => d.day !== '')
                     if (!days.length) {
                       toast({ tone: 'danger', title: t(lang, 'Дней не нашлось', 'No days found'),
                         text: t(lang, 'Нужен столбец с датой в виде ДД.ММ.ГГГГ', 'A column with DD.MM.YYYY dates is required') })
                       return
                     }
                     bulk.mutate({ days }, {
                       onSuccess: () => toast({ tone: 'success', title: t(lang, 'Календарь загружен', 'Calendar loaded'), text: String(days.length) }),
                       onError: (err) => toast({ tone: 'danger', title: t(lang, 'Не загрузился', 'Not loaded'), text: err.message }),
                     })
                   })
                   .catch((err: unknown) => toast({ tone: 'danger', title: t(lang, 'Файл не прочитан', 'Cannot read the file'),
                                                    text: err instanceof Error ? err.message : String(err) }))
               }} />
        <Button variant="secondary" size="sm" iconLeft={<Icon name="upload" size={13} />} disabled={bulk.isPending}
                title={t(lang, 'Загрузить производственный календарь на год: дата, вид дня, примечание',
                              'Load a yearly working calendar: date, kind of day, note')}
                onClick={() => calFileRef.current?.click()}>
          {bulk.isPending ? t(lang, 'Загружаем…', 'Loading…') : t(lang, 'Загрузить год из файла', 'Load a year from a file')}
        </Button>
        <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Строка файла: дата ДД.ММ.ГГГГ, слово «рабочий» или «нерабочий», примечание',
                   'A file row: DD.MM.YYYY date, the word working or non-working, a note')}
        </span>
      </div>
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        {(days.data?.days ?? []).length === 0 ? (
          <div style={{ padding: 12, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
            {t(lang, 'Исключений нет: считаются только суббота и воскресенье.', 'No exceptions: only Saturday and Sunday count as non-working.')}
          </div>
        ) : (days.data?.days ?? []).map((r) => (
          <div key={r.day} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-strong)', width: 90 }}>{r.day}</span>
            <Badge tone={r.working ? 'success' : 'neutral'} size="sm">{r.working ? t(lang, 'рабочий', 'working') : t(lang, 'нерабочий', 'non-working')}</Badge>
            <span style={{ flex: 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{r.note ?? ''}</span>
            <Button variant="ghost" size="sm" iconLeft={<Icon name="trash-2" size={13} />} disabled={drop.isPending}
                    onClick={() => { const d = iso(r.day); if (d) drop.mutate({ day: d }) }}>{t(lang, 'Снять', 'Remove')}</Button>
          </div>
        ))}
      </div>
    </div>
  )
}


function ProjectSettingsBody({ lang }: { lang: Lang }) {
  const card = useSub<ProjectCard>('/admin/project')
  const [draft, setDraft] = useState<{ name: string; plant: string; crs: string; trm: string
                                      workDays: boolean; revisionScheme: string
                                      code: string; customerCode: string } | null>(null)
  const save = useCommand<{ name: string; plant: string; crsDueDays: number; trmDueDays: number
                           workDays: boolean; revisionScheme: string
                           code: string; customerCode: string }>({
    method: 'PUT',
    path: () => '/admin/project',
    body: (a) => a,
    invalidate: () => [qk.sub('/admin/project'), qk.all('audit')],
  })
  if (card.isError) return <ScreenError lang={lang} />
  if (card.isPending) return <ScreenLoading lang={lang} />

  const data = card.data
  const form = draft ?? {
    name: data?.name ?? '',
    plant: data?.plant ?? '',
    crs: String(data?.['crs-due-days'] ?? ''),
    trm: String(data?.['trm-due-days'] ?? ''),
    workDays: data?.['due-work-days'] === 1,
    revisionScheme: data?.['revision-scheme'] ?? 'numeric',
    code: data?.code ?? '',
    customerCode: data?.customerCode ?? '',
  }
  const set = (patch: Partial<typeof form>) => setDraft({ ...form, ...patch })
  const days = (v: string) => Number.parseInt(v, 10)
  /* Коды сторон уходят в номер и потому проверяются тем же набором знаков, что
     и сервер: пробел или косая черта дали бы номер, который не отправить и не
     найти. Пустыми они не сохраняются — сегмент маски без значения отказывает
     на каждом выпуске номера. */
  const codeOk = (v: string) => /^[A-ZА-Я0-9][A-ZА-Я0-9-]{1,31}$/.test(v.trim().toUpperCase())
  const ready = form.name.trim() !== '' && form.plant.trim() !== ''
    && days(form.crs) >= 1 && days(form.trm) >= 1
    && codeOk(form.code) && codeOk(form.customerCode)

  return (
    <div style={{ padding: 24, overflow: 'auto' }}>
      <div style={{ maxWidth: 620, display: 'grid', gap: 14 }}>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang, 'Идентификатор проекта не меняется: на него ссылаются документы, номера и токены доступа.',
                   'The project id never changes: documents, numbers and access tokens refer to it.')}
        </div>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
          {t(lang, 'Идентификатор', 'Id')}: <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-strong)' }}>{data?.id}</span>
        </div>
        <Input label={t(lang, 'Наименование проекта', 'Project name')} value={form.name}
               onChange={(e) => set({ name: e.target.value })} />
        <Input label={t(lang, 'Объект', 'Plant')} value={form.plant}
               onChange={(e) => set({ plant: e.target.value })} />
        {/* Коды сторон: ими собираются номер документа и номер трансмиттала.
            Прежде их не было на экране вовсе — они молча брались из
            идентификатора, и в номер вставал «CMK-DP5» вместо «DP5». */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label={t(lang, 'Код проекта', 'Project code')} mono value={form.code}
                 onChange={(e) => set({ code: e.target.value.toUpperCase() })}
                 hint={t(lang, 'Сегмент «Код проекта» в масках номеров', 'The “Project code” segment of number masks')} />
          <Input label={t(lang, 'Код заказчика', 'Customer code')} mono value={form.customerCode}
                 onChange={(e) => set({ customerCode: e.target.value.toUpperCase() })}
                 hint={t(lang, 'Сторона переписки в номере трансмиттала', 'The correspondence party in a transmittal number')} />
        </div>
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: -8 }}>
          {t(lang, 'Заглавные буквы, цифры и дефис. Меняются только вперёд: уже выпущенные номера не переписываются.',
                   'Capital letters, digits and hyphen. Changing them affects new numbers only; issued ones are left alone.')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label={t(lang, 'Срок ответа на замечание, дней', 'Response to a comment, days')} value={form.crs}
                 onChange={(e) => set({ crs: e.target.value })}
                 hint={t(lang, 'От него считается срок ACSR', 'The ACSR due date counts from it')} />
          <Input label={t(lang, 'Срок рассмотрения пакета, дней', 'Package review, days')} value={form.trm}
                 onChange={(e) => set({ trm: e.target.value })}
                 hint={t(lang, 'Применяется, если в матрице не задан свой норматив', 'Used unless the matrix sets its own')} />
        </div>
        {/* Обозначение ревизий: поставщик присылает VDR со своими (Rev. A,
            R1) — так они стоят в штампе чертежа и в его трансмиттале, и
            переименовывать чужой документ система не должна. */}
        <Select label={t(lang, 'Обозначение ревизий', 'Revision scheme')} value={form.revisionScheme}
                onChange={(e) => set({ revisionScheme: e.target.value })}>
          <option value="numeric">{t(lang, 'Числовое: 00, 01, 02', 'Numeric: 00, 01, 02')}</option>
          <option value="alpha">{t(lang, 'Буквенное: A, B, C', 'Alphabetic: A, B, C')}</option>
          <option value="free">{t(lang, 'Свободное: как в штампе (Rev. A, R1)', 'Free: as in the title block')}</option>
        </Select>
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: -8 }}>
          {t(lang, 'Схема применяется к новым ревизиям и к загрузке реестра. Уже заведённые обозначения не переписываются.',
                   'The scheme applies to new revisions and to register imports. Existing labels are left alone.')}
        </div>
        {/* Рабочие дни: пакет, выпущенный в пятницу со сроком в два дня, был
            просрочен утром понедельника — выходные съедали весь срок. */}
        <Checkbox checked={form.workDays} onChange={(e) => set({ workDays: e.target.checked })}
                  label={t(lang, 'Считать сроки в рабочих днях', 'Count due dates in working days')} />
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: -8 }}>
          {t(lang, 'Выходные и праздники не входят в срок. Исключения — ниже, в рабочем календаре.',
                   'Weekends and holidays do not count. Exceptions are in the working calendar below.')}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="primary" disabled={!ready || save.isPending || draft == null}
                  onClick={() => save.mutate({
                    name: form.name.trim(), plant: form.plant.trim(),
                    crsDueDays: days(form.crs), trmDueDays: days(form.trm), workDays: form.workDays,
                    revisionScheme: form.revisionScheme,
                    code: form.code.trim().toUpperCase(),
                    customerCode: form.customerCode.trim().toUpperCase(),
                  }, {
                    onSuccess: () => { setDraft(null); toast({ tone: 'success', title: t(lang, 'Параметры сохранены', 'Settings saved') }) },
                    onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не сохранилось', 'Not saved'), text: e.message }),
                  })}>
            {save.isPending ? t(lang, 'Сохраняем…', 'Saving…') : t(lang, 'Сохранить', 'Save')}
          </Button>
          {draft ? <Button variant="secondary" onClick={() => setDraft(null)}>{t(lang, 'Отменить правки', 'Discard')}</Button> : null}
        </div>
        <WorkCalendarPanel lang={lang} />
        <CountersPanel lang={lang} />
      </div>
    </div>)
}

const CONTRACT_INVALIDATE = [qk.meta('contracts'), qk.all('audit')]

function ContractsBody({ lang }: { lang: Lang }) {
  const contracts = useContracts()
  const [editing, setEditing] = useState<Contract | null>(null)
  const [dialog, setDialog] = useState(false)
  const cols: Column<Contract>[] = [
    { key: 'code', header: colHeader(lang, 'Номер'), mono: true, width: 120,
      render: (r) => <span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.code}</span> },
    { key: 'title', header: colHeader(lang, 'Наименование'),
      render: (r) => <span style={{ color: 'var(--text-body)' }}>{r.title}</span> },
    { key: 'vendor', header: colHeader(lang, 'Подрядчик'), width: 220,
      render: (r) => r.vendor
        ? <span style={{ color: 'var(--text-body)' }}>{r.vendor}</span>
        : <span style={{ color: 'var(--danger-ink)' }}>{t(lang, 'не указан', 'not set')}</span> },
    { key: 'dateStart', header: colHeader(lang, 'Начало'), mono: true, align: 'right', width: 100,
      render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.dateStart ?? '—'}</span> },
    { key: 'dateEnd', header: colHeader(lang, 'Окончание'), mono: true, align: 'right', width: 100,
      render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.dateEnd ?? '—'}</span> },
    /* Закрытие — конец работы по контракту, а не удаление: реестры и документы
       остаются, но новых реестров контракт больше не принимает. */
    { key: 'status', header: colHeader(lang, 'Состояние'), width: 150,
      render: (r) => <ContractStatus contract={r} lang={lang} /> },
  ]
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Toolbar>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang, 'Реестр MDR заводится на контракт: подрядчик и сроки берутся отсюда.',
                   'An MDR register is created against a contract: contractor and dates come from here.')}
        </div>
        <ToolSpacer />
        <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />}
                onClick={() => { setEditing(null); setDialog(true) }}>
          {t(lang, 'Добавить контракт', 'Add contract')}
        </Button>
      </Toolbar>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {contracts.length
          ? <DataTable columns={cols} rows={contracts} rowKey={(r) => r.code}
                       onRowClick={(r) => { setEditing(r); setDialog(true) }} />
          : <EmptyState title={t(lang, 'Контрактов ещё нет', 'No contracts yet')}
                        text={t(lang, 'Без контракта не завести реестр MDR: он определяет подрядчика и сроки представления документов.',
                                      'Without a contract there is no MDR register: it names the contractor and the submission window.')} />}
      </div>
      {dialog ? <ContractDialog open onClose={() => setDialog(false)} lang={lang} contract={editing} /> : null}
    </div>
  )
}

function ContractStatus({ contract, lang }: { contract: Contract; lang: Lang }) {
  const closed = contract.status === 'closed'
  const setStatus = useCommand<{ code: string; closed: boolean }>({
    path: (a) => `/contracts/${encodeURIComponent(a.code)}/status`,
    body: (a) => ({ closed: a.closed }),
    invalidate: () => CONTRACT_INVALIDATE,
  })
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}
         /* Строка открывает карточку контракта; кнопка состояния — не карточка. */
         onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
      <Badge tone={closed ? 'neutral' : 'success'} size="sm">{closed ? t(lang, 'Закрыт', 'Closed') : t(lang, 'Действует', 'Active')}</Badge>
      <Button variant="ghost" size="sm" disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ code: contract.code, closed: !closed }, {
                onError: (err) => toast({ tone: 'danger', title: t(lang, 'Не вышло', 'Failed'), text: err.message }),
              })}>
        {closed ? t(lang, 'Открыть', 'Reopen') : t(lang, 'Закрыть', 'Close')}
      </Button>
    </div>)
}

function ContractDialog({ open, onClose, lang, contract }: { open: boolean; onClose: () => void; lang: Lang; contract: Contract | null }) {
  const vendorsQuery = useList<Vendor>('vendors', { page: 0, pageSize: 200, sort: [{ id: 'code', desc: false }] })
  const vendors = vendorsQuery.data?.rows ?? []
  const [code, setCode] = useState(contract?.code ?? '')
  const [title, setTitle] = useState(contract?.title ?? '')
  const [vendor, setVendor] = useState(contract?.vendor ?? '')
  const [dateStart, setDateStart] = useState(contract?.dateStart ?? '')
  const [dateEnd, setDateEnd] = useState(contract?.dateEnd ?? '')
  /* Договорные сроки. Пусто — действует настройка проекта: сервер читает
     колонку контракта и падает на неё же. Ноль снимает срок обратно. */
  const [trmDue, setTrmDue] = useState(contract?.trmDueDays ? String(contract.trmDueDays) : '')
  const [crsDue, setCrsDue] = useState(contract?.crsDueDays ? String(contract.crsDueDays) : '')
  /* Подрядчик НЕ подставляется первой строкой справочника, в отличие от прочих
     форм: у контракта, перенесённого из справочника, его могло не быть, и
     тихая подстановка назначила бы работу организации, которую никто не
     выбирал. Пустой выбор виден и отказом кнопки, и подписью. */
  const vendorName = vendor

  type ContractArgs = { code: string; title: string; vendor: string; dateStart?: string; dateEnd?: string
                        trmDueDays?: number | null; crsDueDays?: number | null }
  const add = useCommand<ContractArgs>({
    path: () => '/contracts',
    body: (a) => a,
    invalidate: () => CONTRACT_INVALIDATE,
  })
  const patch = useCommand<ContractArgs>({
    method: 'PATCH',
    path: (a) => `/contracts/${encodeURIComponent(a.code)}`,
    body: (a) => ({ title: a.title, vendor: a.vendor, dateStart: a.dateStart, dateEnd: a.dateEnd,
                    trmDueDays: a.trmDueDays, crsDueDays: a.crsDueDays }),
    invalidate: () => CONTRACT_INVALIDATE,
  })
  const cmd = contract ? patch : add
  /* Срок в днях ничего не значит, пока не сказано, в каких: проект считает их
     рабочими или календарными, и «10» превращается в две недели или в полторы.
     Право то же, что у самого контракта (DICTS), — отказа здесь быть не может. */
  const project = useSub<ProjectCard>('/admin/project')
  const workDays = project.data?.['due-work-days'] === 1
  const byProject = (v: number | undefined) => (v ? t(lang, `по проекту: ${v}`, `project: ${v}`) : t(lang, 'по проекту', 'project default'))
  const ready = code.trim() !== '' && title.trim() !== '' && vendorName !== ''
  /* Очищенная дата уходит пустой строкой, а не пропущенным полем: JSON.stringify
     выбрасывает undefined, и сервер прочитал бы такой запрос как «поле не
     передано» — то есть «оставить как было». Снять ошибочный срок стало бы
     нечем, а диалог всё равно отчитался бы успехом. */
  /* Пустое поле срока уходит нулём, а не пропущенным: ноль означает «снять
     срок контракта и вернуться к настройке проекта», а пропуск сервер прочитал
     бы как «оставить как было» — снять ошибочный срок стало бы нечем.
     У создания смысла «как было» нет, и ноль там не «снять», а недопустимый
     срок: сервер отбивал «задаётся днями от 1 до 365, получено: 0» — то есть
     завести контракт с пустым сроком, как обещает плейсхолдер «по проекту»,
     было нельзя. Пустое поле при создании не передаётся вовсе. */
  const days = (value: string): number | null | undefined => {
    const n = Number.parseInt(value.trim(), 10)
    if (Number.isFinite(n) && n > 0) return n
    return contract ? 0 : undefined
  }
  const submit = () => cmd.mutate(
    { code: code.trim(), title: title.trim(), vendor: vendorName,
      dateStart: dateStart.trim(), dateEnd: dateEnd.trim(),
      trmDueDays: days(trmDue), crsDueDays: days(crsDue) },
    { onSuccess: () => { onClose(); toast({ tone: 'success', title: contract
        ? t(lang, 'Контракт изменён', 'Contract updated')
        : t(lang, 'Контракт добавлен', 'Contract added') }) } },
  )

  return (
    <Dialog open={open} onClose={onClose} sizeKey="contract"
            title={contract ? t(lang, 'Контракт', 'Contract') : t(lang, 'Новый контракт', 'New contract')}>
      <div style={{ width: 520, maxWidth: '100%', display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
          {/* Код не правится у существующего: на него ссылаются реестры. */}
          <Input label={t(lang, 'Номер', 'Number')} mono value={code} disabled={!!contract}
                 onChange={(e) => setCode(e.target.value)} placeholder="C-101" />
          <Input label={t(lang, 'Наименование', 'Title')} value={title}
                 onChange={(e) => setTitle(e.target.value)} />
        </div>
        <Select label={t(lang, 'Подрядчик', 'Contractor')} value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                hint={t(lang, 'Организация проекта — её документы придут по этому контракту. После создания реестра не меняется.',
                              'A project organisation — its documents arrive under this contract. Frozen once a register exists.')}>
          <option value="">{t(lang, '— не выбран —', '— none —')}</option>
          {vendors.map((v) => <option key={v.code} value={v.name}>{v.name}</option>)}
        </Select>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <DateInput label={t(lang, 'Начало', 'Start')} value={dateStart} onChange={setDateStart} lang={lang} />
          <DateInput label={t(lang, 'Окончание', 'End')} value={dateEnd} onChange={setDateEnd} lang={lang} />
        </div>
        {/* Сроки — предмет договора, а не настройка проекта: у генподрядчика
            четырнадцать дней, у поставщика десять. Пусто — по проекту. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label={t(lang, 'Срок рассмотрения пакета, дн', 'Package review, days')} value={trmDue}
                 onChange={(e) => setTrmDue(e.target.value)}
                 placeholder={byProject(project.data?.['trm-due-days'])} />
          <Input label={t(lang, 'Срок ответа на замечание, дн', 'Comment answer, days')} value={crsDue}
                 onChange={(e) => setCrsDue(e.target.value)}
                 placeholder={byProject(project.data?.['crs-due-days'])} />
        </div>
        {/* Сроки уже хранятся, но проверка плановых дат при загрузке реестра
            появится вместе с самой загрузкой. Обещать её сейчас — значит
            сказать, что система проверила то, чего не проверяла. */}
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {workDays
            ? t(lang, 'Дни считаются рабочими: выходные и праздники рабочего календаря проекта в срок не входят.',
                      'Days are working days: weekends and project calendar holidays do not count.')
            : t(lang, 'Дни считаются календарными: выходные и праздники входят в срок.',
                      'Days are calendar days: weekends and holidays count.')}
        </div>
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Сроки контракта: по ним будут проверяться плановые даты представления документов при загрузке реестра. Пустой срок проверку отключит.',
                   'Contract window: planned submission dates will be checked against it when a register is imported. An empty date will disable the check.')}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose} disabled={cmd.isPending}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={!ready || cmd.isPending}
                  title={ready ? undefined : t(lang, 'Заполните номер, наименование и подрядчика', 'Fill in the number, title and contractor')}>
            {cmd.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить', 'Save')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

/* Счётчики нумерации проекта.

   Проект переходит в систему на середине: трансмитталы выпускались в Excel до
   TRM-0120, замечания — до CRS-0450. Первый номер из системы обязан продолжить
   ряд, иначе он повторится в переписке. Назад счётчик не отматывается: выданный
   номер уже ушёл наружу. */
interface CounterRow {
  scope: string
  key: string
  value: number
}

const SCOPE_NAME: Record<string, [string, string]> = {
  transmittal: ['Трансмитталы', 'Transmittals'],
  comment: ['Замечания CRS', 'CRS comments'],
  crs: ['Замечания (старый счётчик)', 'Comments (legacy)'],
  crs_sheet: ['Листы CRS', 'CRS sheets'],
  mdr: ['Реестры MDR', 'MDR registers'],
  vdr: ['Реестры VDR', 'VDR registers'],
  document: ['Документы', 'Documents'],
  doc_file: ['Версии файлов', 'File versions'],
}

function CountersPanel({ lang }: { lang: Lang }) {
  const counters = useSub<{ rows: CounterRow[] }>('/admin/counters')
  const [draft, setDraft] = useState<Record<string, string>>({})
  const set = useCommand<{ scope: string; key: string; value: number }>({
    method: 'PUT',
    path: () => '/admin/counters',
    body: (a) => a,
    invalidate: () => [qk.sub('/admin/counters'), qk.all('audit')],
  })
  const rows = counters.data?.rows ?? []
  const slot = (r: CounterRow) => r.scope + ' ' + r.key
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Счётчики нумерации', 'Numbering counters')}</div>
      <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
        {t(lang, 'Значение — номер, выданный последним: следующий выпуск получит следующий за ним. Назад счётчик не отматывается — выданный номер уже ушёл в переписку.',
                 'The value is the last number issued: the next one continues the series. Counters never move back — an issued number is already out there.')}
      </div>
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        {rows.length === 0 ? (
          <div style={{ padding: 12, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
            {counters.isPending ? t(lang, 'Загрузка…', 'Loading…') : t(lang, 'Счётчики появятся после первого выпуска номера.', 'Counters appear after the first issued number.')}
          </div>
        ) : rows.map((r) => (
          <div key={slot(r)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
            <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', width: 240 }}>
              {t(lang, SCOPE_NAME[r.scope]?.[0] ?? r.scope, SCOPE_NAME[r.scope]?.[1] ?? r.scope)}
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-subtle)', flex: 1 }}>{r.key || '—'}</span>
            <div style={{ width: 120 }}>
              <Input size="sm" value={draft[slot(r)] ?? String(r.value)}
                     onChange={(e) => setDraft({ ...draft, [slot(r)]: e.target.value })} />
            </div>
            <Button variant="secondary" size="sm"
                    disabled={set.isPending || (draft[slot(r)] ?? String(r.value)) === String(r.value)}
                    onClick={() => {
                      const value = Number.parseInt(draft[slot(r)] ?? '', 10)
                      if (!Number.isFinite(value)) return
                      set.mutate({ scope: r.scope, key: r.key, value }, {
                        onSuccess: () => { setDraft({}); toast({ tone: 'success', title: t(lang, 'Счётчик установлен', 'Counter set'), text: `${r.scope} → ${value}` }) },
                        onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не установлен', 'Not set'), text: e.message }),
                      })
                    }}>{t(lang, 'Установить', 'Set')}</Button>
          </div>
        ))}
      </div>
    </div>
  )
}

/* Проектные атрибуты документа: колонки реестра заказчика, которых нет в схеме.

   Заказчик присылает MDR на сорок-шестьдесят колонок — тег оборудования,
   система и зона, позиция заказа, формат, гриф, номер задания. Файл
   загружался, в системе оседало два десятка полей, остальное исчезало молча,
   и ТДО вёл параллельный Excel, сшивая его с реестром по номеру документа.

   Заголовок атрибута — это заголовок КОЛОНКИ В ФАЙЛЕ: по нему импорт узнаёт
   столбец, поэтому он пишется так же, как у заказчика. */
function DocAttrsBody({ lang }: { lang: Lang }) {
  const defs = useDocAttrs()
  const { data: dicts } = useDicts()
  const [form, setForm] = useState({ code: '', title: '', kind: 'text', dict: '', ord: '0' })
  const invalidate = () => [qk.meta('doc-attrs'), qk.all('audit')]
  const define = useCommand<{ code: string; title: string; kind: string; dict: string; ord: number }>({
    path: () => '/doc-attrs',
    body: (a) => a,
    invalidate,
  })
  const drop = useCommand<{ code: string }>({
    method: 'DELETE',
    path: (a) => `/doc-attrs/${encodeURIComponent(a.code)}`,
    invalidate,
  })
  const ready = form.code.trim() !== '' && form.title.trim() !== ''
    && (form.kind !== 'list' || form.dict !== '')

  return (
    <div style={{ padding: 24, overflow: 'auto' }}>
      <div style={{ maxWidth: 860, display: 'grid', gap: 14 }}>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang, 'Атрибут — колонка реестра заказчика, которой нет в схеме. Заголовок пишется так же, как в присланном файле: по нему импорт узнаёт столбец, и колонка перестаёт теряться.',
                   'An attribute is a customer register column absent from the schema. Write the title exactly as in the received file: the import matches columns by it.')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 150px 180px 90px auto', gap: 10, alignItems: 'end' }}>
          <Input label={t(lang, 'Код', 'Code')} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                 placeholder="tag" hint={t(lang, 'латиницей', 'latin')} />
          <Input label={t(lang, 'Заголовок колонки', 'Column title')} value={form.title}
                 onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t(lang, 'Тег оборудования', 'Equipment tag')} />
          <Select label={t(lang, 'Вид', 'Kind')} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="text">{t(lang, 'Текст', 'Text')}</option>
            <option value="number">{t(lang, 'Число', 'Number')}</option>
            <option value="date">{t(lang, 'Дата', 'Date')}</option>
            <option value="list">{t(lang, 'Из справочника', 'From a dictionary')}</option>
          </Select>
          <Select label={t(lang, 'Справочник', 'Dictionary')} value={form.dict} disabled={form.kind !== 'list'}
                  onChange={(e) => setForm({ ...form, dict: e.target.value })}>
            <option value="">{t(lang, '— не выбран —', '— none —')}</option>
            {Object.entries((dicts ?? {}) as Record<string, { title: string }>).map(([code, d]) => <option key={code} value={code}>{d.title}</option>)}
          </Select>
          <Input label={t(lang, 'Порядок', 'Order')} value={form.ord} onChange={(e) => setForm({ ...form, ord: e.target.value })} />
          <Button variant="primary" disabled={!ready || define.isPending}
                  title={ready ? undefined : t(lang, 'Нужны код и заголовок', 'Code and title are required')}
                  onClick={() => define.mutate({ ...form, ord: Number.parseInt(form.ord, 10) || 0 }, {
                    onSuccess: () => { setForm({ code: '', title: '', kind: 'text', dict: '', ord: '0' })
                                       toast({ tone: 'success', title: t(lang, 'Атрибут заведён', 'Attribute added') }) },
                    onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не заведён', 'Not added'), text: e.message }),
                  })}>{t(lang, 'Завести', 'Add')}</Button>
        </div>
        <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          {defs.length === 0 ? (
            <div style={{ padding: 12, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              {t(lang, 'Атрибутов нет: колонки файла, кроме известных системе, при загрузке отбрасываются.',
                       'No attributes: any column beyond the known ones is dropped on import.')}
            </div>
          ) : defs.map((a) => (
            <div key={a.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-strong)', width: 140 }}>{a.code}</span>
              <span style={{ flex: 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{a.title}</span>
              <Badge tone="neutral" size="sm">{a.kind === 'list'
                ? ((dicts as Record<string, { title: string }> | undefined)?.[a.dict ?? '']?.title ?? a.dict)
                : a.kind}</Badge>
              <Button variant="ghost" size="sm" iconLeft={<Icon name="trash-2" size={13} />} disabled={drop.isPending}
                      title={t(lang, 'Снять атрибут. Значения останутся в истории документов.', 'Remove the attribute. Values stay in document history.')}
                      onClick={() => drop.mutate({ code: a.code })}>{t(lang, 'Снять', 'Remove')}</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function DictsBody({ lang }: { lang: Lang }) {
  const { data: dicts } = useDicts()
  /* Выбор хранится отдельно от данных: до ответа сервера ключей ещё нет и
     начальное значение брать неоткуда. Пустая строка — «первый по списку». */
  const [sel, setSel] = useState<DictKey | ''>('')
  /* Что редактируется: 'new' — добавление, строка — правка, null — диалог закрыт. */
  const [editing, setEditing] = useState<DictRow | 'new' | null>(null)
  /* Удаление не всегда удаляет: ссылаемый код сервер снимает с обращения и
     сообщает об этом в ответе. Промолчать значило бы соврать пользователю,
     что строка исчезла, — поэтому исход показывается тостом. */
  const removeRow = useCommand<{ dict: string; code: string }, DictRemoveResult>({
    method: 'DELETE',
    path: (a) => `/dicts/${encodeURIComponent(a.dict)}/${encodeURIComponent(a.code)}`,
    invalidate: () => DICT_INVALIDATE,
  })
  /* Отчёт загрузки. Показывается всегда, а не только при отказах: человек
     прислал файл и обязан узнать, что легло, что изменилось и что отклонено. */
  const [report, setReport] = useState<DictImportResult | null>(null)
  const [reading, setReading] = useState(false)
  /* Свой справочник проекта (V65): системные заводит сборка, эти — администратор. */
  const [newDict, setNewDict] = useState(false)
  /* Поиск по строкам: объектов и титулов на проекте сотни, и листать их
     колесом — единственный способ найти строку, пока нет этого поля. */
  const [rowQ, setRowQ] = useState('')
  const [dictForm, setDictForm] = useState({ code: '', title: '' })
  const addDict = useCommand<{ code: string; title: string }>({
    path: () => '/dicts',
    body: (a) => a,
    invalidate: () => DICT_INVALIDATE,
  })
  const fileRef = useRef<HTMLInputElement>(null)
  const importRows = useCommand<{ rows: DictImportRow[] }, DictImportResult>({
    path: () => '/dicts:import',
    body: (a) => ({ rows: a.rows }),
    invalidate: () => DICT_INVALIDATE,
  })
  const keys = dicts ? (Object.keys(dicts) as DictKey[]) : []
  const cur = sel || keys[0]
  const dict = cur ? dicts?.[cur] : undefined
  if (!dicts || !cur || !dict) return null
  const extra: Partial<SmartColumn<Vendor>> | null = cur === 'titul' ? { key: 'area', header: t(lang, 'Объект', 'Area') } : null
  /* Смысл кода — не подпись, а поведение: по нему документооборот решает
     судьбу документа. В таблице его не было, и админ видел A/B/C, не зная,
     какой из них закрывает круг. */
  const meaningLabel = (m: string | null) =>
    m === 'afd' ? t(lang, 'Принят — в работу (AFD)', 'Accepted — released (AFD)')
      : m === 'approved' ? t(lang, 'Принят с замечаниями', 'Accepted with comments')
      : m === 'reissue' ? t(lang, 'Не принят — переиздать', 'Not accepted — reissue')
      : '—'
  const needle = rowQ.trim().toLowerCase()
  const rows = needle
    ? dict.rows.filter((r) => (r.code + ' ' + r.name).toLowerCase().includes(needle))
    : dict.rows
  /* Разбор файла — на клиенте, проверки — на сервере: уникальность кода и
     ссылки знает он, и повторять их здесь значило бы иметь два ответа. */
  const runImport = async (file: File) => {
    setReading(true)
    try {
      const rows = await parseDicts(file)
      if (!rows.length) {
        toast({ tone: 'info', title: t(lang, 'В файле нет строк', 'No rows in the file'),
                text: t(lang, 'Ожидается лист на справочник с колонками «Код» и «Наименование».',
                              'A sheet per dictionary with «Code» and «Name» columns is expected.') })
        return
      }
      importRows.mutate({ rows }, { onSuccess: setReport })
    } catch (e) {
      toast({ tone: 'danger', title: t(lang, 'Файл не прочитан', 'Failed to read the file'),
              text: e instanceof Error ? e.message : String(e) })
    } finally {
      setReading(false)
    }
  }
  const remove = (code: string) => removeRow.mutate({ dict: cur, code }, {
    onSuccess: (res) => toast(res.deactivated
      ? {
          tone: 'info',
          title: t(lang, 'Код снят с обращения', 'Code deactivated'),
          text: t(lang, `На «${code}» ссылаются данные (${res.uses}), поэтому код не удалён, а помечен неактивным.`,
                       `«${code}» is referenced (${res.uses}), so it was deactivated instead of deleted.`),
        }
      : {
          tone: 'success',
          title: t(lang, 'Строка удалена', 'Row deleted'),
          text: t(lang, `Код «${code}» удалён: ссылок на него нет.`, `«${code}» deleted: nothing referenced it.`),
        }),
  })
  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <div style={{ width: railWidth(260), flex: '0 0 auto', borderRight: '1px solid var(--border-default)', background: 'var(--surface-card)', overflow: 'auto', padding: '8px' }}>
        <div style={{ padding: '8px 10px 6px', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{t(lang, 'Справочники системы', 'Dictionaries')}</div>
        {keys.map((k) => {
          const on = k === cur
          return (
            <button key={k} onClick={() => { setSel(k); setRowQ('') }} className="tdms-rail" style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '8px 10px', marginBottom: 1, border: 0, borderRadius: 'var(--radius-sm)', cursor: 'pointer', textAlign: 'left', background: on ? 'var(--accent-soft)' : 'transparent', boxShadow: on ? 'inset 2px 0 0 var(--accent)' : 'none', color: on ? 'var(--accent-ink)' : 'var(--text-muted)', font: 'var(--font-ui)', fontSize: 13, fontWeight: on ? 600 : 400 }}>
              <CIcon name="book-marked" size={15} color={on ? 'var(--accent-ink)' : 'var(--text-subtle)'} /><span style={{ flex: 1 }}>{dicts[k].title}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-subtle)' }}>{dicts[k].rows.length}</span>
            </button>
          )
        })}
      </div>
      {newDict ? (
        <Dialog open onClose={() => setNewDict(false)} width={460} title={t(lang, 'Завести справочник проекта', 'New project dictionary')}>
          <div style={{ width: 420, maxWidth: '100%', display: 'grid', gap: 12 }}>
            <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              {t(lang, 'Свой справочник нужен атрибуту-списку: «Системы установки», «Гриф», «Формат листа». Системные справочники разбирает сам документооборот, и их набор не меняется.',
                       'A project dictionary feeds a list attribute. System dictionaries are parsed by the workflow itself and cannot be added to.')}
            </div>
            <Input label={t(lang, 'Код', 'Code')} value={dictForm.code} onChange={(e) => setDictForm({ ...dictForm, code: e.target.value })} placeholder="system" />
            <Input label={t(lang, 'Название', 'Title')} value={dictForm.title} onChange={(e) => setDictForm({ ...dictForm, title: e.target.value })} placeholder={t(lang, 'Системы установки', 'Plant systems')} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="secondary" onClick={() => setNewDict(false)}>{t(lang, 'Отмена', 'Cancel')}</Button>
              <Button variant="primary" disabled={dictForm.code.trim() === '' || dictForm.title.trim() === '' || addDict.isPending}
                      onClick={() => addDict.mutate({ code: dictForm.code.trim(), title: dictForm.title.trim() }, {
                        onSuccess: () => { setNewDict(false); setDictForm({ code: '', title: '' })
                                           toast({ tone: 'success', title: t(lang, 'Справочник заведён', 'Dictionary added') }) },
                        onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не заведён', 'Not added'), text: e.message }),
                      })}>{t(lang, 'Завести', 'Add')}</Button>
            </div>
          </div>
        </Dialog>
      ) : null}
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-card)' }}>
          <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{dict.title}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>
            {needle ? rows.length + ' / ' + dict.rows.length : dict.rows.length} {t(lang, 'записей', 'entries')}
          </span>
          <div style={{ width: 240 }}>
            <Input size="sm" value={rowQ} onChange={(e) => setRowQ(e.target.value)}
                   leading={<Icon name="search" size={14} />}
                   placeholder={t(lang, 'Код или наименование…', 'Code or name…')} />
          </div>
          {/* Выгрузка — все справочники одним файлом, лист на справочник:
              заполняют его целиком, а не по одному. Загрузка идёт тем же
              файлом, поэтому имя листа — код справочника. */}
          {/* Свой справочник проекта: набор был закрыт девятью системными, и
              завести «Системы установки» или «Гриф» было нечем — а атрибут-
              список без справочника не заведёшь. */}
          <Button variant="secondary" size="sm" iconLeft={<Icon name="plus" size={13} />} style={{ marginLeft: 'auto' }}
                  onClick={() => setNewDict(true)}>{t(lang, 'Завести справочник', 'Add a dictionary')}</Button>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="download" size={13} />}
                  onClick={() => {
                    exportDicts(keys.map((k) => ({ key: k, title: dicts[k].title, rows: dicts[k].rows })), 'dictionaries.xlsx', lang)
                    noteExport('справочники проекта', undefined, keys.length)
                  }}>
            {t(lang, 'Выгрузить все в Excel', 'Export all to Excel')}
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) void runImport(f); e.target.value = '' }} />
          <Button variant="secondary" size="sm" iconLeft={<Icon name="upload" size={13} />}
                  disabled={reading || importRows.isPending} onClick={() => fileRef.current?.click()}>
            {reading || importRows.isPending ? t(lang, 'Загрузка…', 'Loading…') : t(lang, 'Загрузить Excel', 'Import Excel')}
          </Button>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="plus" size={13} />} onClick={() => setEditing('new')}>{t(lang, 'Добавить', 'Add')}</Button>
        </div>
        {cur === 'purpose' ? (
          <PurposeSchemePanel lang={lang} scheme={dict.scheme ?? []} purposeRows={dict.rows} codeRows={dicts.code.rows} />
        ) : null}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...thS(), width: 110 }}>{dict.code}</th>
              <th style={thS()}>{dict.name}</th>
              {extra ? <th style={{ ...thS(), width: 220 }}>{extra.header}</th> : null}
              {cur === 'code' ? <th style={{ ...thS(), width: 220 }}>{t(lang, 'Смысл', 'Meaning')}</th> : null}
              <th style={{ ...thS(), width: 90, textAlign: 'center' }}>{t(lang, 'Активна', 'Active')}</th>
              <th style={{ ...thS(), width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code} className="lav-tr" style={{ height: 42 }}>
                <td style={tdS()}><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>{r.code}</span></td>
                <td style={{ ...tdS(), font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{r.name}</td>
                {extra ? <td style={{ ...tdS(), font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{extra.key === 'vendor' ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><Avatar name={r.vendor} size={20} />{r.vendor}</span> : (dicts.area.rows.find((a) => a.code === r.area) ? r.area + ' — ' + dicts.area.rows.find((a) => a.code === r.area)?.name : r.area)}</td> : null}
                {cur === 'code' ? <td style={{ ...tdS(), font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: r.meaning ? 'var(--text-muted)' : 'var(--text-subtle)' }}>{meaningLabel(r.meaning ?? null)}</td> : null}
                <td style={{ ...tdS(), textAlign: 'center' }}>{r.active ? <Badge tone="success" size="sm" dot>да</Badge> : <Badge tone="neutral" size="sm" dot>нет</Badge>}</td>
                <td style={{ ...tdS(), textAlign: 'right' }}><span style={{ display: 'inline-flex', gap: 2 }}>
                  <IconBtn icon="pencil" disabled={removeRow.isPending} onClick={() => setEditing(r)} />
                  {/* Кнопка гаснет на время команды: второй клик отправил бы
                      удаление ещё раз, уже по обновлённому состоянию. */}
                  <IconBtn icon="trash-2" danger disabled={removeRow.isPending} onClick={() => remove(r.code)} />
                </span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {report ? <DictImportReport lang={lang} result={report} onClose={() => setReport(null)} /> : null}
      {editing ? (
        <DictRowEditor
          lang={lang}
          dictKey={cur}
          dict={dict}
          row={editing === 'new' ? null : editing}
          field={extra ? (extra.key as 'vendor' | 'area') : null}
          areaRows={dicts.area.rows}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  )
}

/* ==== Схема назначений (V97) ============================================ */

/** Правило схемы в черновике панели: '' в target значит «не выбрано». */
interface SchemeDraft { from: string; meaning: 'start' | 'afd' | 'approved' | 'reissue'; target: string }

/** Цель правила кодируется одной строкой: назначение либо финал. */
const SCHEME_FINAL = '__final'

const SCHEME_MEANING_COLOR: Record<string, string> = { afd: 'var(--code-1)', approved: 'var(--code-2)', reissue: 'var(--code-3)' }

/**
 * Схема переходов назначений: таблица правил и живой граф.
 *
 * <p>Правила говорят, куда документ уходит после кода рассмотрения: IFR (A) →
 * AFD; IFR (B) → IFA; финал — «Согласован». Панель шлёт схему целиком
 * (PUT /dicts/purpose/scheme), как маршрут матрицы: правка отдельной строки в
 * связном наборе смысла не имеет.
 */
function PurposeSchemePanel({ lang, scheme, purposeRows, codeRows }: {
  lang: Lang
  scheme: PurposeSchemeRule[]
  purposeRows: DictRow[]
  codeRows: DictRow[]
}) {
  const toDraft = (rules: PurposeSchemeRule[]): SchemeDraft[] =>
    rules.map((r) => ({ from: r.from, meaning: r.meaning, target: r.final ? SCHEME_FINAL : (r.to ?? '') }))
  const [draft, setDraft] = useState<SchemeDraft[] | null>(null)
  const rows = draft ?? toDraft(scheme)
  const dirty = draft != null
  const save = useCommand<{ rules: SchemeDraft[] }>({
    method: 'PUT',
    path: () => '/dicts/purpose/scheme',
    body: (a) => a.rules.map((r) => r.target === SCHEME_FINAL
      ? { from: r.from, meaning: r.meaning, final: true }
      : { from: r.from, meaning: r.meaning, to: r.target, final: false }),
    invalidate: () => DICT_INVALIDATE,
  })
  /* Буквы кодов проекта по смыслу: правило хранит смысл (V71), а человек
     думает буквами — «IFR (A) → AFD». */
  const letters = (meaning: string) => {
    const own = codeRows.filter((r) => r.active && r.meaning === meaning).map((r) => r.code)
    return own.length ? own.join('/') : meaning
  }
  const meaningTitle = (m: string) =>
    m === 'afd' ? t(lang, 'принято — в работу', 'accepted — released')
      : m === 'approved' ? t(lang, 'принято с замечаниями', 'accepted with comments')
      : m === 'reissue' ? t(lang, 'не принято', 'not accepted') : m
  const purposes = purposeRows.filter((r) => r.active)
  const edit = (i: number, patch: Partial<SchemeDraft>) => {
    const next = rows.map((r, k) => (k === i ? { ...r, ...patch } : r))
    setDraft(next)
  }
  const complete = rows.every((r) => (r.meaning === 'start' ? r.target !== '' && r.target !== SCHEME_FINAL : r.from !== '' && r.target !== ''))
  return (
    <div style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px 0' }}>
        <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Схема переходов', 'Purpose flow scheme')}
        </span>
        <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'назначение следующей подачи диктуется кодом прошлого круга; финал даёт статус «Согласован»',
                   'the next submission purpose follows the previous round code; the final rule grants «Approved»')}
        </span>
        {dirty ? (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 8 }}>
            <Button variant="secondary" size="sm" onClick={() => setDraft(null)}>{t(lang, 'Отменить', 'Discard')}</Button>
            <Button variant="primary" size="sm" disabled={!complete || save.isPending}
                    title={complete ? undefined : t(lang, 'У каждого правила нужны исходное назначение и цель', 'Every rule needs a source and a target')}
                    onClick={() => save.mutate({ rules: rows }, {
                      onSuccess: () => { setDraft(null); toast({ tone: 'success', title: t(lang, 'Схема сохранена', 'Scheme saved') }) },
                      onError: (e) => toast({ tone: 'danger', title: t(lang, 'Схема не сохранена', 'Scheme not saved'), text: e.message }),
                    })}>{save.isPending ? t(lang, 'Сохраняем…', 'Saving…') : t(lang, 'Сохранить схему', 'Save scheme')}</Button>
          </span>
        ) : null}
      </div>
      <SchemeGraph lang={lang} rows={rows} letters={letters} />
      <div style={{ padding: '0 18px 12px' }}>
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={{ padding: '3px 8px 3px 0' }}>
                  {r.meaning === 'start' ? (
                    <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{t(lang, 'Старт — первая подача', 'Start — first submission')}</span>
                  ) : (
                    <Select size="sm" block={false} value={r.from} onChange={(e) => edit(i, { from: e.target.value })}>
                      <option value="">{t(lang, '— откуда —', '— from —')}</option>
                      {purposes.map((p) => <option key={p.code} value={p.code}>{p.code}</option>)}
                    </Select>
                  )}
                </td>
                <td style={{ padding: '3px 8px 3px 0' }}>
                  {r.meaning === 'start' ? null : (
                    <span title={meaningTitle(r.meaning)} style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: SCHEME_MEANING_COLOR[r.meaning] ?? 'var(--text-muted)' }}>
                      ({letters(r.meaning)})
                    </span>
                  )}
                </td>
                <td style={{ padding: '3px 8px 3px 0', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-subtle)' }}>→</td>
                <td style={{ padding: '3px 8px 3px 0' }}>
                  <Select size="sm" block={false} value={r.target} onChange={(e) => edit(i, { target: e.target.value })}>
                    <option value="">{t(lang, '— куда —', '— to —')}</option>
                    {purposes.map((p) => <option key={p.code} value={p.code}>{p.code}</option>)}
                    {r.meaning === 'start' ? null : <option value={SCHEME_FINAL}>{t(lang, '✓ Согласован (финал)', '✓ Approved (final)')}</option>}
                  </Select>
                </td>
                <td style={{ padding: '3px 0' }}>
                  {r.meaning === 'start' ? null : <IconBtn icon="trash-2" danger onClick={() => setDraft(rows.filter((_, k) => k !== i))} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="plus" size={13} />}
                  onClick={() => setDraft([...rows, { from: '', meaning: 'afd', target: '' }])}>
            {t(lang, 'Правило', 'Add rule')}
          </Button>
          {rows.some((r) => r.meaning === 'start') ? null : (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="plus" size={13} />}
                    onClick={() => setDraft([{ from: '', meaning: 'start', target: '' }, ...rows])}>
              {t(lang, 'Строка старта', 'Add start rule')}
            </Button>
          )}
          {rows.some((r) => r.meaning !== 'start') ? (
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', alignSelf: 'center' }}>
              {t(lang, 'у пары «откуда + код» правило одно; смысл кода меняется кнопкой правила',
                       'one rule per «from + code» pair; pick the code meaning per rule')}
            </span>
          ) : null}
        </div>
        {/* Смысл нового правила: выбирается тут же, третьей колонкой не место. */}
        {dirty ? (
          <div style={{ display: 'flex', gap: 10, marginTop: 6, alignItems: 'center' }}>
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'Смысл кода у последнего правила:', 'Last rule code meaning:')}</span>
            {(['afd', 'approved', 'reissue'] as const).map((m) => {
              const last = rows.length - 1
              const on = rows[last]?.meaning === m
              return (
                <button key={m} onClick={() => edit(last, { meaning: m })}
                        style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '2px 8px', cursor: 'pointer', background: on ? 'var(--accent-soft)' : 'transparent', fontFamily: 'var(--font-mono)', fontSize: 11.5, color: SCHEME_MEANING_COLOR[m] }}>
                  {letters(m)} · {meaningTitle(m)}
                </button>
              )
            })}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** Раскладка графа схемы — личная настройка рабочего места, как ширина колонок. */
const SCHEME_LAYOUT_KEY = 'tdms_purpose_scheme_layout_v1'

/**
 * Граф схемы: узлы-назначения от старта к финалу «Согласован». Рёбра подписаны
 * буквами кодов и покрашены их смыслом, как фишка кода.
 *
 * <p>Узлы ЖИВЫЕ: их можно перетаскивать — автоматическая раскладка в один ряд
 * годится трём назначениям, а схема заказчика бывает и ветвистой. Раскладка
 * переживает перезагрузку (localStorage) и сбрасывается кнопкой.
 */
function SchemeGraph({ lang, rows, letters }: { lang: Lang; rows: SchemeDraft[]; letters: (m: string) => string }) {
  const [layout, setLayout] = useState<Record<string, { x: number; y: number }>>(() => {
    try { return JSON.parse(localStorage.getItem(SCHEME_LAYOUT_KEY) ?? '{}') as Record<string, { x: number; y: number }> } catch { return {} }
  })
  /* Последняя раскладка в ref: сохранение на pointerup читало бы состояние
     прошлого рендера и теряло последние пиксели тяги. */
  const latest = useRef(layout)
  latest.current = layout
  const drag = useRef<{ node: string; dx: number; dy: number } | null>(null)

  const rules = rows.filter((r) => r.meaning === 'start' ? r.target && r.target !== SCHEME_FINAL : r.from && r.target)
  if (!rules.length) {
    return (
      <div style={{ padding: '10px 18px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
        {t(lang, 'Схема не ведётся: назначения свободны. Добавьте строку старта и правила переходов.',
                 'No scheme: purposes are unrestricted. Add a start rule and transitions.')}
      </div>
    )
  }
  /* Порядок узлов — обходом от старта: так IFR встаёт перед IFA, IFA перед AFD. */
  const order: string[] = []
  const push = (p: string) => { if (p && p !== SCHEME_FINAL && !order.includes(p)) order.push(p) }
  for (const r of rules) if (r.meaning === 'start') push(r.target)
  for (let i = 0; i < order.length; i++) {
    for (const r of rules) if (r.from === order[i] && r.target !== SCHEME_FINAL) push(r.target)
  }
  for (const r of rules) if (r.meaning !== 'start') push(r.from)
  const hasFinal = rules.some((r) => r.target === SCHEME_FINAL)
  const nodes = ['__start', ...order, ...(hasFinal ? [SCHEME_FINAL] : [])]
  const STEP = 170
  const NODE_W = 92
  const NODE_H = 30
  const BASE = 92
  const wOf = (n: string) => (n === SCHEME_FINAL ? NODE_W + 18 : n === '__start' ? 26 : NODE_W)
  /* Центр узла: перетащенный — из раскладки, остальные — автоматом в ряд. */
  const at = (n: string): { x: number; y: number } =>
    layout[n] ?? { x: 30 + nodes.indexOf(n) * STEP + wOf(n) / 2, y: BASE }
  const width = Math.max(30 + nodes.length * STEP,
    ...nodes.map((n) => at(n).x + wOf(n) / 2 + 40))
  const height = Math.max(168, ...nodes.map((n) => at(n).y + NODE_H + 40))
  const custom = nodes.some((n) => layout[n] != null)

  const startDrag = (node: string, e: React.PointerEvent<SVGGElement>) => {
    const p = at(node)
    drag.current = { node, dx: p.x - e.clientX, dy: p.y - e.clientY }
    e.currentTarget.setPointerCapture(e.pointerId)
    e.preventDefault()
  }
  const moveDrag = (e: React.PointerEvent<SVGGElement>) => {
    const d = drag.current
    if (!d) return
    setLayout((l) => {
      const next = { ...l, [d.node]: {
        x: Math.max(20, e.clientX + d.dx),
        y: Math.max(24, Math.min(560, e.clientY + d.dy)),
      } }
      latest.current = next
      return next
    })
  }
  const endDrag = (e: React.PointerEvent<SVGGElement>) => {
    if (!drag.current) return
    drag.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
    try { localStorage.setItem(SCHEME_LAYOUT_KEY, JSON.stringify(latest.current)) } catch { /* личная настройка, не данные */ }
  }
  const resetLayout = () => {
    setLayout({})
    try { localStorage.removeItem(SCHEME_LAYOUT_KEY) } catch { /* ignore */ }
  }

  /* Смещение параллельных рёбер между одной парой узлов, чтобы подписи не легли друг на друга. */
  const seen = new Map<string, number>()
  const edges = rules.map((r) => {
    const a = r.meaning === 'start' ? '__start' : r.from
    const b = r.target
    const key = a + '→' + b
    const k = seen.get(key) ?? 0
    seen.set(key, k + 1)
    return { a, b, meaning: r.meaning, k }
  })
  /* Конец ребра подрезается у границы узла: стрелка в центр пряталась бы под
     плашкой. Подрезка — по эллипсу вокруг узла, этого глазу достаточно. */
  const clip = (from: { x: number; y: number }, to: { x: number; y: number }, n: string) => {
    const vx = from.x - to.x
    const vy = from.y - to.y
    const len = Math.hypot(vx, vy) || 1
    const rx = wOf(n) / 2 + 6
    const ry = NODE_H / 2 + 6
    /* Радиус эллипса вдоль направления: r = 1/sqrt((cosφ/rx)² + (sinφ/ry)²). */
    const ux = vx / len
    const uy = vy / len
    const r = 1 / Math.sqrt((ux / rx) * (ux / rx) + (uy / ry) * (uy / ry))
    return { x: to.x + ux * r, y: to.y + uy * r }
  }
  return (
    <div style={{ position: 'relative', overflow: 'auto', padding: '6px 18px 2px' }}>
      {custom ? (
        <Button variant="ghost" size="sm" style={{ position: 'absolute', top: 4, right: 18, zIndex: 1 }}
                onClick={resetLayout}>{t(lang, 'Сбросить раскладку', 'Reset layout')}</Button>
      ) : null}
      <svg width={width} height={height} style={{ display: 'block' }}>
        <defs>
          <marker id="scheme-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L8,4 L0,8 z" fill="var(--text-subtle)" />
          </marker>
        </defs>
        {edges.map((e, i) => {
          const color = e.meaning === 'start' ? 'var(--text-subtle)' : (SCHEME_MEANING_COLOR[e.meaning] ?? 'var(--text-subtle)')
          const label = e.meaning === 'start' ? t(lang, 'старт', 'start') : letters(e.meaning)
          const pa = at(e.a)
          if (e.a === e.b) {
            /* Петля «остаёмся на месте»: дуга над узлом. */
            const top = pa.y - NODE_H / 2
            const lift = 30 + e.k * 18
            return (
              <g key={i}>
                <path d={`M ${pa.x - 16} ${top} C ${pa.x - 26} ${top - lift}, ${pa.x + 26} ${top - lift}, ${pa.x + 16} ${top}`}
                      fill="none" stroke={color} strokeWidth={1.6} markerEnd="url(#scheme-arrow)" />
                <text x={pa.x} y={top - lift + 8} textAnchor="middle" fontFamily="var(--font-mono)" fontSize="10.5" fontWeight="600" fill={color}>{label}</text>
              </g>
            )
          }
          const pb = at(e.b)
          const from = clip(pb, pa, e.a)
          const to = clip(pa, pb, e.b)
          /* Изгиб — перпендикуляром к ребру; параллельные рёбра расходятся
             своим смещением, обратные (b левее a) выгибаются вниз. */
          const mx = (from.x + to.x) / 2
          const my = (from.y + to.y) / 2
          const ex = to.x - from.x
          const ey = to.y - from.y
          const elen = Math.hypot(ex, ey) || 1
          const sign = to.x >= from.x ? -1 : 1
          const bend = (18 + e.k * 16) * sign
          const cx = mx + (-ey / elen) * bend
          const cy = my + (ex / elen) * bend
          // Точка подписи — на самой кривой (t = 0.5 квадратичной Безье).
          const lx = 0.25 * from.x + 0.5 * cx + 0.25 * to.x
          const ly = 0.25 * from.y + 0.5 * cy + 0.25 * to.y
          return (
            <g key={i}>
              <path d={`M ${from.x} ${from.y} Q ${cx} ${cy}, ${to.x} ${to.y}`}
                    fill="none" stroke={color} strokeWidth={1.6} markerEnd="url(#scheme-arrow)" />
              <text x={lx} y={ly + (sign < 0 ? -4 : 11)} textAnchor="middle" fontFamily="var(--font-mono)" fontSize="10.5" fontWeight="600" fill={color}>{label}</text>
            </g>
          )
        })}
        {nodes.map((n) => {
          const p = at(n)
          const grab = { cursor: 'grab' as const }
          if (n === '__start') {
            return (
              <g key={n} style={grab} onPointerDown={(e) => startDrag(n, e)} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}>
                <circle cx={p.x} cy={p.y} r={7} fill="var(--text-subtle)" />
                <text x={p.x} y={p.y + 24} textAnchor="middle" fontFamily="var(--font-ui)" fontSize="10.5" fill="var(--text-subtle)">{t(lang, 'старт', 'start')}</text>
              </g>
            )
          }
          if (n === SCHEME_FINAL) {
            return (
              <g key={n} style={grab} onPointerDown={(e) => startDrag(n, e)} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}>
                <rect x={p.x - (NODE_W + 18) / 2} y={p.y - NODE_H / 2} width={NODE_W + 18} height={NODE_H} rx={15} fill="var(--code-1)" />
                <text x={p.x} y={p.y + 4} textAnchor="middle" fontFamily="var(--font-ui)" fontSize="11.5" fontWeight="600" fill="#fff">{t(lang, '✓ Согласован', '✓ Approved')}</text>
              </g>
            )
          }
          return (
            <g key={n} style={grab} onPointerDown={(e) => startDrag(n, e)} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}>
              <rect x={p.x - NODE_W / 2} y={p.y - NODE_H / 2} width={NODE_W} height={NODE_H} rx={6}
                    fill="var(--surface-sunken)" stroke="var(--border-strong)" />
              <text x={p.x} y={p.y + 4} textAnchor="middle" fontFamily="var(--font-mono)" fontSize="12" fontWeight="600" fill="var(--text-strong)">{n}</text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

/** Итог загрузки справочников — ответ POST /api/dicts:import. */
interface DictImportResult {
  created: number
  updated: number
  unchanged: number
  rejected: number
  errors: { row: number; dict: string | null; code: string | null; reason: string }[]
}

/* Отчёт по строкам. Ничего не скрывается: отклонённая строка называет причину,
   иначе «загружено 12 из 40» превращается в загадку. */
function DictImportReport({ lang, result, onClose }: { lang: Lang; result: DictImportResult; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} title={t(lang, 'Загрузка справочников', 'Dictionary import')}>
      <div style={{ display: 'flex', gap: 18, font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
        <span><b style={{ color: 'var(--green-600)' }}>{result.created}</b> {t(lang, 'добавлено', 'added')}</span>
        <span><b style={{ color: 'var(--blue-600)' }}>{result.updated}</b> {t(lang, 'изменено', 'updated')}</span>
        <span><b style={{ color: 'var(--text-muted)' }}>{result.unchanged}</b> {t(lang, 'без изменений', 'unchanged')}</span>
        <span><b style={{ color: result.rejected ? 'var(--danger-ink)' : 'var(--text-muted)' }}>{result.rejected}</b> {t(lang, 'отклонено', 'rejected')}</span>
      </div>
      {result.errors.length ? (
        <div style={{ marginTop: 14, maxHeight: 260, overflow: 'auto', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
          {result.errors.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '7px 10px', borderBottom: '1px solid var(--border-subtle)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)', width: 100, flex: '0 0 auto' }}>
                {e.dict ?? '—'} · {e.code ?? '—'}
              </span>
              <span style={{ color: 'var(--text-body)' }}>{e.reason}</span>
            </div>
          ))}
        </div>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <Button variant="primary" onClick={onClose}>{t(lang, 'Закрыть', 'Close')}</Button>
      </div>
    </Dialog>
  )
}

/* Добавление и правка строки справочника. Один диалог на оба случая: поля те
   же, различие одно — код существующей строки не меняется, на него ссылаются
   документы (сервер его из тела и не читает). */
function DictRowEditor({ lang, dictKey, dict, row, field, areaRows, onClose }: {
  lang: Lang
  dictKey: DictKey
  dict: { title: string; code: string; name: string }
  row: DictRow | null
  field: 'vendor' | 'area' | null
  areaRows: DictRow[]
  onClose: () => void
}) {
  const addRow = useCommand<DictRowArgs>({
    path: (a) => `/dicts/${encodeURIComponent(a.dict)}`,
    body: (a) => ({ code: a.code, name: a.name, active: a.active, vendor: a.vendor, area: a.area, meaning: a.meaning }),
    invalidate: () => DICT_INVALIDATE,
  })
  const editRow = useCommand<DictRowArgs>({
    method: 'PUT',
    path: (a) => `/dicts/${encodeURIComponent(a.dict)}/${encodeURIComponent(a.code)}`,
    body: (a) => ({ name: a.name, active: a.active, vendor: a.vendor, area: a.area, meaning: a.meaning }),
    invalidate: () => DICT_INVALIDATE,
  })
  const [code, setCode] = useState(row?.code ?? '')
  const [name, setName] = useState(row?.name ?? '')
  const [active, setActive] = useState(row?.active ?? true)
  const [vendor, setVendor] = useState(row?.vendor ?? '')
  const [area, setArea] = useState(row?.area ?? '')
  /* Код рассмотрения обязан говорить, что становится с документом: набор кодов
     у каждого заказчика свой, а разбирает документооборот смысл, а не букву
     (V71). Без смысла сервер строку не примет. */
  const isCode = dictKey === 'code'
  const [meaning, setMeaning] = useState(row?.meaning ?? '')

  const command = row ? editRow : addRow
  /* Пустой код и пустое наименование сервер отбивает (dict.code-required,
     dict.name-required). Кнопка гаснет раньше, чтобы не слать заведомый отказ;
     дублирующийся код виден только серверу — он ответит 409. */
  const ready = code.trim() !== '' && name.trim() !== '' && (!isCode || meaning !== '')
  const submit = () => command.mutate({
    dict: dictKey,
    code: code.trim(),
    name: name.trim(),
    active,
    vendor: field === 'vendor' ? (vendor.trim() || null) : null,
    area: field === 'area' ? (area || null) : null,
    meaning: isCode ? meaning : null,
  }, { onSuccess: onClose })

  return (
    <Dialog open onClose={onClose} title={row
      ? t(lang, `Строка справочника «${dict.title}»`, `Entry of «${dict.title}»`)
      : t(lang, `Новая строка справочника «${dict.title}»`, `New entry of «${dict.title}»`)}>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
        <Input
          label={dict.code}
          size="sm"
          mono
          value={code}
          disabled={!!row}
          hint={row ? t(lang, 'Код не меняется', 'Code is immutable') : undefined}
          onChange={(e) => setCode(e.target.value)}
        />
        <Input label={dict.name} size="sm" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      {isCode ? (
        <div style={{ marginTop: 12 }}>
          <Select label={t(lang, 'Что означает код', 'What the code means')} size="sm" value={meaning}
                  onChange={(e) => setMeaning(e.target.value)}
                  hint={t(lang, 'По нему документооборот решает судьбу документа, а экран красит фишку кода',
                                'The workflow decides the document fate by it, and the screen colours the code chip')}>
            <option value="">—</option>
            <option value="afd">{t(lang, 'Принят — в работу (AFD)', 'Accepted — released (AFD)')}</option>
            <option value="approved">{t(lang, 'Принят с замечаниями', 'Accepted with comments')}</option>
            <option value="reissue">{t(lang, 'Не принят — переиздать', 'Not accepted — reissue')}</option>
          </Select>
        </div>
      ) : null}
      {field === 'vendor' ? (
        <div style={{ marginTop: 12 }}>
          <Input label={t(lang, 'Поставщик / подрядчик', 'Vendor')} size="sm" value={vendor} onChange={(e) => setVendor(e.target.value)} />
        </div>
      ) : field === 'area' ? (
        <div style={{ marginTop: 12 }}>
          <Select label={t(lang, 'Объект', 'Area')} size="sm" value={area} onChange={(e) => setArea(e.target.value)}>
            <option value="">—</option>
            {areaRows.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
          </Select>
        </div>
      ) : null}
      <div style={{ marginTop: 14 }}>
        <Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} label={t(lang, 'Активна', 'Active')} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button
          variant="primary"
          disabled={!ready || command.isPending}
          title={ready ? undefined : isCode
            ? t(lang, 'Нужны код, наименование и смысл', 'Code, name and meaning are required')
            : t(lang, 'Нужны код и наименование', 'Code and name are required')}
          onClick={submit}
        >
          {command.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить', 'Save')}
        </Button>
      </div>
    </Dialog>
  )
}

/**
 * Группы пользователей.
 *
 * Группа — адресат шага рассмотрения: шаг поручается ей, задача видна всем
 * участникам, закрывает тот, кто взял, и записывается именно он. Права группа
 * не даёт — их даёт роль.
 */
function GroupsBlock({ lang, canAdmin }: { lang: Lang; canAdmin: boolean }) {
  const groupsQuery = useMeta<UserGroup[]>('groups')
  const groups = groupsQuery.data ?? []
  const usersQuery = useList<User>('users', { page: 0, pageSize: 200, sort: [{ id: 'id', desc: false }] })
  // Подрядчик рассмотрение не ведёт, и в группу его не берут — сервер отбивает.
  const people = (usersQuery.data?.rows ?? []).filter((u) => !u.external)
  const nameOf = new Map(people.map((u) => [u.id, u.name]))
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<UserGroup | null>(null)

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{t(lang, 'Группы пользователей', 'User groups')}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{groups.length}</span>
        <span style={{ flex: 1, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Шаг рассмотрения поручается группе — закрывает любой участник',
                'A review step can be assigned to a group — any member may act')}
        </span>
        {canAdmin ? (
          <Button variant="secondary" size="sm" iconLeft={<Icon name="plus" size={13} />} onClick={() => setAdding(true)}>
            {t(lang, 'Добавить группу', 'Add group')}
          </Button>
        ) : null}
      </div>
      <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
        {groups.length ? groups.map((g) => (
          <div key={g.code} className="lav-tr" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
            <CIcon name="users" size={13} color="var(--text-subtle)" />
            <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--text-strong)' }}>{g.name}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{g.code}</span>
            <span style={{ flex: 1, minWidth: 0, font: 'var(--font-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {g.members.length
                ? g.members.map((m) => nameOf.get(m) ?? m).join(', ')
                : t(lang, 'нет участников — шаг такой группе поручить нельзя', 'no members — a step cannot be assigned to it')}
            </span>
            {canAdmin ? <IconBtn icon="pencil" onClick={() => setEditing(g)} /> : null}
          </div>
        )) : (
          <div style={{ padding: '18px 14px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
            {t(lang, 'Групп нет. Пока шаг рассмотрения поручается одному человеку.',
                  'No groups yet. A review step is assigned to a single person.')}
          </div>
        )}
      </div>
      {adding ? <GroupDialog lang={lang} onClose={() => setAdding(false)} /> : null}
      {editing ? <GroupMembersDialog lang={lang} group={editing} people={people} onClose={() => setEditing(null)} /> : null}
    </div>
  )
}

function GroupDialog({ lang, onClose }: { lang: Lang; onClose: () => void }) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const command = useCommand<{ code: string; name: string }>({
    path: () => '/groups',
    body: (a) => ({ code: a.code, name: a.name }),
    invalidate: () => [qk.meta('groups'), qk.all('audit')],
  })
  const ready = code.trim().length > 1 && name.trim().length > 0
  return (
    <Dialog open onClose={onClose} title={t(lang, 'Новая группа', 'New group')}>
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12 }}>
        <Input label={t(lang, 'Код', 'Code')} size="sm" mono value={code} onChange={(e) => setCode(e.target.value)} />
        <Input label={t(lang, 'Название', 'Name')} size="sm" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div style={{ marginTop: 12, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
        {t(lang, 'Состав задаётся после создания. Пустую группу нельзя поручить шагу: её некому закрыть.',
              'Members are added after creation. An empty group cannot own a step — nobody could close it.')}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button variant="primary" disabled={!ready || command.isPending}
                onClick={() => command.mutate({ code: code.trim(), name: name.trim() }, { onSuccess: onClose })}>
          {command.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Добавить', 'Add')}
        </Button>
      </div>
    </Dialog>
  )
}

/* Состав правится целиком: администратор собирает группу списком, а сервер
   заменяет набор — история состава остаётся в слоях. */
function GroupMembersDialog({ lang, group, people, onClose }: {
  lang: Lang
  group: UserGroup
  people: User[]
  onClose: () => void
}) {
  const [members, setMembers] = useState<string[]>(group.members)
  const command = useCommand<{ code: string; users: string[] }>({
    method: 'PUT',
    path: (a) => `/groups/${encodeURIComponent(a.code)}/members`,
    body: (a) => ({ users: a.users }),
    invalidate: () => [qk.meta('groups'), qk.meta('matrix-rules'), ...inboxKeys, qk.all('audit')],
  })
  const toggle = (id: string) =>
    setMembers(members.includes(id) ? members.filter((m) => m !== id) : [...members, id])
  return (
    <Dialog open onClose={onClose} title={t(lang, `Состав группы «${group.name}»`, `Members of «${group.name}»`)}>
      <div style={{ maxHeight: 320, overflow: 'auto', display: 'grid', gap: 6 }}>
        {people.map((u) => (
          <Checkbox key={u.id} checked={members.includes(u.id)} onChange={() => toggle(u.id)}
                    label={`${u.name} · ${u.role}`} />
        ))}
      </div>
      <div style={{ marginTop: 12, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
        {t(lang, 'Задача видна всем участникам, пока кто-нибудь её не закроет; записывается тот, кто закрыл.',
              'The task shows for every member until one closes it; the closer is recorded.')}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button variant="primary" disabled={command.isPending}
                onClick={() => command.mutate({ code: group.code, users: members }, { onSuccess: onClose })}>
          {command.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить состав', 'Save members')}
        </Button>
      </div>
    </Dialog>
  )
}

/**
 * Новая роль.
 *
 * Права не задаются здесь: роль заводится пустой, и уровни выставляются в самой
 * матрице — там же, где их видно у остальных ролей. Иначе одно и то же
 * настраивалось бы в двух местах.
 */
function RoleDialog({ open, onClose, lang }: { open: boolean; onClose: () => void; lang: Lang }) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const command = useCommand<{ code: string; name: string }>({
    path: () => '/roles',
    body: (a) => ({ code: a.code, name: a.name }),
    invalidate: () => [qk.meta('roles'), qk.all('audit')],
  })
  const ready = code.trim().length > 1 && name.trim().length > 0
  return (
    <Dialog open={open} onClose={onClose} title={t(lang, 'Новая роль', 'New role')}>
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 12 }}>
        <Input label={t(lang, 'Код', 'Code')} size="sm" mono value={code}
               hint={t(lang, 'латиница, цифры, дефис', 'latin letters, digits, hyphen')}
               onChange={(e) => setCode(e.target.value)} />
        <Input label={t(lang, 'Название', 'Name')} size="sm" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div style={{ marginTop: 12, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
        {t(lang, 'Роль появится в матрице без прав — уровни по разделам выставляются там.',
              'The role appears in the matrix with no access — set the levels there.')}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button variant="primary" disabled={!ready || command.isPending}
                title={ready ? undefined : t(lang, 'Нужны код и название', 'Code and name are required')}
                onClick={() => command.mutate({ code: code.trim(), name: name.trim() }, { onSuccess: onClose })}>
          {command.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Добавить', 'Add')}
        </Button>
      </div>
    </Dialog>
  )
}

// ===== Замещения на время отсутствия ====================================
/**
 * Приказ о замещении.
 *
 * Замещение — ПЕРЕДАЧА ПРАВА на срок, а не переназначение шага: шаги
 * отсутствующего остаются за ним, а замещающий на срок приказа получает право
 * их вести и разделы отсутствующего в придачу к своим. Переназначение переписало
 * бы исполнителя, и по завершённому кругу уже не сказать, кому шаг был поручен
 * на самом деле.
 *
 * Кто вправе завести — решает СЕРВЕР, и решение зависит от содержимого запроса:
 * администратор заводит за кого угодно, остальные только за себя. Экран
 * повторяет это выбором поля «Отсутствующий», чтобы не предлагать заведомо
 * запрещённое, но защита стоит на сервере: скрытое поле — оформление, а не
 * запрет.
 */
interface Substitution {
  id: string
  who: string
  whoName: string | null
  deputy: string
  deputyName: string | null
  since: string
  until: string
  reason: string | null
  createdBy: string
  /** Считает БД по своему календарю: срок замещения — факт, а не мнение вкладки. */
  active: boolean
}

/* Приказ меняет права разделов и очередь задач замещающего. Если замещающий —
   сам вошедший, то и его собственные «/me» и «Мои действия» устарели: без их
   сброса право появляется только после перезагрузки вкладки. Тот же набор
   ключей, что у правки уровня роли выше по экрану. */
const SUBSTITUTION_INVALIDATE = [qk.sub('/substitutions'), qk.sub('/me'), ...inboxKeys, qk.all('audit')]

/**
 * Отказ показывается СЛОВАМИ СЕРВЕРА.
 *
 * Правила замещения — «администратора не замещают», «период задом наперёд»,
 * «сам себя не замещает» — объясняет сервер, и он же их меняет. Пересказ своими
 * словами разошёлся бы с ним при первой правке правила, а человек читал бы
 * объяснение, которого больше нет.
 */
function refusal(error: Error): { title: string; text?: string } {
  return error instanceof ApiError ? { title: error.title, text: error.detail } : { title: error.message }
}

function SubstitutionsBody({ lang, canAdmin }: { lang: Lang; canAdmin: boolean }) {
  const me = useMe()
  const myCode = me.data?.user?.id ?? ''
  const query = useSub<{ rows: Substitution[] }>('/substitutions')
  /* Отбор делает сервер: администратору — приказы проекта, остальным те, где
     названы они сами. Своего отбора здесь нет — он разошёлся бы с серверным. */
  const rows = query.data?.rows ?? []
  const [dialog, setDialog] = useState(false)
  const revoke = useCommand<{ id: string }>({
    method: 'DELETE',
    path: (a) => `/substitutions/${encodeURIComponent(a.id)}`,
    invalidate: () => SUBSTITUTION_INVALIDATE,
  })
  /* Отзывает тот же, кто вправе завести: администратор или сам отсутствующий.
     Замещающий отказаться за отсутствующего не может — права передал не он. */
  const mayRevoke = (r: Substitution) => canAdmin || r.who === myCode

  const cols: Column<Substitution>[] = [
    { key: 'who', header: colHeader(lang, 'Отсутствующий'), width: 220,
      render: (r) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <Avatar name={r.whoName ?? r.who} size={22} />
        <span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.whoName ?? r.who}</span>
      </span> },
    { key: 'deputy', header: colHeader(lang, 'Замещающий'), width: 220,
      render: (r) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <Avatar name={r.deputyName ?? r.deputy} size={22} />
        <span style={{ color: 'var(--text-body)' }}>{r.deputyName ?? r.deputy}</span>
      </span> },
    { key: 'since', header: colHeader(lang, 'Отсутствует с'), mono: true, align: 'right', width: 130,
      render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.since}</span> },
    /* «По» включительно: приказ пишут «по 14-е», и полуоткрытый интервал
       читался бы неверно на каждом заведении. */
    { key: 'until', header: colHeader(lang, 'По включительно'), mono: true, align: 'right', width: 130,
      render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.until}</span> },
    { key: 'reason', header: colHeader(lang, 'Причина'),
      render: (r) => <span style={{ color: r.reason ? 'var(--text-body)' : 'var(--text-subtle)' }}>
        {r.reason ?? t(lang, 'не указана', 'not given')}
      </span> },
    { key: 'state', header: colHeader(lang, 'Состояние'), width: 120,
      render: (r) => <SubstitutionState row={r} lang={lang} /> },
    { key: 'act', header: '', width: 110,
      render: (r) => mayRevoke(r) ? (
        <Button variant="ghost" size="sm" disabled={revoke.isPending}
                onClick={() => revoke.mutate({ id: r.id }, {
                  onError: (err) => toast({ tone: 'danger', ...refusal(err) }),
                  onSuccess: () => toast({ tone: 'info', title: t(lang, 'Замещение отозвано', 'Substitution revoked') }),
                })}>
          {t(lang, 'Отозвать', 'Revoke')}
        </Button>
      ) : null },
  ]

  if (query.isError) return <ScreenError lang={lang} error={query.error} />
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Toolbar>
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {canAdmin
            ? t(lang, 'На срок приказа шаги отсутствующего ведёт замещающий, получая и права его разделов. Шаги не переназначаются.',
                     'For the period of the order the deputy runs the absent person’s steps and gains their section rights. The steps are not reassigned.')
            : t(lang, 'Здесь только ваши приказы: где отсутствующий вы и где замещающий вы. Передать вправе только владелец права.',
                     'Only your own orders: where you are absent and where you are the deputy. Only the owner may hand a right over.')}
        </div>
        <ToolSpacer />
        <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setDialog(true)}>
          {t(lang, 'Завести замещение', 'Add substitution')}
        </Button>
      </Toolbar>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {rows.length
          ? <DataTable columns={cols} rows={rows} rowKey={(r) => r.id} />
          : <EmptyState icon="calendar-days" title={t(lang, 'Замещений нет', 'No substitutions')}
                        text={t(lang, 'Ушедший в отпуск оставляет шаги висеть до возвращения. Приказ передаёт право их вести на срок отсутствия — и снимает его сам, без второго захода.',
                                      'Someone away on leave leaves their steps hanging until they are back. An order hands the right to run them over for the period — and expires on its own.')} />}
      </div>
      {dialog ? <SubstitutionDialog lang={lang} canAdmin={canAdmin} myCode={myCode} onClose={() => setDialog(false)} /> : null}
    </div>
  )
}

/** Действует / ещё не начался / уже кончился. */
function SubstitutionState({ row, lang }: { row: Substitution; lang: Lang }) {
  /* Действует — только по ответу сервера: сегодняшний день считает БД, и
     вкладка, открытая со вчера, не вправе решать это сама. Клиентское сравнение
     остаётся лишь на то, чтобы отличить «ещё не начался» от «уже кончился». */
  if (row.active) return <Badge tone="success" size="sm" dot>{t(lang, 'Действует', 'Active')}</Badge>
  const since = parseRu(row.since)
  return since != null && since > today()
    ? <Badge tone="info" size="sm">{t(lang, 'Ожидает', 'Scheduled')}</Badge>
    : <Badge tone="neutral" size="sm">{t(lang, 'Истекло', 'Expired')}</Badge>
}

function SubstitutionDialog({ lang, canAdmin, myCode, onClose }: {
  lang: Lang
  canAdmin: boolean
  myCode: string
  onClose: () => void
}) {
  /* Справочник рассмотрения, а не список учётных записей: «/users» закрыт
     разделом администрирования, а приказ за себя заводит любой участник —
     рецензент упёрся бы в 403 на форме, которую ему разрешено заполнять. */
  const people = (useMeta<{ code: string; name: string; kind: 'user' | 'group' }[]>('reviewers').data ?? [])
    .filter((p) => p.kind === 'user')
  /* Обычный участник заводит только за себя, поэтому поле не предлагается:
     выбор, который сервер всё равно отобьёт, — не выбор. */
  const [who, setWho] = useState(canAdmin ? '' : myCode)
  const [deputy, setDeputy] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')
  const [reason, setReason] = useState('')
  const add = useCommand<{ who: string; deputy: string; since: string; until: string; reason: string }>({
    path: () => '/substitutions',
    body: (a) => a,
    invalidate: () => SUBSTITUTION_INVALIDATE,
  })
  /* Проверяется только заполненность. Задом наперёд, сам себя и замещение
     администратора отбивает сервер — своей проверкой их не подменить: правила
     живут там и меняются там. */
  const ready = who !== '' && deputy !== '' && since.trim() !== '' && until.trim() !== ''
  const bad = add.error ? refusal(add.error) : null
  const myName = people.find((p) => p.code === myCode)?.name ?? myCode

  return (
    <Dialog open onClose={onClose} title={t(lang, 'Замещение на время отсутствия', 'Substitution while away')}>
      <div style={{ display: 'grid', gap: 12 }}>
        {canAdmin ? (
          <Select label={t(lang, 'Отсутствующий', 'Absent')} value={who} onChange={(e) => setWho(e.target.value)}
                  hint={t(lang, 'Администратора не замещают: у него не работа в круге, а ключи от системы.',
                                'An administrator is never substituted: they hold the keys to the system, not a place in the round.')}>
            <option value="">{t(lang, '— не выбран —', '— none —')}</option>
            {people.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </Select>
        ) : (
          <Field label={t(lang, 'Отсутствующий', 'Absent')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)' }}>
              <Avatar name={myName} size={22} />{myName}
              <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
                {t(lang, 'право подписи передаёт только его владелец', 'only the owner may hand their right over')}
              </span>
            </div>
          </Field>
        )}
        <Select label={t(lang, 'Замещающий', 'Deputy')} value={deputy} onChange={(e) => setDeputy(e.target.value)}
                hint={t(lang, 'Своих прав он не теряет: замещение расширяет принадлежность шага, а не подменяет человека.',
                              'The deputy keeps their own rights: a substitution widens who owns a step, it does not swap people.')}>
          <option value="">{t(lang, '— не выбран —', '— none —')}</option>
          {people.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
        </Select>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <DateInput label={t(lang, 'Отсутствует с', 'Away from')} value={since} onChange={setSince} lang={lang} />
          <DateInput label={t(lang, 'По включительно', 'To inclusive')} value={until} onChange={setUntil} lang={lang}
                     hint={t(lang, 'последний день замещения', 'the last day of the substitution')} />
        </div>
        <Input label={t(lang, 'Причина', 'Reason')} value={reason} onChange={(e) => setReason(e.target.value)}
               placeholder={t(lang, 'отпуск, больничный, командировка', 'leave, sick leave, business trip')}
               hint={t(lang, 'Свободным текстом: закрытый список причин отсутствия пришлось бы вести, и он ничего не решает.',
                             'Free text: a closed list of reasons would need maintaining and settles nothing.')} />
        {bad ? (
          <div style={{ padding: '9px 11px', border: '1px solid var(--danger-line)', borderRadius: 'var(--radius-sm)', background: 'var(--danger-bg)' }}>
            <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--danger-ink)' }}>{bad.title}</div>
            {bad.text ? <div style={{ marginTop: 2, font: 'var(--font-caption)', color: 'var(--text-body)' }}>{bad.text}</div> : null}
          </div>
        ) : null}
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Цепочек нет: если замещающий сам уйдёт, приказ пишется на третьего от отсутствующего — права, которых никто не выдавал явно, не наследуются.',
                   'No chains: if the deputy leaves too, a new order is written from the absent person to a third one — rights nobody granted explicitly are not inherited.')}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose} disabled={add.isPending}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={!ready || add.isPending}
                  title={ready ? undefined : t(lang, 'Нужны замещающий и обе даты', 'The deputy and both dates are required')}
                  onClick={() => add.mutate({ who, deputy, since: since.trim(), until: until.trim(), reason: reason.trim() }, {
                    onSuccess: () => { onClose(); toast({ tone: 'success', title: t(lang, 'Замещение заведено', 'Substitution created') }) },
                  })}>
            {add.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Завести', 'Create')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ===== Object coding rules (numbering masks) ============================
const TARGETS = [
  { id: 'doc', ru: 'Документы', en: 'Documents', icon: 'file-text' },
  { id: 'mdr', ru: 'Реестры MDR', en: 'MDR', icon: 'folder-tree' },
  { id: 'vdr', ru: 'Реестры VDR', en: 'VDR', icon: 'package' },
  { id: 'trm', ru: 'Трансмитталы', en: 'TRM', icon: 'send' },
  { id: 'crs', ru: 'Замечания CRS', en: 'CRS', icon: 'message-square-warning' },
]
const SEG_COLORS = ['--blue-500', '--amber-500', '--green-500', '--violet-500', '--red-500', '--blue-600']
/** Поля маски, которые принимает PUT /coding/masks/{code}. */
type MaskField = 'label' | 'note' | 'example' | 'separator' | 'mask' | 'extension'

/* Правка маски уходит по потере фокуса, а не на каждую букву: прежний
   S.updateMask вызывался на каждый символ, и с сервером это была бы команда на
   символ. Значение с сервера остаётся источником истины — после успеха его
   приносит инвалидация, после отказа (mask.frozen) поле к нему возвращается. */
function MaskInput({ code, field, label, value, hint, mono, lang }: {
  code: string
  field: MaskField
  label: string
  value: string
  hint?: string
  mono?: boolean
  lang: Lang
}) {
  const save = useCommand<{ code: string; patch: Partial<Record<MaskField, string>> }>({
    method: 'PUT',
    path: (a) => `/coding/masks/${encodeURIComponent(a.code)}`,
    body: (a) => a.patch,
    invalidate: () => [qk.meta('coding'), qk.all('audit')],
  })
  const [draft, setDraft] = useState(value)
  useEffect(() => { setDraft(value) }, [value])
  const commit = () => {
    // Разделитель не обрезается: пробел — такой же допустимый разделитель, как дефис.
    const next = field === 'separator' ? draft : draft.trim()
    /* Пустое значение сервер отбивать не станет, а маска без подписи или без
       шаблона бессмысленна: пустое поле просто откатывается. Исключение —
       расширение: пустым его СТАВЯТ, так выключается проверка расширения, и
       откатывать это значило бы не давать его снять. */
    if (!next && field !== 'extension') { setDraft(value); return }
    if (next === value) return
    save.mutate({ code, patch: { [field]: next } }, { onError: () => setDraft(value) })
  }
  return (
    <Input
      label={label}
      value={draft}
      mono={mono}
      disabled={save.isPending}
      hint={save.isPending ? t(lang, 'Сохранение…', 'Saving…') : hint}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
    />
  )
}

/** Источники значения сегмента. Порядок — как в подсказке администратору. */
const SEGMENT_SOURCES: { kind: SegmentSource; ru: string; en: string }[] = [
  /* Объект и титул берутся у СТРОКИ (с V90 они её свойства), а объект реестра
     остаётся запасным значением, когда строка своего не назвала. */
  { kind: 'area', ru: 'Объект документа', en: 'Document area' },
  { kind: 'titul', ru: 'Титул проекта', en: 'Project title' },
  { kind: 'disc', ru: 'Дисциплина реестра', en: 'Register discipline' },
  { kind: 'type', ru: 'Тип документа', en: 'Document type' },
  { kind: 'seq', ru: 'Порядковый номер', en: 'Sequence' },
  { kind: 'const', ru: 'Постоянная строка', en: 'Constant' },
  { kind: 'year', ru: 'Год', en: 'Year' },
  /* Стороны переписки и лист. Сервер их принимал с самого начала (MaskTargets:
     project и customer стоят в маске документа и в обеих масках трансмиттала,
     sheet — в маске документа), а в списке источников их не было: собрать
     маску с кодом проекта было нельзя, хотя ровно с него начинается маска по
     умолчанию. Коды берутся у проекта (PartyCodes), их правят в настройках. */
  { kind: 'project', ru: 'Код проекта', en: 'Project code' },
  { kind: 'customer', ru: 'Код заказчика', en: 'Customer code' },
  { kind: 'sheet', ru: 'Лист документа', en: 'Document sheet' },
  { kind: 'stage', ru: 'Стадия', en: 'Stage' },
  { kind: 'contract', ru: 'Контракт', en: 'Contract' },
  { kind: 'vendor', ru: 'Поставщик', en: 'Vendor' },
  { kind: 'po', ru: 'Заказ на поставку', en: 'Purchase order' },
  /* Ссылка на документ: ими выражены коды листа замечаний и самой позиции —
     «имя документа» в маске, о котором просил заказчик. */
  { kind: 'doc', ru: 'Номер документа', en: 'Document number' },
  { kind: 'rev', ru: 'Ревизия документа', en: 'Document revision' },
  { kind: 'ver', ru: 'Версия основного файла', en: 'Main file version' },
  /* Роль файла — источник маски ИМЕНИ: PDF у основного, «дополнительный» и
     «Исходник» у прочих. Подставляется при загрузке по выбранной роли. */
  { kind: 'role', ru: 'Роль файла', en: 'File role' },
]

/* Что цель умеет заполнить в момент выпуска — зеркало MaskTargets на сервере.
   Сервер остаётся второй линией (400 mask.source-not-here), но предлагать
   источник, которым номер потом нечем собрать, — ловушка: отказ приходил уже
   после того, как администратор собрал маску целиком. */
const TARGET_SOURCES: Record<string, SegmentSource[]> = {
  trm: ['const', 'seq', 'year'],
  /* Трансмиттал по направлениям называет стороны переписки: номер читается
     как «от кого — кому», и порядок сегментов задаёт направление. */
  'trm-in': ['const', 'seq', 'year', 'project', 'customer', 'vendor'],
  'trm-out': ['const', 'seq', 'year', 'project', 'customer', 'vendor'],
  // Реестр MDR заводится на контракт, а не на дисциплину с объектом.
  mdr: ['const', 'contract'],
  vdr: ['const', 'po', 'vendor'],
  crs: ['const', 'seq', 'doc', 'rev', 'ver'],
  comm: ['const', 'seq', 'doc', 'rev', 'ver'],
  /* Имя файла: номер по маске не выпускается — по ней имя проверяется, —
     поэтому счётчика нет. Расширение задаётся полем маски, не сегментом. */
  file: ['const', 'doc', 'rev', 'role'],
}

/** Новая маска документа: код, подпись, разделитель и сразу состав номера. */
function MaskDialog({ open, onClose, lang }: { open: boolean; onClose: () => void; lang: Lang }) {
  const [form, setForm] = useState({ code: '', label: '', separator: '-' })
  /* Состав задаётся здесь же: заводя маску, администратор уже знает, из чего
     собирается номер, а маска без состава номер собрать не может — до сих пор
     её приходилось заводить пустой и дописывать вторым заходом. */
  const [rows, setRows] = useState<CodingSegment[]>([])
  const save = useCommand<typeof form>({
    path: () => '/coding/masks',
    body: (a) => a,
    invalidate: () => [qk.meta('coding'), qk.all('audit')],
  })
  /* Состав уходит второй командой: маски ещё нет, пока не выполнена первая.
     Отказ по составу оставляет заведённую маску — её состав дописывается на
     самой маске, и повторное заведение не нужно. */
  const composition = useCommand<{ code: string; segments: CodingSegment[] }>({
    method: 'PUT',
    path: (a) => `/coding/masks/${encodeURIComponent(a.code)}/segments`,
    body: (a) => ({
      segments: a.segments.map((sgm, i) => ({
        code: sgm.id || 'seg' + (i + 1),
        label: sgm.label,
        src: sgm.src,
        srcKind: sgm.srcKind,
        value: sgm.srcKind === 'const' ? sgm.value : null,
        width: sgm.srcKind === 'seq' ? sgm.width : null,
        example: sgm.example,
        len: sgm.len,
      })),
    }),
    invalidate: () => [qk.meta('coding'), qk.all('audit')],
  })
  const busy = save.isPending || composition.isPending
  const submit = () => save.mutate(form, {
    onSuccess: () => {
      if (!rows.length) {
        toast({ tone: 'success', title: 'Маска заведена', text: form.label })
        onClose()
        return
      }
      composition.mutate({ code: form.code, segments: rows }, {
        onSuccess: () => {
          toast({ tone: 'success', title: 'Маска заведена', text: form.label })
          onClose()
        },
      })
    },
  })
  return (
    <Dialog open={open} onClose={onClose} sizeKey="coding-mask" title={t(lang, 'Новая маска документа', 'New document mask')}>
      <div style={{ width: 720, maxWidth: '100%', display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label={t(lang, 'Код', 'Code')} value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
          <Input label={t(lang, 'Разделитель', 'Separator')} mono value={form.separator} onChange={(e) => setForm((f) => ({ ...f, separator: e.target.value }))} />
        </div>
        <Input label={t(lang, 'Наименование маски', 'Mask name')} value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} />
        <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          <SegmentRows rows={rows} setRows={setRows} lang={lang} target="document" />
          <div style={{ padding: '10px 14px', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            {rows.length
              ? t(lang, 'Порядок сегментов — порядок частей номера.', 'The order of the segments is the order of the number parts.')
              : t(lang, 'Состав можно не задавать сейчас — он правится и на самой маске.', 'The composition can be left for later — it is editable on the mask itself.')}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={busy} onClick={submit}>{busy ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить', 'Save')}</Button>
        </div>
      </div>
    </Dialog>
  )
}

/* Состав номера. Один редактор на все маски: с 17.08.26 номер реестра,
   трансмиттала, листа и замечания собирается теми же сегментами, что и номер
   документа, — экран это и показывает. */
function SegmentEditor({ mask, lang, target }: { mask: Pick<CodingDoc, 'code' | 'title' | 'sep' | 'segments'>; lang: Lang; target?: string }) {
  const [rows, setRows] = useState<CodingSegment[]>(mask.segments ?? [])
  useEffect(() => { setRows(mask.segments ?? []) }, [mask])
  /* Команда принимает сегмент с полем code, а на экране он зовётся id: это
     ключ сегмента в маске. Разрешение одного в другое — здесь, иначе сервер
     получает строку без ключа. */
  const save = useCommand<{ code: string; segments: CodingSegment[] }>({
    method: 'PUT',
    path: (a) => `/coding/masks/${encodeURIComponent(a.code)}/segments`,
    body: (a) => ({
      segments: a.segments.map((s, i) => ({
        code: s.id || 'seg' + (i + 1),
        label: s.label,
        src: s.src,
        srcKind: s.srcKind,
        value: s.srcKind === 'const' ? s.value : null,
        width: s.srcKind === 'seq' ? s.width : null,
        example: s.example,
        len: s.len,
      })),
    }),
    invalidate: () => [qk.meta('coding'), qk.all('audit')],
  })
  return (
    <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      <SegmentRows rows={rows} setRows={setRows} lang={lang} target={target} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px' }}>
        <span style={{ marginLeft: 'auto', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Маска с выпущенными документами заморожена — заведите новую и переведите на неё реестр', 'A mask with issued documents is frozen — add a new one and move the register to it')}
        </span>
        <Button variant="primary" size="sm" disabled={save.isPending} onClick={() => save.mutate({ code: mask.code, segments: rows }, {
          onSuccess: () => toast({ tone: 'success', title: 'Состав номера сохранён', text: mask.title }),
        })}>{save.isPending ? t(lang, 'Сохранение…', 'Saving…') : t(lang, 'Сохранить состав', 'Save composition')}</Button>
      </div>
    </div>
  )
}

/* Строки состава номера: метка, источник, значение, длина и порядок. Отдельно
   от SegmentEditor, потому что тот же набор строк собирают в двух местах — на
   заведённой маске и в диалоге создания новой, где маски ещё нет. */
function SegmentRows({ rows, setRows, lang, target }: { rows: CodingSegment[]; setRows: (f: (r: CodingSegment[]) => CodingSegment[]) => void; lang: Lang; target?: string }) {
  /* Источник, уже стоящий в сегменте, остаётся в списке, даже если цель его
     больше не допускает: иначе выбор молча съехал бы на соседний. */
  const sources = (current?: SegmentSource) => {
    const allowed = target ? TARGET_SOURCES[target] : undefined
    if (!allowed) return SEGMENT_SOURCES
    return SEGMENT_SOURCES.filter((s) => allowed.includes(s.kind) || s.kind === current)
  }
  const set = (i: number, patch: Partial<CodingSegment>) =>
    setRows((r) => r.map((row, at) => (at === i ? { ...row, ...patch } : row)))
  const add = () => setRows((r) => [...r, { id: 'seg' + (r.length + 1), label: '', src: '', srcKind: 'const', value: '', example: '', len: '' }])
  const drop = (i: number) => setRows((r) => r.filter((_, at) => at !== i))
  const move = (i: number, to: number) => setRows((r) => {
    if (to < 0 || to >= r.length) return r
    const next = r.slice()
    const [row] = next.splice(i, 1)
    if (!row) return r
    next.splice(to, 0, row)
    return next
  })
  const cols = '24px 1.1fr 1.3fr 1fr 90px 96px'
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 10, padding: '9px 14px', background: 'var(--surface-sunken)', borderBottom: '1px solid var(--border-strong)' }}>
        {['', t(lang, 'Метка', 'Label'), t(lang, 'Источник', 'Source'), t(lang, 'Значение', 'Value'), t(lang, 'Длина', 'Length'), ''].map((h, i) => (
          <span key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-2xs)', fontWeight: 600, letterSpacing: 'var(--tracking-caps)', textTransform: 'uppercase', color: 'var(--text-subtle)' }}>{h}</span>
        ))}
      </div>
      {rows.map((s, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: cols, gap: 10, alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: `var(${SEG_COLORS[i % SEG_COLORS.length]})` }} />
          <Input size="sm" value={s.label} onChange={(e) => set(i, { label: e.target.value })} />
          <Select size="sm" value={s.srcKind ?? 'const'} onChange={(e) => set(i, { srcKind: e.target.value as SegmentSource })}>
            {sources(s.srcKind).map((src) => <option key={src.kind} value={src.kind}>{t(lang, src.ru, src.en)}</option>)}
          </Select>
          {/* Постоянная строка нужна только своему источнику; у остальных
              значение приходит из реестра или документа. */}
          <Input size="sm" mono disabled={s.srcKind !== 'const'} value={s.srcKind === 'const' ? (s.value ?? '') : '—'}
                 onChange={(e) => set(i, { value: e.target.value })} />
          <Input size="sm" mono disabled={s.srcKind !== 'seq'} value={s.srcKind === 'seq' ? String(s.width ?? '') : '—'}
                 onChange={(e) => set(i, { width: Number(e.target.value) || undefined })} />
          <span style={{ display: 'inline-flex', gap: 2 }}>
            <button onClick={() => move(i, i - 1)} title={t(lang, 'Выше', 'Up')} className="icon-btn" style={segBtn}><Icon name="chevron-up" size={13} /></button>
            <button onClick={() => move(i, i + 1)} title={t(lang, 'Ниже', 'Down')} className="icon-btn" style={segBtn}><Icon name="chevron-down" size={13} /></button>
            <button onClick={() => drop(i)} title={t(lang, 'Убрать', 'Remove')} className="icon-btn" style={segBtn}><Icon name="trash-2" size={13} /></button>
          </span>
        </div>
      ))}
      <div style={{ padding: '10px 14px 0' }}>
        <Button variant="secondary" size="sm" iconLeft={<Icon name="plus" size={13} />} onClick={add}>{t(lang, 'Сегмент', 'Segment')}</Button>
      </div>
    </>
  )
}
const segBtn: CSSProperties = { display: 'grid', placeItems: 'center', width: 26, height: 26, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)', color: 'var(--text-subtle)', cursor: 'pointer' }

function CodingBody({ lang }: { lang: Lang }) {
  const codingQuery = useCoding()
  const coding = codingQuery.data
  const [sel, setSel] = useState('doc')
  const [adding, setAdding] = useState(false)
  if (codingQuery.isError) return <ScreenError lang={lang} />
  if (!coding) return <ScreenLoading lang={lang} />

  const order = (code: string) => { const i = TARGETS.findIndex((tg) => tg.id === code); return i < 0 ? TARGETS.length : i }
  const masks = [
    ...coding.docs.map((d) => ({ code: d.code, label: d.title, example: d.example })),
    ...coding.others.map((o) => ({ code: o.id, label: o.label, example: o.example })),
  ].sort((a, b) => order(a.code) - order(b.code))
  const cur = masks.find((m) => m.code === sel) ?? masks[0]
  if (!cur) return <EmptyState icon="hash" title={t(lang, 'Маски нумерации не настроены', 'No numbering masks')} />
  const doc = coding.docs.find((d) => d.code === cur.code)
  const other = coding.others.find((o) => o.id === cur.code)
  /* Состав номера показывается у ЛЮБОЙ маски: с 17.08.26 их собирает одна
     механика, и текст шаблона перестал быть надписью. Маска имени файла
     сегментов не имеет — по ней проверяется имя файла, а не выпускается номер. */
  const segments = doc?.segments ?? other?.segments ?? []
  const sep = doc?.sep ?? other?.sep ?? '-'
  /* Проверяется НАПОЛНЕНИЕ, а не наличие массива: сервер отдаёт segments у
     каждой маски, и пустой массив у маски имени файла оказался бы истиной —
     редактор состава открылся бы там, где номер не выпускается. */
  const editable = doc ?? (other && other.segments?.length
    ? { code: other.id, title: other.label, sep, segments: other.segments }
    : null)
  const frozenHint = doc
    ? t(lang, 'Заморожен, если по маске уже выпущены документы', 'Frozen once documents are issued')
    : t(lang, 'Выпущенные номера не переписываются — новые собираются по новой маске',
           'Issued numbers are not rewritten — new ones follow the new mask')

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <div style={{ width: railWidth(264), flex: '0 0 auto', borderRight: '1px solid var(--border-default)', background: 'var(--surface-card)', overflow: 'auto', padding: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px 6px' }}>
          <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{t(lang, 'Маски нумерации', 'Numbering masks')}</span>
          <button onClick={() => setAdding(true)} title={t(lang, 'Новая маска документа', 'New document mask')} className="icon-btn" style={{ ...segBtn, marginLeft: 'auto' }}><Icon name="plus" size={13} /></button>
        </div>
        {masks.map((m) => {
          const on = m.code === cur.code
          const tg = TARGETS.find((x) => x.id === m.code)
          return (
            <button key={m.code} onClick={() => setSel(m.code)} className="tdms-rail" style={{ display: 'flex', alignItems: 'flex-start', gap: 9, width: '100%', textAlign: 'left', padding: '8px 10px', marginBottom: 1, border: 0, borderRadius: 'var(--radius-sm)', cursor: 'pointer', background: on ? 'var(--accent-soft)' : 'transparent', boxShadow: on ? 'inset 2px 0 0 var(--accent)' : 'none' }}>
              <span style={{ display: 'inline-flex', marginTop: 2, color: on ? 'var(--accent-ink)' : 'var(--text-subtle)' }}><Icon name={tg?.icon ?? 'hash'} size={15} /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: on ? 600 : 400, color: on ? 'var(--accent-ink)' : 'var(--text-body)' }}>{m.label}</span>
                <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)', marginTop: 2 }}>{m.example}</span>
              </span>
            </button>
          )
        })}
      </div>

      <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: 24 }}>
        <div key={cur.code} style={{ maxWidth: 920, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: 18 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 200px', gap: 14, alignItems: 'flex-start', marginBottom: 16 }}>
              <MaskInput code={cur.code} field="label" label={t(lang, 'Наименование маски', 'Mask name')} value={cur.label} lang={lang} />
              <MaskInput code={cur.code} field="separator" label={t(lang, 'Разделитель', 'Separator')} value={sep} mono hint={frozenHint} lang={lang} />
              <MaskInput code={cur.code} field="example" label={t(lang, 'Пример', 'Example')} value={cur.example ?? ''} mono lang={lang} />
              {/* Расширение — только у маски имени файла. Сегментом оно не
                  выражается: части соединяются разделителем маски, а расширение
                  приписывается точкой. Прежде оно лежало текстом внутри шаблона
                  ([Документ]_[Роль].pdf), и правило «у исходника расширение
                  свободное» на экране не читалось вовсе. */}
              {other && cur.code === 'file' ? (
                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '160px 1fr', gap: 14, alignItems: 'flex-start' }}>
                  <MaskInput code={cur.code} field="extension" label={t(lang, 'Расширение', 'Extension')}
                             value={other.extension ?? ''} mono lang={lang} />
                  <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', paddingTop: 22 }}>
                    {t(lang, 'Без точки. Пусто — расширение не проверяется. У исходника оно свободное всегда: сверяется основа имени.',
                             'No dot. Blank means the extension is not checked. A source file always keeps its own: only the name stem is compared.')}
                  </div>
                </div>
              ) : null}
              {other ? <div style={{ gridColumn: '1 / -1' }}><MaskInput code={cur.code} field="note" label={t(lang, 'Примечание', 'Note')} value={other.note ?? ''} lang={lang} /></div> : null}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '18px', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
              {segments.length ? segments.map((s, i) => (
                <Fragment key={i}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 19, fontWeight: 600, color: `var(${SEG_COLORS[i % SEG_COLORS.length]})`, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '5px 12px' }}>{s.example || s.value || s.srcKind}</span>
                    <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{s.label}</span>
                  </div>
                  {i < segments.length - 1 ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 19, color: 'var(--text-subtle)', alignSelf: 'flex-start', marginTop: 6 }}>{sep}</span> : null}
                </Fragment>
              )) : (
                /* Маска имени файла: номер по ней не выпускается, поэтому и
                   сегментов нет — показывается её шаблон как есть. */
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 19, fontWeight: 600, color: 'var(--text-strong)', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '5px 12px' }}>{other?.mask}</span>
              )}
              <span style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{t(lang, 'Пример', 'Example')}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 600, color: 'var(--text-strong)' }}>{cur.example}</span>
              </span>
            </div>
          </div>

          {editable ? <SegmentEditor mask={editable} lang={lang} target={doc ? 'document' : other?.id} /> : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              <Icon name="info" size={14} />
              {t(lang, 'Это маска имени файла: по ней проверяется имя загружаемого файла, а не выпускается номер.',
                    'This is a file-name mask: it validates an uploaded file name instead of issuing a number.')}
            </div>
          )}

          {doc?.rev ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)' }}>
              <CIcon name="git-commit-vertical" size={15} color="var(--accent-ink)" />
              <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}><b>{doc.rev.label}:</b> {doc.rev.scheme}</span>
            </div>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            <Icon name="info" size={13} />{t(lang, 'Поля маски сохраняются, когда из них уходит фокус; состав номера — кнопкой «Сохранить состав».', 'Mask fields are saved when they lose focus; the composition is saved with its own button.')}
          </div>
        </div>
      </div>
      {adding ? <MaskDialog open onClose={() => setAdding(false)} lang={lang} /> : null}
    </div>
  )
}

// ===== Matrix tab: routing builder =====================================
/* onOpenDoc принимается для совместимости с вызывающим кодом, но внутри не
   используется — открывать документ отсюда пока некому. */
function MatrixTab({ lang }: { lang: Lang; onOpenDoc?: (no: string) => void }) {
  /* Два режима: реестр всех веток (по умолчанию) и редактор одной. Реестр
     отвечает на вопрос «сколько у проекта уникальных маршрутов», который
     прежде выяснялся перещёлкиванием всех комбинаций четырёх селектов. */
  const [branch, setBranch] = useState<MatrixBranchRef | null>(null)
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {branch
        ? <MatrixConfig lang={lang} initial={branch} onBack={() => setBranch(null)} />
        : <MatrixRegistry lang={lang} onOpen={setBranch} />}
    </div>
  )
}

// ===== Documents of MDR registers (standalone screen) ===================
/* Значение отдаётся с задержкой: серверный поиск не должен стрелять запросом
   на каждую нажатую клавишу. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms)
    return () => window.clearTimeout(id)
  }, [value, ms])
  return debounced
}

export function MdrDocs({ lang = 'ru', vendor, onOpenDoc, onOpenViewer }: { lang?: Lang; vendor?: string | null; onOpenDoc?: (no: string) => void; onOpenViewer?: (no: string) => void }) {
  const me = useMe()
  const [page, setPage] = useState(0)
  /* Поиск уходит на сервер параметром q: он ищет по всему реестру, а не по
     загруженной странице — таблица показывает только сотню строк из многих
     тысяч, и локальный фильтр по ней вводил бы в заблуждение. */
  const [q, setQ] = useState('')
  const qd = useDebounced(q, 300)
  /* Отбор в шапке таблицы. Держит экран, применяет сервер — так же, как в
     реестрах MDR и VDR. Галочки отбирали ЗАГРУЖЕННУЮ страницу: отобрав
     «КИПсервис», таблица показывала десять строк из тридцати семи, а остальные
     оставались на следующих страницах того же отбора. */
  const [colFilters, setColFilters] = useState<Record<string, string[]>>({})
  /* Переход с экрана организаций приходит с готовым отбором по подрядчику:
     цифры «Документов» там были некликабельны, и до них добирались руками. */
  useEffect(() => {
    setColFilters(vendor ? { vendor: [vendor] } : {})
    setPage(0)
  }, [vendor])
  const docsQuery = useList<Doc>('docs', {
    page,
    pageSize: DOCS_PAGE_SIZE,
    sort: [{ id: 'no', desc: false }],
    filters: colFilters,
    q: qd || undefined,
  })
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={<span>{me.data?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Реестр MDR', 'MDR register')} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Документы', 'Documents')}</span>}
        title={t(lang, 'Документы реестров MDR', 'MDR documents')}
        sub={t(lang, 'Единая таблица всех документов проекта — группировка и фильтрация по любому атрибуту', 'All project documents in one table — group & filter by any attribute')}
        count={docsQuery.isSuccess ? docsQuery.data.total : undefined}
      />
      <Toolbar>
        {/* Сброс страницы — по нажатию, а не по debounce: с пятой страницы
            прежнего набора легко запросить несуществующую страницу нового. */}
        <div style={{ width: 280 }}><Input size="sm" value={q} onChange={(e) => { setQ(e.target.value); setPage(0) }} leading={<Icon name="search" size={14} />} placeholder={t(lang, 'Поиск по №, наименованию, TRM…', 'Search by №, title, TRM…')} /></div>
        <ToolSpacer />
        {qd ? <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{t(lang, 'Найдено', 'Found')}: {docsQuery.data?.total ?? '…'}</span> : null}
      </Toolbar>
      {docsQuery.isError
        ? <ScreenError lang={lang} />
        : <div style={{ flex: 1, minHeight: 0 }}>
            <MdrDocsTable lang={lang} onOpenDoc={onOpenDoc} onOpenViewer={onOpenViewer} rows={docsQuery.data?.rows ?? []} loading={docsQuery.isPending}
                          colFilters={colFilters}
                          onFilter={(key, values) => {
                            setColFilters((f) => {
                              const n = { ...f }
                              if (values && values.length) n[key] = values
                              else delete n[key]
                              return n
                            })
                            // Смена отбора сбрасывает страницу: на второй странице
                            // нового набора легко оказаться за его пределами.
                            setPage(0)
                          }} />
          </div>}
      <Pager page={docsQuery.data?.page ?? 0} pageCount={docsQuery.data?.pageCount ?? 1}
             total={docsQuery.data?.total ?? 0} lang={lang} onPage={setPage} />
    </div>
  )
}

/* Колонки, по которым отбирают эту таблицу. Счётчики к ним считает сервер
   (GET /api/facets/docs?by=…) — по всему реестру, а не по странице. */
const MDR_DOCS_FACETS = ['titul', 'area', 'contract', 'stage', 'mdr', 'disc', 'vendor', 'purpose', 'code', 'life'] as const

function MdrDocsTable({ lang, onOpenDoc, onOpenViewer, rows, loading, colFilters, onFilter }: { lang: Lang; onOpenDoc?: (no: string) => void; onOpenViewer?: (no: string) => void; rows: Doc[]; loading?: boolean; colFilters?: Record<string, string[]>; onFilter?: (key: string, values: string[] | null) => void }) {
  const titulRows = useDictRows('titul')
  const contracts = useContracts()
  const stageRows = useDictRows('stage')
  const discLabels = useDictLabels('disc')
  const lifeLabels = useDictLabels('life')
  const titLbl = (v: string) => { const r = titulRows.find((x) => x.code === v); return r ? r.code + ' — ' + r.name : v }
  const areaRows = useDictRows('area')
  /* Значения отбора и числа рядом с ними — с сервера. Список, собранный по
     загруженной странице, показывал шесть подрядчиков из тридцати и суммы,
     которые складывались в размер страницы, а не в размер реестра. */
  const facets = useFacets('docs', MDR_DOCS_FACETS)
  const countsOf = (key: string): Record<string, number> | undefined => {
    const buckets = facets.data?.[key]
    return buckets ? Object.fromEntries(buckets.map((b) => [String(b.value), b.count])) : undefined
  }
  const cols: SmartColumn<Doc>[] = [
    { key: 'titul', header: t(lang, 'Титул', 'Title'), width: 70, groupable: true, filterable: true, filterCounts: countsOf('titul'), valueLabel: (v) => titLbl(String(v)), render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--violet-600)', background: 'var(--violet-50)', border: '1px solid var(--violet-100)', borderRadius: 2, padding: '1px 5px' }}>{r.titul}</span> },
    /* Объект строки: с V90 он её свойство, а не свойство реестра — реестр по
       контракту идёт по нескольким объектам, и увидеть объект надо в строке. */
    { key: 'area', header: t(lang, 'Объект', 'Area'), width: 70, groupable: true, filterable: true, filterCounts: countsOf('area'), valueLabel: (v) => { const r = areaRows.find((x) => x.code === v); return r ? r.code + ' — ' + r.name : String(v) }, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-body)' }}>{r.area}</span> },
    { key: 'contract', header: t(lang, 'Контракт', 'Contract'), width: 78, groupable: true, filterable: true, filterCounts: countsOf('contract'), valueLabel: (v) => { const c = contracts.find((x) => x.code === v); return c ? v + ' — ' + c.title : v }, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-body)' }}>{r.contract}</span> },
    { key: 'stage', header: t(lang, 'Стадия', 'Stage'), width: 60, groupable: true, filterable: true, filterCounts: countsOf('stage'), valueLabel: (v) => { const s = stageRows.find((x) => x.code === v); return s ? v + ' — ' + s.name : v }, render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--blue-600)' }}>{r.stage}</span> },
    { key: 'mdr', header: 'MDR', mono: true, nowrap: true, width: 130, groupable: true, filterable: true, filterCounts: countsOf('mdr'), render: (r) => <span style={{ color: 'var(--text-link)' }}>{r.mdr}</span> },
    { key: 'no', header: t(lang, '№ документа', 'Document №'), mono: true, nowrap: true, width: 150, required: true, render: (r) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Crit on={r.crit} /><span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.no}</span></span> },
    { key: 'title', header: t(lang, 'Наименование', 'Title'), sortable: true, render: (r) => <div style={{ minWidth: 0 }}><div style={{ color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}>{r.title}</div><div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{r.type} · {r.vendor}</div></div> },
    { key: 'disc', header: t(lang, 'Дисц.', 'Disc.'), width: 60, groupable: true, filterable: true, filterCounts: countsOf('disc'), valueLabel: (v) => String(v) + ' — ' + (discLabels[String(v)] ?? String(v)), render: (r) => <DiscTag code={r.disc} /> },
    { key: 'vendor', header: t(lang, 'Подрядчик', 'Vendor'), width: 150, groupable: true, filterable: true, filterCounts: countsOf('vendor'), render: (r) => <span style={{ color: 'var(--text-muted)' }}>{r.vendor}</span> },
    { key: 'purpose', header: t(lang, 'Назн.', 'Purpose'), width: 64, filterable: true, filterCounts: countsOf('purpose'), render: (r) => <Purpose value={r.purpose} size="sm" /> },
    { key: 'code', header: t(lang, 'Код', 'Code'), align: 'center', width: 52, filterable: true, filterCounts: countsOf('code'), valueLabel: (v) => v || '—', render: (r) => <CodeChip code={r.code} size="sm" /> },
    { key: 'life', header: t(lang, 'Статус', 'Status'), width: 150, groupable: true, filterable: true, filterCounts: countsOf('life'), valueLabel: (v) => lifeLabels[String(v)] ?? String(v), render: (r) => <LifeBadge life={r.life} /> },
    /* Есть ли основной файл. По значку открывается сам чертёж: иначе до файла
       три нажатия вместо одного. */
    {
      key: 'hasMain', header: t(lang, 'Файл', 'File'), align: 'center', width: 56, render: (r) => (
        <button type="button" className="tool-btn"
              title={r.hasMain
          ? t(lang, 'Основной файл загружен — открыть', 'Main file uploaded — open')
          : t(lang, 'Основного файла нет', 'No main file')}
              disabled={!r.hasMain || !onOpenViewer}
              onClick={(e) => { if (r.hasMain && onOpenViewer) { e.stopPropagation(); onOpenViewer(r.no) } }}
              style={{ display: 'inline-flex', border: 0, padding: 0, background: 'transparent', color: r.hasMain ? 'var(--success-ink)' : 'var(--text-subtle)', cursor: r.hasMain && onOpenViewer ? 'pointer' : 'default' }}>
          <Icon name={r.hasMain ? 'file-text' : 'square-dashed'} size={14} />
        </button>
      ),
    },
    { key: 'plan', header: t(lang, 'План', 'Plan'), mono: true, align: 'right', width: 84, render: (r) => <DateCell value={r.plan} kind="planned" /> },
    { key: 'forecast', header: t(lang, 'Прогноз', 'Forecast'), mono: true, align: 'right', width: 88, render: (r) => <DateCell value={r.forecast} kind="forecast" /> },
    { key: 'fact', header: t(lang, 'Факт', 'Actual'), mono: true, align: 'right', width: 84, render: (r) => <DateCell value={r.fact} kind="actual" strong /> },
  ]
  return (
    /* Высота, а не flex: обёртка на экране — обычный блок, и flex: 1 в ней
       ничего не значил. Таблица росла по содержимому и вытягивала за собой
       страницу — вместе с заголовками столбцов. */
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', padding: '12px 24px 16px' }}>
      <div style={{ flex: 1, minHeight: 0, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--surface-card)' }}>
        {/* Группировки по умолчанию больше нет: SmartTable группирует то, что ему
            передали, а передаётся страница. Группировка «по титулу» по всему
            реестру требует групповых счётчиков с сервера — вместе с витринами
            дашборда (Ф2.7). Показывать группу, посчитанную по сотне строк из
            двухсот тысяч, значит врать. */}
        <SmartTable name="mdr-docs-admin" columns={cols} rows={rows} rowKey={(r) => r.no} lang={lang} onRowClick={(r) => onOpenDoc && onOpenDoc(r.no)} loading={loading}
                    colFilters={colFilters} onFilter={onFilter}
                    emptyText={t(lang, 'Нет документов', 'No documents')} />
      </div>
    </div>
  )
}
